
/**
 * Recursively merge `source` into `target` (both plain objects), returning a new
 * object. Used where a `STRINGS.x = {...}` assignment adds i18n keys to a section
 * that is ALSO defined in the main object — without this, the assignment would
 * replace the original and drop keys the app relies on (e.g.
 * salesAnalytics.filters.periods), causing runtime crashes.
 */
const deepMerge = (target, source) => {
  const out = { ...(target || {}) };
  for (const key of Object.keys(source || {})) {
    const s = source[key];
    const t = out[key];
    if (s && typeof s === "object" && !Array.isArray(s) && t && typeof t === "object" && !Array.isArray(t)) {
      out[key] = deepMerge(t, s);
    } else {
      out[key] = s;
    }
  }
  return out;
};

const STRINGS = {
  common: {
    appTitle: "Order Management System",
    searchPlaceholder: "Search...",
    searchAriaLabel: "Search",
    symbols: {
      emDash: "—",
      required: "*",
      currency: "₹",
    },
    units: {
      kg: "kg",
      meters: "meters",
      pieces: "pieces",
      m: "m",
      mm: "mm",
      pcs: "Pcs",
      kgOrMtr: "Kg/Mtr",
      // pieces: 'pieces',
      // m2: 'm²',
      // g: 'g',
      // l: 'L',
      // units: 'units',
      // box: 'box',
    },
    alerts: {
      exportFailed: "Export failed",
      successTitle: "Success",
      errorTitle: "Error",
      fetchFailed: "Failed to fetch data",
      errorMessage: "Something went wrong. Please try again.",
    },
    status: {
      success: "Success",
      error: "Error",
      failed: "Failed",
      warning: "Warning",
      info: "Info",
    },
    notifications: {
      importCompleted: "Import completed successfully",
      exportCompleted: "Export completed successfully",
    },
    buttons: {
      refresh: "Refresh",
      refreshing: "Refreshing...",
      exportCsv: "Export CSV",
      bulkAdd: "Bulk Add",
      addUser: "Add User",
      addIpAddress: "Add IP Address",
      deleteSelected: "Delete Selected",
      loading: "Marking...",
      cancel: "Cancel",
      import: "Import",
      importing: "Importing...",
      downloadTemplate: "Download template",
      close: "Close",
      ok: "OK",
      save: "Save",
      saving: "Saving...",
      resetting: "Resetting...",
      delete: "Delete",
      deleting: "Deleting...",
      view: "View",
      edit: "Edit",
      done: "Done",
      gotIt: "Got it",
    },
    values: {
      notAvailable: "N/A",
      yesUpper: "YES",
      noUpper: "NO",
    },
    statusValues: {
      active: "Active",
      inactive: "Inactive",
      activeUpper: "ACTIVE",
      inactiveUpper: "INACTIVE",
    },
    importHelper: "Accepted formats: .xls, .xlsx. Maximum size 5 MB.",
    importingText: "Uploading...",
    importReady: "Ready to import",
    dialogs: {
      confirm: {
        title: "Confirm Action",
        confirm: "Confirm",
        cancel: "Cancel",
      },
      delete: {
        title: "Confirm Delete",
        confirm: "Delete",
        cancel: "Cancel",
      },
      import: {
        title: "Import Records",
        subtitle: "Upload File to Import Data",
        description: "Select an Excel file to import records in bulk.",
        uploadLabel: "Upload File",
        helper: "Accepted formats: .xls, .xlsx. Maximum size 5 MB.",
        status: {
          success: "Import successful",
          error: "Import failed",
          uploading: "Uploading...",
          ready: "Ready to import",
        },
        errors: {
          invalidType: "Invalid file type",
          noFile: "Please select a file",
          maxSize: "Maximum file size exceeded (5 MB)",
          parseError: "Failed to parse Excel file",
        },
        messages: {
          columnsMismatch: "Column names don't match the expected template.",
          // Only REQUIRED columns block an import. Optional columns may be deleted and
          // unrecognised ones are ignored, so neither is mentioned here.
          missingRequiredColumns:
            "These required columns are missing from your file: {missing}. Add them (or download the sample again) and re-upload.",
          columnsMismatchDetail:
            "Missing columns: {missing}. Extra columns: {extra}.",
          // A failed import can report one error per row. Only the first
          // {shown} are listed; the rest go to the CSV.
          errorsTruncated: "...and {count} more error(s) not shown.",
          errorSummary: "{count} row(s) could not be imported.",
          downloadErrors: "Download all errors (CSV)",
          errorsFilename: "import_errors",
        },
        buttons: {
          export: {
            filename: "product_categories",
            downloadPrefix: "product_categories",
          },
          cancel: "Cancel",
          import: "Import",
          importing: "Importing...",
          downloadTemplate: "Download template",
        },
        config: {
          allowedExtensions: ["xls", "xlsx"],
          maxFileSizeBytes: 5242880, // 5 MB
          defaultTemplateFilename: "import_template",
          defaultInputId: "import-input",
          acceptAttribute: ".xls,.xlsx",
        },
      },
    },
    table: {
      actions: "Actions",
      status: "Status",
      empty: {
        title: "No records found",
        message: "There is no data to display right now.",
      },
      pagination: {
        showing: "Showing",
        of: "of",
        entries: "entries",
        page: "Page",
        previous: "Previous",
        next: "Next",
      },
    },
    // Shared action vocabulary — reused across every screen's row-action icons and buttons.
    // Introduced for i18n so these labels have a single source of truth.
    actions: {
      view: "View",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      saving: "Saving...",
      saved: "Saved",
      cancel: "Cancel",
      confirm: "Confirm",
      discard: "Discard",
      reset: "Reset",
      submit: "Submit",
      close: "Close",
      ok: "OK",
      keepEditing: "Keep Editing",
      print: "Print",
      loading: "Loading...",
      printChallan: "Print Challan",
      printOrder: "Print Order",
      printPurchaseAdvice: "Print Purchase Advice",
      printDeliveryOrder: "Print Delivery Order",
      generateChallan: "Generate Challan",
      editChallan: "Edit Challan",
      editSupervisor: "Edit Supervisor",
      editUnloading: "Edit Unloading",
      markComplete: "Mark as Complete",
      confirmOrder: "Confirm Order",
      cancelOrder: "Cancel Order",
      goToDeliveryOrders: "Go to Delivery Orders",
      sendEmail: "Send via Email",
      sendChallanEmail: "Send challan via Email",
      sendWhatsApp: "Send via WhatsApp",
      sendChallanWhatsApp: "Send challan via WhatsApp",
      sendPoEmail: "Send PO via Email",
      sendPoWhatsApp: "Send PO via WhatsApp",
      email: "Email",
      whatsapp: "WhatsApp",
      auditLogs: "Audit Logs",
      preview: "Preview",
      download: "Download",
      remove: "Remove",
      addItem: "Add Item",
      exportPrintedPurchaseChallans: "Export Printed Purchase Challans",
      exportPurchaseChallans: "Export Purchase Challans",
      deleteWarehouse: "Delete Warehouse",
      deleteAttachment: "Delete Attachment",
      expandRow: "Expand row",
      collapseRow: "Collapse row",
    },
    // Shared form-field vocabulary — labels, placeholders, and helper text used across
    // multiple forms. Domain-specific labels stay in their domain STRINGS block.
    formFields: {
      labels: {
        vehicleNumber: "Vehicle Number",
        driverContact: "Driver Contact",
        vehicleRemark: "Vehicle Remark",
        internalRemarks: "Internal Remarks",
        orderRemark: "Order Remark",
        deliveryOrderRemark: "Delivery Order Remark",
        remark: "Remark",
        itemRemark: "Item Remark",
        expenseRemark: "Expense Remark",
        discountRemark: "Discount Remark",
        expenses: "Expenses",
        discount: "Discount",
        subtotal: "Subtotal",
        totalWithoutGst: "Total Without GST",
        roundingOff: "Rounding Off",
        grandTotal: "Grand Total",
        loadingCost: "Loading Cost",
        handlingCharges: "Handling Charges",
        freightGst: "Freight GST",
        totalLoaded: "Total Loaded",
        adjustedWeight: "Adjusted Weight",
        adjustedPerPieceWeight: "Adjusted per piece weight",
        weight: "Weight",
        pieces: "Pieces",
        actualPieces: "Actual Pieces",
        actualQty: "Actual Qty",
        perPieceWeight: "Per Piece Weight",
        payment: "Payment Terms",
        paymentTermsRequired: "Payment Terms *",
        supplier: "Supplier",
        supplierInvoiceNo: "Supplier Invoice Number",
        supplierInvoiceDate: "Supplier Invoice Date",
        customer: "Customer",
        challanDate: "Challan Date",
        poNumber: "PO Number",
        destination: "Destination",
        contact: "Contact",
        supervisor: "Supervisor",
        deliveryLocation: "Delivery Location",
        assignedBy: "Assigned by",
        orderCreatedBy: "Order Created By",
        deliveryOrderCreatedBy: "Delivery Order Created By",
        purchaseAdviceCreatedBy: "Purchase Advice Created By",
        purchaseOrderCreatedBy: "Purchase Order Created By",
        challanGeneratedBy: "Challan Generated By",
        generatedBy: "Generated By",
        freightType: "Freight Type",
        freightPricingType: "Freight Pricing Type",
        hsnCodes: "HSN Codes",
        emptyTruckWeight: "Empty Truck Weight",
        finalTruckWeight: "Final Truck Weight",
        loadedTruckWeight: "Loaded Truck Weight",
        totalWeight: "Total Weight",
        totalItems: "Total Items",
        totalQuantity: "Total Quantity",
        receivedQuantity: "Received Quantity",
        status: "Status",
        dueDate: "Due Date",
        expectedDeliveryDate: "Expected Delivery Date",
        referenceParty: "Reference Party Name",
        contactPerson: "Contact Person",
        contactPhone: "Contact Phone",
        warehouseName: "Warehouse Name",
        fullAddress: "Full Address",
        phoneNumber: "Phone Number",
        default: "Default",
        unlistedDealer: "Unlisted Dealer",
        loadByPieces: "Load by Pieces",
        warehouseInformation: "Warehouse Information",
        additionalInformation: "Additional Information",
        summary: "Summary",
        price: "Price",
        priceWithCurrency: "Price ({currency})",
        emptyTruckWeightKg: "Empty Truck Weight (kg)",
        finalTruckWeightKg: "Final Truck Weight (kg)",
        loadedTruckWeightKg: "Loaded Truck Weight (kg)",
        qtyWithUnit: "Qty ({unit})",
        weightKg: "Weight (kg)",
        perPieceWtKg: "Per Piece Wt (kg)",
        expensesPlus: "Expenses (+)",
        discountMinus: "Discount (-)",
        attachments: "Attachments",
        editPurchaseChallanTitle: "Edit Purchase Challan",
        itemsAndQuantities: "Items & Quantities",
        itemsAndActualQuantities: "Items & Actual Quantities",
        editFinancialFields: "Edit Financial Fields",
        saveChanges: "Save Changes",
        actualWeightKg: "Actual Weight (Kg)",
        actualQtyWithUnit: "Actual Qty ({unit})",
        pricePerUnit: "Price / {unit}",
        loadingCostPerKgShort: "Loading Cost / Kg",
        loadingCostPerKg: "Loading Cost Per KG",
        emptyTruckWeightKgCapital: "Empty Truck Weight (Kg)",
        finalTruckWeightKgCapital: "Final Truck Weight (Kg)",
        product: "Product",
        qty: "Qty",
        loadBy: "Load By",
        remarks: "Remarks",
        orderedPieces: "Ordered Pieces",
        calculatedWeight: "Calculated Weight",
        netWeight: "Net Weight",
        vehicleAndDispatchDetails: "Vehicle & Dispatch Details",
        subtotalItems: "Subtotal (Items)",
        gstAtPercent: "GST @ {pct}%",
        totalIncGst: "Total (inc. GST)",
        items: "Items",
        calcWt: "Calc. Wt.",
        netTruckWeight: "Net Truck Weight:",
        selectedLabours: "Selected Labours ({n})",
        itemsAndInitialPieces: "Items & Initial Pieces",
        itemsToUnload: "Items to Unload",
        editUnloadingTitle: "Edit Unloading",
        country: "Country",
        state: "State",
        city: "City",
        address: "Address",
        mapUrl: "Map URL",
      },
      placeholders: {
        vehicleNumber: "Vehicle number",
        driverContact: "Driver contact",
        driverContactPhone: "Phone number",
        vehicleRemark: "Vehicle remark",
        remark: "Remark",
        itemRemark: "Line item note...",
        itemRemarkGeneric: "Item remark",
        expenseRemarkGeneric: "Expense remark",
        discountRemarkGeneric: "Discount remark",
        expensesNote: "Transport, labour etc.",
        discountNote: "Rebate, scheme etc.",
        orderRemark: "Enter order remark",
        challanNotes: "Overall challan notes...",
        purchaseOrderNotes: "Purchase order notes...",
        transportNotes: "Notes about transport / driver...",
        internalRemarksNotVisibleCustomer: "Internal remarks (not visible to customer)",
        internalRemarksNotVisibleSupplier: "Internal remarks (not visible to supplier)",
        referenceParty: "Enter reference party name (Optional)",
        selectPaymentTerms: "Select or type payment terms",
        selectFreightPricingType: "Select Freight Pricing Type",
        selectFreightType: "Select Freight Type",
        loadedWeight: "Enter loaded weight",
        contactPerson: "Enter contact person name",
        contactPhone: "Enter contact phone",
        warehouseName: "Enter warehouse name",
        warehouseAddress: "Enter warehouse address",
        deliveryLocationSearch: "Search delivery location...",
        india: "India",
        selectState: "Select state",
        enterCity: "Enter city",
        mapUrl: "Enter map URL",
        attachmentRemark: "Short remark / description (optional)",
        allSupervisors: "All Supervisors",
        addOrderRemark: "Add order remark...",
        addDeliveryOrderRemark: "Add delivery order remark...",
        internalRemarksOptional: "Internal remarks (optional)",
        vehicleNumberExample: "e.g. MH12AD9988",
        driverContactExample: "e.g. 9876543210",
        vehicleRemarkExample: "e.g. Black Tata 407",
        supplierInvoiceNoExample: "e.g. RV0123000601",
        remarkExample: "e.g., Check tire pressure",
        selectDefault: "Select...",
        selectOption: "Select option",
        noOptionsFound: "No options found",
      },
      options: {
        freightType: {
          exWarehouse: "EX Warehouse",
          freeDelivery: "Free Delivery by Supplier",
          freightChargeable: "Freight Chargeable",
          freeDeliveryFreightToPay: "Free Delivery, Freight to Pay",
          freeDeliveryPaidByCompany: "Free Delivery, Paid by Company",
        },
      },
      empty: {
        noItems: "No items",
        noOrderRemark: "No order remark",
        noDeliveryOrderRemark: "No delivery order remark",
        noWarehousesYet: "No warehouses added yet",
        warehousesOptional: "Warehouses are optional",
        noDataFound: "No data found",
      },
      errors: {
        vehicleNumberRequired: "Vehicle number is required.",
        orderRemarkRequired: "Order remark is required.",
        warehouseNameRequired: "Warehouse name is required",
        stateRequired: "State is required",
        cityRequired: "City is required",
        addressRequired: "Address is required",
        selectLocation: "Please select a location",
      },
    },
    // Common alert/toast/dialog messages used from many screens.
    messages: {
      unableToOpenPrintWindow: "Unable to open print window. Please allow popups for this site.",
      detailsLoadFailed: "Could not load the latest details — close and reopen this form before submitting.",
      unableToPreparePrintContent: "Unable to prepare print content.",
      unableToLoadOrderForPrinting: "Unable to load order details for printing.",
      unableToLoadAdviceForPrinting: "Unable to load advice details for printing.",
      failedToLoadOrderForPrinting: "Failed to load order data for printing.",
      failedToLoadDeliveryOrderForPrinting: "Failed to load delivery order data for printing.",
      failedToSendNotification: "Failed to send notification",
      unexpectedError: "An unexpected error occurred. Please try again.",
      unexpectedErrorDuringExport: "An unexpected error occurred during export",
      exportError: "Export Error",
      validationError: "Validation Error",
      subOrderIdNotFound: "Sub-order ID not found",
      fromDateFuture: "From date cannot be in the future",
      fromDateGreaterThanTo: "From date must be less than or equal to To date",
      unknownWarehouse: "Unknown warehouse",
      unknownError: "Unknown error",
      updateFailed: "Update failed",
      networkError: "Network error",
      error: "Error",
      weightOverage: "Weight Overage",
      weightShortage: "Weight Shortage",
      pressResetHint: "Press Reset to update the values",
      estimatedWeightTooltip: "Estimated weight in kg based on quantity and standard weight/length",
    },
    print: {
      invoice: "Invoice",
      deliveryChallan: "Delivery Challan",
      deliveryOrder: "Delivery Order",
      purchaseChallan: "PURCHASE CHALLAN",
      purchaseAdvice: "Purchase Advice",
      hsnSummary: "HSN Summary",
      hsnSummaryUpper: "HSN SUMMARY",
      columns: {
        size: "Size",
        actualQty: "Actual Qty",
        actQty: "Act Qty",
        actPcs: "Act Pcs",
        igst: "IGST",
        hsn: "HSN",
        hsnCode: "HSN Code",
        pcs: "Pcs",
        pieces: "Pieces",
        receivedQty: "Received Qty",
        gst: "GST",
        amount: "Amount",
        gstAmount: "GST Amount",
        totalIncGst: "Total Inc GST",
        product: "Product",
        category: "Category",
        unit: "Unit",
        description: "Description",
        weight: "Weight",
        rate: "Rate",
        remarks: "Remarks",
        remark: "Remark",
        quantity: "Quantity",
        qty: "Qty",
        poItemQty: "PO Item Qty",
        stdLength: "Std Length",
        stdWeight: "Std Weight",
        itemDescription: "Item Description",
        ordQty: "Ord Qty",
        totalQty: "Total Qty",
        itemTotal: "Item Total",
      },
      headers: {
        name: "Name:",
        customer: "Customer:",
        customerAddress: "Customer Address:",
        supplier: "SUPPLIER:",
        date: "Date:",
        driver: "Driver:",
        created: "Created:",
        printed: "Printed:",
        challanSerial: "Challan Serial:",
        challanNo: "CHALLAN NO:",
        orderDate: "ORDER DATE:",
        paymentTerms: "Payment Terms:",
        dueDate: "Due Date:",
        deliveryAddress: "Delivery Address:",
        timeCreated: "Time Created:",
        timePrint: "Time Print:",
        truckNumber: "Truck Number:",
        driverContact: "Driver Contact:",
        emptyWeight: "Empty Weight:",
        finalWeight: "Final Weight:",
        emptyWt: "Empty Wt:",
        finalWt: "Final Wt:",
        generatedBy: "Generated By:",
        orderCreatedBy: "Order Created By:",
        deliveryOrderCreatedBy: "Delivery Order Created By:",
        supervisors: "Supervisors:",
        orderRemark: "Order Remark:",
        remarks: "Remarks:",
        adviceNumber: "Advice Number:",
        poNumber: "PO Number:",
        adviceDate: "Advice Date:",
        status: "Status:",
        warehouse: "Warehouse:",
      },
      summary: {
        subtotal: "Subtotal",
        loading: "Loading",
        loadingGst: "Loading GST",
        freightGst: "Freight GST",
        handlingCharges: "Handling Charges",
        expenses: "Expenses",
        expense: "Expense",
        discount: "Discount",
        totalWithoutGst: "Total Without GST",
        roundingOff: "Rounding Off",
        grandTotal: "Grand Total",
        itemTotal: "Item Total",
        summary: "Summary",
        additionalInformation: "Additional Information",
        totalItems: "Total Items",
        totalQuantity: "Total Quantity",
        receivedQuantity: "Received Quantity",
        status: "Status",
      },
      empty: {
        noItems: "No items",
      },
      buttons: {
        printChallan: "Print Challan",
        printOrder: "Print Order",
        printDeliveryOrder: "Print Delivery Order",
        printPurchaseAdvice: "Print Purchase Advice",
        printChallanUpper: "PRINT CHALLAN",
      },
    },
    // Widget/section titles used across dashboard and analytics screens.
    analyticsWidgets: {
      topProducts: {
        title: "Top Products",
        subtitle: "By order volume this period",
        items: "items",
      },
      lowStock: {
        title: "Stock Shortage Alert",
        subtitle: "Products with pending orders exceeding available stock",
      },
      challanList: {
        title: "Challan List",
        subtitle: "All delivery challans for the selected period",
      },
      salesLeaderboard: {
        title: "Sales Leaderboard",
        subtitle: "Staff ranked by weight performance",
      },
      supervisorLeaderboard: {
        title: "Supervisor Leaderboard",
      },
      supervisorWorkload: {
        title: "Supervisor Workload",
      },
      pendingBacklog: {
        title: "Pending Backlog",
        subtitle: "Assigned sub-orders awaiting challan — oldest first",
      },
      unprintedChallans: {
        title: "Unprinted Challans",
        subtitle: "Challans generated but not yet printed. Missing from Tally daybook until marked printed.",
        empty: "No unprinted challans",
        tiles: {
          totalUnprinted: "Total Unprinted",
          over1Day: "Over 1 Day",
          over7Days: "Over 7 Days",
          oldestPending: "Oldest Pending",
        },
        searchPlaceholder: "Challan no., order no., customer, vehicle…",
        apply: "Apply",
        loading: "Loading…",
        order: "Order",
        age: "Age",
      },
    },
    // Shared column-header vocabulary for analytics tables (dashboard, challan-analytics,
    // supervisor-analytics, sales-analytics, movement-analytics, assigned-to-challan).
    analyticsColumns: {
      rank: "Rank",
      product: "Product",
      category: "Category",
      orders: "Orders",
      cancelled: "Cancelled",
      delivered: "Delivered",
      quantity: "Quantity",
      weight: "Weight",
      warehouse: "Warehouse",
      inStock: "In Stock",
      pendingOrders: "Pending Orders",
      shortage: "Shortage",
      supervisor: "Supervisor",
      assigned: "Assigned",
      loading: "Loading",
      loaded: "Loaded",
      total: "Total",
      pendingWt: "Pending Wt.",
      loadedWt: "Loaded Wt.",
      salesStaff: "Sales Staff",
      customer: "Customer",
      tallyName: "Tally Name",
      challan: "Challan",
      challanNo: "Challan No.",
      vehicle: "Vehicle",
      status: "Status",
      date: "Date",
      printed: "Printed",
      remark: "Remark",
      adviceNo: "Advice No.",
      poNumber: "PO Number",
      supplier: "Supplier",
      orderNo: "Order No.",
      generatedBy: "Generated By",
      pending: "Pending",
      aging: "Aging",
      action: "Action",
      challanDate: "Challan Date",
      subOrder: "Sub-Order",
      weightT: "Weight (T)",
      weightKg: "Weight (KG)",
      weightTons: "Weight (Tons)",
      search: "Search",
      fromDate: "From Date",
      toDate: "To Date",
      groupBy: "Group By",
      unitType: "Unit Type",
      challanPrinted: "Challan Printed",
      productCatalogue: "Product Catalogue",
      all: "All",
      allSupervisors: "All Supervisors",
      notPrinted: "Not Printed",
      kg: "KG",
      ton: "Ton",
      allStatuses: "All Statuses",
      inTransit: "In Transit",
      productSizeSku: "Product Size (SKU)",
      applyFilters: "Apply Filters",
      searching: "Searching...",
      challans: "Challans",
      productName: "Product Name",
      skuSize: "SKU / Size",
      qtyUnits: "Qty / Units",
      weightT2: "Weight (T)",
      count: "Count",
      average: "Average",
      min: "Min",
      max: "Max",
      completed: "Completed",
      unloading: "Unloading",
      totalWeight: "Total Weight",
      confirmed: "Confirmed",
      invoiced: "Invoiced",
      deliveryAssigned: "Delivery Assigned",
      supervisorAssigned: "Supervisor Assigned",
      unloaded: "Unloaded",
      deliveries: "Deliveries",
      deliveryRate: "Delivery Rate",
      qty: "Qty",
    },
    filters: {
      from: "From",
      to: "To",
      fromDate: "From Date",
      toDate: "To Date",
      status: "Status",
      allStatuses: "All Statuses",
      sortBy: "Sort By",
      apply: "Apply",
      reset: "Reset",
    },
    statuses: {
      pending: "Pending",
      loading: "Loading",
      loaded: "Loaded",
      inTransit: "In Transit",
      delivered: "Delivered",
      unloaded: "Unloaded",
      assigned: "Assigned",
      confirmed: "Confirmed",
      invoiced: "Invoiced",
      cancelled: "Cancelled",
      supervisorAssigned: "Supervisor Assigned",
    },
    sections: {
      overview: "Overview",
      dailyTrend: "Daily Trend",
    },
    emptyStates: {
      noData: "No Data",
      noDataAvailable: "No data available",
      loadingDailyData: "Loading daily data...",
      noDailyData: "No daily data found for the selected filters.",
    },
    labels: {
      unassigned: "Unassigned",
      notAssigned: "Not assigned",
      daysCountSuffix: "days",
      itemsCountSuffix: "items",
      kgSuffix: "kg",
    },
    form: {
      requiredIndicator: "*",
      editHint: {
        keepCurrent: "(leave blank to keep current)",
      },
      assignPersonnel: {
        title: "Assign Delivery Personnel",
        subtitle: "Choose delivery supervisor and delivery boy for this order.",
        labels: {
          supervisor: "Delivery Supervisor",
          deliveryBoy: "Delivery Boy",
          vehicleNumber: "Vehicle Number",
          driverContact: "Driver Contact",
        },
        buttons: {
          cancel: "Cancel",
          assign: "Assign",
          assigning: "Assigning...",
        },
        messages: {
          loadingUsers: "Loading users...",
          selectAtLeastOne: "Select at least one person",
          invalidOrder: "Invalid order",
          vehicleRequired: "Vehicle number is required",
          driverContactRequired: "Driver contact is required",
          assignFailed: "Failed to assign personnel",
        },
        placeholders: {
          vehicleNumber: "e.g., KA-53-1234",
          driverContact: "e.g., 9876543210",
        },
      },
      startTruck: {
        title: "Start Truck Loading",
        subtitle: "Enter truck details and assign labours for loading.",
        labels: {
          vehicleNumber: "Vehicle Number",
          driverContact: "Driver Contact",
          emptyTruckWeight: "Empty Truck Weight (kg)",
          labours: "Labours",
          remark: "Remark",
        },
        placeholders: {
          vehicleNumber: "e.g., KA-53-1234",
          driverContact: "e.g., 9876543210",
          emptyTruckWeight: "e.g., 5000",
          remark: "e.g., Check tire pressure",
        },
        buttons: {
          cancel: "Cancel",
          start: "Start Loading",
          starting: "Starting...",
        },
        messages: {
          loadingUsers: "Loading labours...",
          selectAtLeastOneLabour: "Select at least one labour",
          vehicleRequired: "Vehicle number is required",
          invalidVehicleNumber:
            "Please enter a valid vehicle number (e.g., MH-12-AB-1234)",
          invalidDriverContact: "Please enter a valid driver contact number",
          invalidEmptyTruckWeight: "Please enter a valid empty truck weight",
          formErrors: "Please fix the highlighted fields",
          invalidOrder: "Invalid order",
          startFailed: "Failed to start truck loading",
        },
      },
    },

    /* Backwards-compatible alias: some components use `orders.forms` */
    forms: {
      requiredIndicator: "*",
      editHint: {
        keepCurrent: "(leave blank to keep current)",
      },
      assignPersonnel: {
        title: "Assign Delivery Personnel",
        subtitle: "Choose delivery supervisor and delivery boy for this order.",
        labels: {
          supervisor: "Delivery Supervisor",
          deliveryBoy: "Delivery Boy",
          vehicleNumber: "Vehicle Number",
          driverContact: "Driver Contact",
        },
        buttons: {
          cancel: "Cancel",
          assign: "Assign",
          assigning: "Assigning...",
        },
        messages: {
          loadingUsers: "Loading users...",
          selectAtLeastOne: "Select at least one person",
          invalidOrder: "Invalid order",
          vehicleRequired: "Vehicle number is required",
          driverContactRequired: "Driver contact is required",
          assignFailed: "Failed to assign personnel",
        },
        placeholders: {
          vehicleNumber: "e.g., MH-12-AB-1234",
          driverContact: "e.g., 9876543210",
        },
      },
      startTruck: {
        title: "Start Truck Loading",
        subtitle: "Enter truck details and assign labours for loading.",
        labels: {
          vehicleNumber: "Vehicle Number",
          driverContact: "Driver Contact",
          emptyTruckWeight: "Empty Truck Weight (kg)",
          labours: "Labours",
          remark: "Remark",
        },
        placeholders: {
          vehicleNumber: "e.g., MH-12-AB-1234",
          driverContact: "e.g., 9876543210",
          emptyTruckWeight: "e.g., 5000",
          remark: "e.g., Check tire pressure",
          selectLabour: "Select labour to add...",
        },
        buttons: {
          cancel: "Cancel",
          start: "Start Loading",
          starting: "Starting...",
        },
        messages: {
          loadingUsers: "Loading labours...",
          selectAtLeastOneLabour: "Select at least one labour",
          vehicleRequired: "Vehicle number is required",
          invalidVehicleNumber:
            "Please enter a valid vehicle number (e.g., MH-12-AB-1234)",
          invalidDriverContact: "Please enter a valid driver contact number",
          invalidEmptyTruckWeight: "Please enter a valid empty truck weight",
          formErrors: "Please fix the highlighted fields",
          invalidOrder: "Invalid order",
          startFailed: "Failed to start truck loading",
          selectLabourHelper:
            "Select a labour from the dropdown to add them to the list.",
          noLaboursSelected: "No labours selected",
        },
      },
      completeTruckLoading: {
        title: "Complete Truck Loading",
        subtitle:
          "Review items, enter actual loaded quantities, and finalize truck weight.",
        labels: {
          orderId: "Order ID",
          truckWeight: "Final Truck Weight (kg)",
          items: "Items",
          productName: "Product Name",
          quantity: "Quantity",
          actualQuantity: "Actual Quantity",
          unknownProduct: "Unknown Product",
          approxWeight: "Approx W:",
          loadingCost: "Loading Cost:",
          totalLoadingCost: "Total Loading Cost",
        },
        messages: {
          loadingOrder: "Loading order details...",
          invalidOrder: "Invalid order or order not found",
          noItems: "No items in this order",
          invalidWeight: "Please enter a valid final truck weight",
          invalidQuantity: "Please enter valid numbers for actual quantities",
          completeFailed: "Failed to complete truck loading",
          weightsAdjusted:
            "* Weights adjusted (Factor: {factor}) due to Net Truck Weight mismatch.",
        },
        placeholders: {
          truckWeight: "e.g., 15000",
        },
        buttons: {
          cancel: "Cancel",
          complete: "Complete Loading",
          completing: "Completing...",
        },
      },
      completeUnloadingForm: {
        title: "Complete Unloading",
        subtitle:
          "Review items, enter actual unloaded quantities, and finalize truck weight.",
        labels: {
          orderId: "Order ID",
          truckWeight: "Final Truck Weight (kg)",
          items: "Items",
          productName: "Product Name",
          quantity: "Quantity",
          actualQuantity: "Actual Quantity",
          unknownProduct: "Unknown Product",
          approxWeight: "Approx W:",
          loadingCost: "Unloading Cost:",
          totalLoadingCost: "Total Unloading Cost",
        },
        messages: {
          loadingOrder: "Loading details...",
          invalidOrder: "Invalid order or order not found",
          noItems: "No items in this advice",
          invalidWeight: "Please enter a valid final truck weight",
          invalidQuantity: "Please enter valid numbers for actual quantities",
          completeFailed: "Failed to complete unloading",
          weightsAdjusted:
            "* Weights adjusted (Factor: {factor}) due to Net Truck Weight mismatch.",
        },
        placeholders: {
          truckWeight: "e.g., 5000",
        },
        buttons: {
          cancel: "Cancel",
          complete: "Complete Unloading",
          completing: "Completing...",
        },
      },
    },
    messageAlert: {
      closeAria: "Close message",
      providerError:
        "useMessageAlert must be used within a MessageAlertProvider",
    },
    developerCredit: {
      prefix: "Developed and Managed by",
      linkText: "Flip Retail",
    },
  },
  dashboard: {
    title: "Dashboard",
    subtitle: "Overview of your business performance",
    generatedAt: "Generated at",
    exportExcel: "Export Excel",
    exportPdf: "Export PDF",
    kpis: {
      todayRevenue: "Today Revenue",
      todayOrders: "Today Orders",
      monthRevenue: "Month Revenue",
      monthOrders: "Month Orders",
      revenueGrowth: "Revenue Growth",
      pendingOrders: "Pending Orders",
      inTransitOrders: "In Transit",
      deliveredToday: "Delivered Today",
      lowStockAlerts: "Low Stock Alerts",
      outOfStockItems: "Out of Stock",
      pendingPurchaseOrders: "Pending POs",
      expectedDeliveries: "Expected Deliveries",
    },
    charts: {
      salesTrend: {
        title: "Sales Trend",
        revenue: "Revenue",
        orders: "Orders",
      },
      orderStatus: {
        title: "Order Status Distribution",
        titleShort: "Order Status",
        titleBreakdown: "Order Status Breakdown",
        totalOrders: "Total: {count} orders",
        noData: "No data available",
        tonSuffix: "Ton",
        statuses: {
          pending: "Pending",
          in_transit: "In Transit",
          loading: "Loading",
          loaded: "Loaded",
          invoiced: "Invoiced",
        },
      },
    },
    tables: {
      topProducts: {
        title: "Top Selling Products",
        columns: {
          product: "Product",
          category: "Category",
          quantity: "Total Quantity",
          revenue: "Total Revenue",
          orders: "Order Count",
          avgPrice: "Avg Price",
        },
      },
      lowStock: {
        title: "Low Stock Alerts",
        columns: {
          product: "Product",
          warehouse: "Warehouse",
          currentStock: "Current Stock",
          minStock: "Min Stock",
          shortage: "Shortage",
          pendingOrders: "Pending Orders",
        },
      },
    },
  },
  salesAnalytics: {
    page: {
      title: "Sales Staff",
      subtitle:
        "Analyze your sales performance across products, categories, and warehouses",
    },
    filters: {
      fromDate: "From Date",
      toDate: "To Date",
      submit: "Submit",
      period: "Period",
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        last_90_days: "Last 90 Days",
        this_month: "This Month",
        last_month: "Last Month",
        this_year: "This Year",
        custom: "Custom",
      },
    },
    overview: {
      title: "Sales Overview",
      sectionTitle: "Overview",
      totalOrders: "Total Orders",
      totalRevenue: "Total Revenue",
      totalQuantity: "Total Quantity",
      totalWeight: "Total Weight",
      avgOrderValue: "Avg. Order Value",
      totalDiscount: "Total Discount",
      totalFreight: "Total Freight",
      totalGst: "Total GST",
      subs: {
        inSelectedPeriod: "In selected period",
        dispatchedWeight: "Dispatched weight",
        unitsOrdered: "Units ordered",
      },
    },
    summary: {
      sections: {
        overview: "Overview",
        byStatus: "By Status",
        fulfillment: "Fulfillment",
      },
      cards: {
        totalOrders: "Total Orders",
        totalWeight: "Total Weight",
        confirmed: "Confirmed",
        pending: "Pending",
        cancelled: "Cancelled",
        delivered: "Delivered",
        invoiced: "Invoiced",
        challanGenerated: "Challan Generated",
      },
      subs: {
        allOrdersInPeriod: "All orders in period",
        tonsKgDispatched: "Tons / KG dispatched",
        confirmedOrders: "Confirmed orders",
        awaitingConfirmation: "Awaiting confirmation",
        cancelledOrders: "Cancelled orders",
        ordersDelivered: "Orders delivered",
        ordersInvoiced: "Orders invoiced",
        challansCreated: "Challans created",
      },
    },
    charts: {
      trend: {
        title: "Sales Trend",
        subtitle: "Daily weight & order activity",
        revenue: "Revenue",
        orders: "Orders",
        quantity: "Quantity",
        weightTon: "Weight (Ton)",
      },
      category: {
        title: "Sales by Category",
        heading: "By Category",
        subtitle: "Quantity distribution",
        orderCountSuffix: "ord",
      },
      warehouse: {
        title: "Sales by Warehouse",
        heading: "By Warehouse",
        subtitle: "Orders & weight by location",
        warehousesCount: "{count} warehouses",
        warehousePrefix: "Warehouse #{id}",
        orders: "Orders",
        weight: "Weight",
        qtyLabel: "Qty:",
        unitsSuffix: "units",
        tonSuffix: "Ton",
      },
      orderStatus: {
        title: "Order Status Distribution",
        subtitle: "Total: {count} orders across all statuses",
        tonSuffix: "Ton",
        statuses: {
          pending: "Pending",
          delivery_assigned: "Delivery Assigned",
          loading: "Loading",
          loaded: "Loaded",
          in_transit: "In Transit",
          delivered: "Delivered",
          invoiced: "Invoiced",
          confirmed: "Confirmed",
          cancelled: "Cancelled",
        },
      },
    },
    topProductsWidget: {
      title: "Top Products",
      subtitle: "By quantity ordered this period",
      productsCount: "{count} products",
      headers: {
        product: "Product",
        category: "Category",
        orders: "Orders",
        quantity: "Quantity",
      },
    },
    dailyTable: {
      title: "Daily Trend",
      subtitle: "Sales activity day by day",
      daysCount: "{count} days",
      loading: "Loading daily data...",
      noData: "No daily data found for the selected filters.",
      headers: {
        index: "#",
        date: "Date",
        orders: "Orders",
      },
    },
    ordersTable: {
      title: "Orders List",
      subtitle: "All sales orders for the selected period",
      ordersCount: "{count} orders",
      loading: "Loading orders...",
      noData: "No orders found for the selected filters.",
      headers: {
        index: "#",
        orderNo: "Order No.",
        customer: "Customer",
        salesStaff: "Sales Staff",
        status: "Status",
        date: "Date",
      },
    },
    filtersExtra: {
      allSalesStaff: "All Sales Staff",
      allStatuses: "All Statuses",
      apply: "Apply",
      loading: "Loading...",
      status: "Status",
      fromDate: "From Date",
      toDate: "To Date",
      statusOptions: {
        pending: "Pending",
        confirmed: "Confirmed",
        cancelled: "Cancelled",
        assigned: "Assigned",
        loading: "Loading",
        loaded: "Loaded",
        inTransit: "In Transit",
      },
    },
    mySubOrders: {
      loading: "Loading deliveries…",
      empty: "No active delivery sub-orders",
      unassigned: "Unassigned",
      kg: "kg",
      statuses: {
        pending: "Pending",
        assigned: "Assigned",
        loading: "Loading",
        loaded: "Loaded",
        inTransit: "In Transit",
        delivered: "Delivered",
        cancelled: "Cancelled",
      },
      detail: {
        orderDetail: "Order Detail",
        supervisor: "Supervisor",
        mobile: "Mobile",
        vehicle: "Vehicle",
        totalWeight: "Total Weight",
        challan: "Challan",
        items: "Items",
        loadingItems: "Loading items…",
        noItems: "No item details available",
        headers: {
          product: "Product",
          ordered: "Ordered",
          actual: "Actual",
          pieces: "Pieces",
        },
        loadByPieces: "Load by Pieces",
      },
    },
    tables: {
      topProducts: {
        title: "Top Selling Products",
        headers: {
          product: "Product Name",
          category: "Category",
          quantity: "Total Quantity",
          revenue: "Total Revenue",
          orders: "Order Count",
          avgPrice: "Avg Price",
        },
      },
      warehouse: {
        title: "Sales by Warehouse",
        headers: {
          warehouse: "Warehouse Name",
          orders: "Orders",
          revenue: "Total Revenue",
          quantity: "Total Quantity",
        },
      },
      orderStatus: {
        title: "Order Status Distribution",
        headers: {
          status: "Status",
          count: "Count",
          percentage: "Percentage",
          amount: "Total Amount",
        },
        statuses: {
          pending: "Pending",
          delivery_assigned: "Delivery Assigned",
          loading: "Loading",
          loaded: "Loaded",
          in_transit: "In Transit",
          delivered: "Delivered",
          invoiced: "Invoiced",
        },
      },
    },
  },
  deliveryOrders: {
    page: {
      header: {
        title: "Delivery Orders",
        subtitle: "Manage delivery sub-orders and track status",
      },
    },
    table: {
      columns: {
        subOrderNumber: "Sub Order Number",
        orderNumber: "Order Number",
      },
    },
  },
  deliveryAnalytics: {
    page: {
      title: "Delivery Analytics",
      subtitle:
        "Analyze your delivery performance, challans, and personnel efficiency",
    },
    filters: {
      fromDate: "From Date",
      toDate: "To Date",
      submit: "Submit",
      period: "Period",
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        last_90_days: "Last 90 Days",
        this_month: "This Month",
        last_month: "Last Month",
        this_year: "This Year",
        custom: "Custom",
      },
    },
    overview: {
      title: "Delivery Overview",
      challanOverview: "Challan Overview",
      totalChallans: "Total Challans",
      totalWeight: "Total Weight",
      totalAmount: "Total Amount",
      totalQuantity: "Total Quantity",
      challanCount: "Challan Count",
      tonSuffix: "Ton",
      subs: {
        inSelectedPeriod: "In selected period",
        dispatchedWeight: "Dispatched weight",
      },
    },
    charts: {
      trend: {
        title: "Delivery Trend",
        challanTitle: "Challan Trend",
        subtitle: "Daily challan count & weight dispatched",
        amount: "Amount",
        challans: "Challans",
        quantity: "Quantity",
        weightTon: "Weight (Ton)",
        empty: "No challan data for this period",
      },
      performance: {
        title: "Delivery Performance",
        subtitle: "Order fulfilment breakdown",
        totalOrders: "Total Orders",
        totalOrdersSuffix: "total orders",
        delivered: "Delivered",
        pending: "Pending",
        inTransit: "In Transit",
        avgDeliveryDays: "Avg Delivery Days",
        avgDeliveryTime: "Avg. Delivery Time",
        daysSuffix: "days",
        deliveryRate: "Delivery Rate",
      },
      personnel: {
        title: "Personnel Performance",
        subtitle: "Delivery supervisors activity this period",
        supervisorsCount: "{count} supervisors",
        avgPerDelivery: "Avg/delivery",
        deliveries: "Deliveries",
        weight: "Weight",
        tonSuffix: "Ton",
        headers: {
          name: "Name",
          role: "Role",
          deliveries: "Total Deliveries",
          amount: "Total Amount",
          avgPerDelivery: "Avg Per Delivery",
        },
      },
    },
  },
  purchaseAnalytics: {
    page: {
      title: "Purchase Analytics",
      subtitle: "Analyze your purchase orders, suppliers, and trends",
    },
    filters: {
      fromDate: "From Date",
      toDate: "To Date",
      submit: "Submit",
      period: "Period",
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        last_90_days: "Last 90 Days",
        this_month: "This Month",
        last_month: "Last Month",
        this_year: "This Year",
        custom: "Custom",
      },
    },
    overview: {
      title: "Purchase Overview",
      totalOrders: "Total Orders",
      totalWeightTons: "Total Weight (T)",
      totalQuantity: "Total Quantity",
      pendingOrders: "Pending Orders",
      receivedOrders: "Received Orders",
    },
    charts: {
      trend: {
        title: "Purchase Trend",
        weightTons: "Weight (Tons)",
        orders: "Orders",
        quantity: "Quantity",
      },
    },
    tables: {
      topSuppliers: {
        title: "Top Suppliers",
        headers: {
          supplier: "Supplier",
          orders: "Orders",
          weightTons: "Weight (T)",
          quantity: "Quantity",
        },
      },
    },
  },
  inventoryAnalytics: {
    page: {
      title: "Inventory Analytics",
      subtitle: "Monitor warehouse stocks and low inventory alerts",
    },
    charts: {
      warehouseStock: {
        title: "Warehouse Stock Distribution",
        subtitle: "Stock quantity vs pending orders per warehouse",
        summary: "Warehouse Summary",
        detailsTitle: "Warehouse Details",
        detailsSubtitle: "Stock, pending orders, and alerts by warehouse",
        warehousesCount: "{count} warehouses",
        stock: "Stock",
        pending: "Pending",
        available: "Available",
        lowStockLabel: "Low Stock",
        outOfStockLabel: "Out of Stock",
        headers: {
          warehouse: "Warehouse",
          products: "Products",
          stock: "Total Stock",
          pending: "Pending Orders",
          available: "Available",
          lowStock: "Low Stock",
          outOfStock: "Out of Stock",
        },
      },
      lowStock: {
        title: "Low Stock Alerts",
        subtitle: "Products below minimum stock level",
        itemsCount: "{count} items",
        headers: {
          product: "Product",
          category: "Category",
          warehouse: "Warehouse",
          stock: "Current Stock",
          minimum: "Min Stock",
          shortage: "Shortage",
          status: "Status",
        },
      },
    },
  },
  customerAnalytics: {
    page: {
      title: "Customer Analytics",
      subtitle: "Track customer growth and top performers",
    },
    filters: {
      period: "Period",
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        last_90_days: "Last 90 Days",
        this_month: "This Month",
        last_month: "Last Month",
        this_year: "This Year",
        custom: "Custom Range",
      },
      apply: "Apply Filter",
      fromDate: "From Date",
      toDate: "To Date",
    },
    overview: {
      newCustomers: "New Customers",
      returningCustomers: "Returning Customers",
      activeCustomers: "Total Active",
      newWeightTons: "New Customer Weight (T)",
      returningWeightTons: "Returning Weight (T)",
    },
    tables: {
      topCustomers: {
        title: "Top Customers",
        headers: {
          customer: "Customer",
          orders: "Orders",
          weightTons: "Weight (T)",
          lastOrder: "Last Order",
        },
      },
    },
  },
  inquiryAnalytics: {
    page: {
      title: "Inquiry Analytics",
      subtitle: "Track inquiries and conversions",
    },
    filters: {
      period: "Period",
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        last_90_days: "Last 90 Days",
        this_month: "This Month",
        last_month: "Last Month",
        this_year: "This Year",
        custom: "Custom Range",
      },
      apply: "Apply Filter",
      fromDate: "From Date",
      toDate: "To Date",
    },
    overview: {
      totalInquiries: "Total Inquiries",
      pendingInquiries: "Pending",
      convertedToOrders: "Converted to Orders",
      totalQuotedQuantity: "Total Quoted Qty",
      conversionRate: "Conversion Rate",
    },
    charts: {
      trend: {
        title: "Inquiry Trend",
        seriesInquiries: "Inquiries",
        seriesQuantity: "Quoted Qty",
      },
    },
  },
  financialAnalytics: {
    page: {
      title: "Financial Analytics",
      subtitle: "Tax and revenue overview",
    },
    filters: {
      period: "Period",
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        last_90_days: "Last 90 Days",
        this_month: "This Month",
        last_month: "Last Month",
        this_year: "This Year",
        custom: "Custom Range",
      },
      apply: "Apply Filter",
      fromDate: "From Date",
      toDate: "To Date",
    },
    gst: {
      title: "GST Summary",
      totalTaxable: "Taxable Value",
      totalGst: "Total GST",
      cgst: "CGST",
      sgst: "SGST",
      igst: "IGST",
    },
    tcs: {
      title: "TCS Summary",
      totalOrdersWithTcs: "Orders with TCS",
      totalTcsCollected: "TCS Collected",
    },
    revenue: {
      title: "Revenue Comparison",
      weightChange: "Weight Change",
      ordersChange: "Orders Change",
      currentPeriod: "Current Period",
      previousPeriod: "Previous Period",
    },
  },
  access: {
    authenticationRequired: {
      title: "Authentication Required",
      message: "Please log in to access this page.",
    },
    accessDenied: {
      title: "Access Denied",
      message: "You don't have permission to access this page.",
    },
  },
  taxes: {
    actions: {
      searchPlaceholder: "Search taxes...",
      alerts: {
        download: "Download",
        noDataToExport: "No taxes available to export",
      },
      downloadPrefix: "taxes_export_",
      buttons: {
        add: "Add Tax",
      },
    },
    page: {
      header: {
        title: "Tax Slab",
        subtitle: "Manage GST, CGST, SGST, and IGST rates",
      },
      stats: {
        total: "Total Tax Rates",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Error",
      },
      accessDenied: {
        title: "Access Denied",
        message: "You must be a Super Admin to view this page.",
      },
    },
    table: {
      columns: {
        select: "Select",
        gst: "GST (%)",
        cgst: "CGST (%)",
        sgst: "SGST (%)",
        igst: "IGST (%)",
        created: "Created",
        actions: "Actions",
      },
      emptyState: {
        title: "No Tax Rates Found",
        message: "Start by adding your first tax rate configuration.",
      },
      aria: {
        selectAll: "Select all tax rates",
        selectTax: "Select tax rate",
        viewTax: "View Tax Rate",
        editTax: "Edit Tax Rate",
        deleteTax: "Delete Tax Rate",
      },
    },
    forms: {
      title: {
        add: "Add New Tax Rate",
        edit: "Edit Tax Rate",
      },
      fields: {
        gst: {
          label: "GST Rate (%)",
          placeholder: "Enter GST rate",
          errors: {
            required: "GST rate is required",
            invalid: "Please enter a valid GST rate (0-100)",
          },
        },
        cgst: {
          label: "CGST Rate (%)",
          placeholder: "Enter CGST rate",
          errors: {
            required: "CGST rate is required",
            invalid: "Please enter a valid CGST rate (0-100)",
          },
        },
        sgst: {
          label: "SGST Rate (%)",
          placeholder: "Enter SGST rate",
          errors: {
            required: "SGST rate is required",
            invalid: "Please enter a valid SGST rate (0-100)",
          },
        },
        igst: {
          label: "IGST Rate (%)",
          placeholder: "Enter IGST rate",
          errors: {
            required: "IGST rate is required",
            invalid: "Please enter a valid IGST rate (0-100)",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Add Tax Rate",
          edit: "Update Tax Rate",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete Tax Rate",
        message:
          "Are you sure you want to delete this tax rate? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete Selected Tax Rates",
        messageSingle:
          "Are you sure you want to delete the selected tax rate? This action cannot be undone.",
        messageMultiple:
          "Are you sure you want to delete {count} selected tax rates? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      buttons: {
        cancel: "Cancel",
      },
    },
  },

  productSizes: {
    notifications: {
      created: 'Product "{name}" has been created successfully',
      updated: 'Product "{name}" has been updated successfully',
      deleted: 'Product "{name}" has been deleted successfully',
      stockUpdated: 'Stock updated for "{name}"',
      imported: "{count} Product imported successfully",
      bulkDeletedSingle: "1 Product has been deleted successfully",
      bulkDeletedMultiple: "{count} Product have been deleted successfully",
      bulkDeletedList: "{names} have been deleted successfully",
      bulkDeletedListSingle: "{name} has been deleted successfully",
      bulkDeletedListMultiple: "{names} have been deleted successfully",
    },
    page: {
      header: {
        title: "Product",
        subtitle: "Manage product, inventory, and specifications",
      },
      stats: {
        total: "Total Product",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Product Error",
      },
    },
    actions: {
      searchPlaceholder: "Search Product...",
      alerts: {
        noDataToExport: "No Product available to export",
      },
      downloadPrefix: "product_sizes_export_",
      buttons: {
        add: "Add Product",
        bulkDelete: "Delete Selected",
        refresh: "Refresh",
        import: "Import",
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete Product",
        message:
          "Are you sure you want to delete {name}? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete Product",
        messageSingle:
          "Are you sure you want to delete the selected Product? This action cannot be undone.",
        messageMultiple:
          "Are you sure you want to delete {count} selected Product? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      buttons: {
        cancel: "Cancel",
      },
      errorTitle: "Error",
    },
    errors: {
      noSelection: "No Product selected for deletion",
    },
    table: {
      columns: {
        select: "Select",
        aliasName: "Alias Name",
        productSize: "Product",
        weight: "Weight",
        standardLength: "Standard Length",
        thickness: "Thickness",
        category: "Category",
        productName: "Product Name",
        hsnCode: "HSN Code",
        basePrice: "Base Price",
        currentStock: "Current Stock",
        minimumStock: "Optional re-order level in whole kg, never negative. A row with only this filled in changes nothing else — existing stock and coils are kept.",
        stockStatus: "Stock Status",
        actions: "Actions",
      },
      copy: {
        unnamedProductSize: "Unnamed Product",
        noProductSize: "N/A",
        noThickness: "N/A",
        noWeight: "N/A",
        noCategory: "N/A",
        noHsnCode: "N/A",
        noPrice: "₹0.00",
        noProductName: "N/A",
        noStock: "0",
        noMinStock: "0",
        // Stock status labels
        statuses: {
          in_stock: "In Stock",
          low_stock: "Low Stock",
          out_of_stock: "Out of Stock",
        },
      },
      emptyState: {
        title: "No Product Found",
        message: "There are no Product to display.",
      },
      aria: {
        selectAll: "Select all Product",
        selectProductSize: "Select Product",
        viewProductSize: "View Product",
        editProductSize: "Edit Product",
        deleteProductSize: "Delete Product",
      },
    },
    forms: {
      title: {
        add: "Add Product",
        edit: "Edit Product",
      },
      actions: {
        searchPlaceholder: "Search Product...",
        buttons: {
          add: "Add Product",
          bulkDelete: "Delete Selected",
          refresh: "Refresh",
        },
      },
      fields: {
        alias_name: {
          label: "Alias Name",
          placeholder: "Enter product alias name",
          errors: {
            required: "Alias name is required",
            minLength: "Alias name must be at least 2 characters",
          },
        },
        product_size: {
          label: "Product",
          placeholder: "Enter Product (e.g., 4x8 feet)",
          errors: {
            required: "Product is required",
          },
        },
        product_thickness: {
          label: "Product Thickness",
          placeholder: "Enter thickness (e.g., 2)",
          errors: {
            maxLength: "Thickness cannot exceed 50 characters",
          },
        },
        thickness_unit: {
          label: "Thickness Unit",
          placeholder: "e.g., mm, cm, inch",
        },
        price: {
          label: "Price (₹)",
          placeholder: "Optional — leave blank if unpriced",
        },
        product_weight: {
          label: "Weight (kg)",
          placeholder: "Enter weight in kg",
          errors: {
            invalid: "Please enter a valid weight",
          },
        },
        standard_length: {
          label: "Standard Length (m)",
          placeholder: "Enter length in meters",
          errors: {
            invalid: "Please enter a valid length",
          },
        },
        category_id: {
          label: "Category",
          placeholder: "Select a category",
          errors: {
            required: "Category is required",
          },
        },
        product_catalogue_id: {
          label: "Sub Product",
          placeholder: "Select a Sub Product",
          errors: {
            required: "Sub Product is required",
          },
        },
        hsn_code_id: {
          label: "HSN Code",
          placeholder: "Select an HSN code",
          errors: {
            required: "HSN code is required",
          },
        },
        base_price: {
          label: "Base Price",
          placeholder: "e.g., 1500.00",
          errors: {
            required: "Base price is required",
            invalid: "Please enter a valid price",
          },
        },

        current_stock: {
          label: "Current Stock",
          placeholder: "0",
          errors: {
            invalid: "Please enter a valid stock quantity",
          },
        },
        minimum_stock: {
          label: "Minimum Stock",
          placeholder: "0",
          errors: {
            invalid: "Please enter a valid minimum stock",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Create Product",
          edit: "Update Product",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
    },
    view: {
      header: {
        sectionTitle: "Product Details",
        fallbackTitle: "Product",
      },
      loading: "Loading Product details...",
      empty: {
        title: "Product data unavailable",
        message: "We couldn't load the selected Product. Please try again.",
      },
      fields: {
        aliasName: "Alias Name",
        productSize: "Product",
        productName: "Product Name",
        thickness: "Thickness",
        productWeight: "Weight",
        standardLength: "Standard Length",
        basePrice: "Base Price",
        category: "Category",
        hsnCode: "HSN Code",
        currentStock: "Current Stock",
        minimumStock: "Minimum Stock",
        stockStatus: "Stock Status",
      },
      buttons: {
        close: "Close",
      },
    },
    import: {
      config: {
        templateHeaders: [
          "alias_name",
          "product_size",
          "product_weight",
          "standard_length",
          "product_thickness",
          "current_stock",
          "minimum_stock",
          "product_category_name",
          "product_catalogue_name",
        ],
        templateFilename: "product_sizes_import_template",
        inputId: "product-sizes-import-input",
      },
    },
    distribution: {
      header: {
        sectionTitle: "Product Distribution",
      },
      loading: "Loading distribution data...",
      error: {
        title: "Failed to load distribution data",
      },
      empty: {
        title: "No distribution data available",
        message: "This product is not currently stocked in any warehouse.",
      },
      fields: {
        productName: "Product Name",
        productSize: "Product",
        warehouseDistribution: "Warehouse Distribution",
        warehouseId: "Warehouse ID",
        stockQuantity: "Stock Quantity",
        totalStock: "Total Stock",
        acrossWarehouses: "Across {count} warehouses",
        totalUnits: "Total Units",
      },
      buttons: {
        close: "Close",
      },
      actions: {
        view: "View Distribution",
        tooltip: "View warehouse distribution for this product",
      },
    },
  },
  ipAddresses: {
    actions: {
      searchPlaceholder: "Search IP addresses...",
      statusOptions: {
        all: "All Status",
        active: "Active Only",
        inactive: "Inactive Only",
      },
      alerts: {
        noDataToExport: "No IP addresses available to export",
      },
      downloadPrefix: "ip_addresses_export_",
      buttons: {
        bulkAdd: "Bulk Add",
        add: "Add IP Address",
      },
    },
    page: {
      header: {
        title: "IP Address Management",
        subtitle: "Manage and monitor IP addresses in your network",
      },
      stats: {
        total: "Total IPs",
        active: "Active",
        inactive: "Inactive",
      },
      alerts: {
        errorTitle: "Error",
        dismissAria: "Close error",
      },
    },
    confirm: {
      delete: {
        title: "Delete IP Address",
        message:
          "Are you sure you want to permanently delete {ip}? This action cannot be undone.",
        confirmLabel: "Delete",
      },
      deactivate: {
        title: "Deactivate IP Address",
        message:
          "Are you sure you want to deactivate {ip}? Users will no longer be able to use this IP until it is reactivated.",
        confirmLabel: "Deactivate",
      },
      buttons: {
        cancel: "Cancel",
      },
    },
    export: {
      headers: {
        ipAddress: "ip_address",
        description: "description",
        isActive: "is_active",
        createdAt: "created_at",
      },
      values: {
        active: "Active",
        inactive: "Inactive",
        notAvailable: "N/A",
      },
    },
    table: {
      columns: {
        ipAddress: "IP Address",
        description: "Description",
        status: "Status",
        actions: "Actions",
      },
      copy: {
        noDescription: "No description",
      },
      statuses: {
        active: "Active",
        inactive: "Inactive",
      },
      emptyState: {
        defaultTitle: "No IP addresses found",
        defaultMessage: "Start by adding your first IP address to the system.",
        filteredTitle: "No IP addresses match your filters",
        filteredMessage: "Try adjusting your search criteria or filters.",
        clearFilters: "Clear filters",
      },
      tooltips: {
        view: "View IP Details",
        edit: "Edit IP Address",
        activate: "Activate IP",
        deactivate: "Deactivate IP",
        delete: "Delete IP Address",
      },
    },
    forms: {
      single: {
        title: {
          add: "Add New IP Address",
          edit: "Edit IP Address",
        },
        subtitle: {
          add: "Enter the details for the new IP address.",
          edit: "Update the IP address information below.",
        },
        fields: {
          ipAddress: {
            label: "IP Address",
            placeholder: "e.g., 192.168.1.105",
            errors: {
              required: "IP address is required",
              invalid: "Please enter a valid IP address (e.g., 192.168.1.1)",
            },
          },
          description: {
            label: "Description",
            placeholder: "e.g., Office Computer 3",
            errors: {
              required: "Description is required",
              minLength: "Description must be at least 5 characters long",
            },
          },
          isActive: {
            label: "Active",
          },
        },
        buttons: {
          cancel: "Cancel",
          submit: {
            add: "Add IP Address",
            edit: "Update IP Address",
            loading: {
              add: "Creating...",
              edit: "Updating...",
            },
          },
        },
      },
      bulk: {
        title: "Add Multiple IP Addresses",
        subtitle: "Enter one IP per line, or separate with commas.",
        fields: {
          ipAddresses: {
            label: "IP Addresses",
            placeholder:
              "192.168.1.106\n192.168.1.107\n10.0.0.55\n\nOr separate with commas: 192.168.1.1, 192.168.1.2",
            actions: {
              pasteExample: "Paste Example",
            },
            errors: {
              required: "At least one IP address is required",
              noneValid: "Please enter valid IP addresses",
              invalidList: "Invalid IP addresses: {ips}",
              limitExceeded: "Maximum {max} IP addresses allowed at once",
            },
          },
          description: {
            label: "Description",
            placeholder: "e.g., Bulk added office computers",
            helper:
              "This description will be applied to all IP addresses in this batch.",
            errors: {
              required: "Description is required for bulk import",
              minLength: "Description must be at least 5 characters long",
            },
          },
        },
        preview: {
          title: "Preview ({count} IP addresses)",
          truncated: "Showing first {count}",
          more: "+{count} more",
        },
        buttons: {
          cancel: "Cancel",
          submit: "Add {count} IP Addresses",
          submitFallback: "Add IP Addresses",
          loading: "Adding...",
        },
        examples: {
          ipList: "192.168.1.106\n192.168.1.107\n10.0.0.55",
        },
      },
    },
    view: {
      header: {
        sectionTitle: "IP Address Details",
        fallbackTitle: "IP Record",
      },
      loading: "Loading IP details...",
      empty: {
        title: "IP data unavailable",
        message: "We couldn't load the selected IP address. Please try again.",
      },
      fields: {
        ipAddress: "IP Address",
        description: "Description",
        status: "Status",
        recordId: "Record ID",
        createdAt: "Created At",
        updatedAt: "Last Updated",
      },
      buttons: {
        close: "Close",
      },
    },
  },
  users: {
    actions: {
      searchPlaceholder: "Search users...",
      alerts: {
        noDataToExport: "No users available to export",
      },
      downloadPrefix: "users_export_",
      buttons: {
        add: "Add User",
      },
    },
    page: {
      header: {
        title: "Users",
        subtitle: "Manage system users and their permissions",
      },
      stats: {
        total: "Total Users",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Error",
      },
      accessDenied: {
        title: "Access Denied",
        message: "You must be a Super Admin to view this page.",
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete User",
        message:
          "Are you sure you want to delete {name}? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete Selected Users",
        messageSingle:
          "Are you sure you want to delete the selected user? This action cannot be undone.",
        messageMultiple:
          "Are you sure you want to delete {count} selected users? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      buttons: {
        cancel: "Cancel",
      },
    },
    table: {
      columns: {
        select: "Select",
        name: "Name",
        email: "Email",
        phone: "Phone",
        mobile: "Mobile Number",
        role: "Role",
        status: "Status",
        created: "Created",
        actions: "Actions",
        warehouses: "Warehouses",
      },
      copy: {
        unnamedUser: "Unnamed user",
        noEmail: "No email",
        noPhone: "N/A",
        noMobile: "N/A",
        initialFallback: "U",
      },
      statuses: {
        active: "Active",
        inactive: "Inactive",
      },
      emptyState: {
        title: "No Users Found",
        message: "There are no users to display.",
      },
      aria: {
        selectAll: "Select all users",
        selectUser: "Select user",
        viewUser: "View User",
        editUser: "Edit User",
        deleteUser: "Delete User",
        activateUser: "Activate User",
      },
    },
    forms: {
      title: {
        add: "Add New User",
        edit: "Edit User",
      },
      userTypes: [
        { value: "SUPER_ADMIN", label: "Super Admin" },
        { value: "ADMIN", label: "Admin" },
        { value: "OPERATIONS_MANAGER", label: "Operations Manager" },
        { value: "SALES_STAFF", label: "Sales Executive" },
        { value: "DELIVERY_SUPERVISOR", label: "Delivery Supervisor" },
        { value: "ACCOUNT_USER", label: "Account Executive" },
        { value: "LABOUR", label: "Labour" },
      ],
      fields: {
        firstName: {
          label: "First Name",
          placeholder: "Enter first name",
          errors: {
            required: "First name is required",
          },
        },
        lastName: {
          label: "Last Name",
          placeholder: "Enter last name",
          errors: {
            required: "Last name is required",
          },
        },
        email: {
          label: "Email Address",
          placeholder: "Enter email address",
          errors: {
            required: "Email is required",
            invalid: "Please enter a valid email address",
          },
        },
        phoneNumber: {
          label: "Phone Number",
          placeholder: "Enter phone number",
          errors: {
            invalid: "Please enter a valid phone number (10 digits)",
          },
        },
        mobileNumber: {
          label: "Mobile Number",
          placeholder: "Enter mobile number",
          errors: {
            invalid: "Please enter a valid mobile number",
          },
        },
        warehouses: {
          label: "Warehouses",
          placeholder: "Select warehouse to add...",
          empty: "No warehouses assigned",
          helper: "Select a warehouse from the dropdown to add it to the list.",
        },
        defaultWarehouse: {
          label: "Default Warehouse",
          placeholder: "Select default warehouse...",
          helper: "The warehouse assigned to orders by default.",
          errors: {
            required: "Default warehouse is required",
          },
        },
        userType: {
          label: "User Type",
          errors: {
            required: "User type is required",
          },
        },
        language: {
          label: "Language",
          english: "English",
          hindi: "हिंदी",
        },
        password: {
          label: "Password",
          hints: {
            keepCurrent: "(leave blank to keep current)",
          },
          placeholders: {
            add: "Enter password",
            edit: "Leave blank to keep current password",
          },
          aria: {
            show: "Show password",
            hide: "Hide password",
          },
          errors: {
            required: "Password is required",
            policy:
              "Password must be at least 8 characters and include uppercase, lowercase, a number and a symbol",
          },
        },
        confirmPassword: {
          label: "Confirm Password",
          placeholders: {
            add: "Confirm password",
            edit: "Confirm new password",
          },
          aria: {
            show: "Show confirm password",
            hide: "Hide confirm password",
          },
          errors: {
            required: "Please confirm the password",
            mismatch: "Passwords do not match",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Create User",
          edit: "Update User",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
    },
    view: {
      header: {
        sectionTitle: "User Details",
        fallbackTitle: "User Profile",
      },
      loading: "Loading user details...",
      empty: {
        title: "User data unavailable",
        message: "We couldn't load the selected user. Please try again.",
      },
      fields: {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email",
        role: "Role",
        phoneNumber: "Phone Number",
        mobileNumber: "Mobile Number",
        status: "Status",
        userId: "User ID",
        createdAt: "Created At",
      },
      buttons: {
        close: "Close",
      },
    },
    notifications: {
      created: "{name} has been created successfully",
      updated: "{name} has been updated successfully",
      deleted: "{name} has been deleted successfully",
      activated: "{name} has been activated",
      deactivated: "{name} has been deactivated",
      bulkDeletedSingle: "1 user has been deleted successfully",
      bulkDeletedMultiple: "{count} users have been deleted successfully",
    },
    alerts: {
      fetchFailed: "Failed to fetch users",
      unexpected: "An unexpected error occurred",
      createFailed: "Failed to create user",
      updateFailed: "Failed to update user",
      deleteFailed: "Failed to delete user",
      exportFailed: "Failed to export users",
      noSelection: "Select at least one user to continue",
    },
    warehousesModal: {
      title: "Assigned Warehouses",
      empty: "No warehouses assigned to this user.",
    },
  },
  customers: {
    header: {
      title: "Customers",
      subtitle: "Manage customer records, account status, and imports",
      totalCard: {
        label: "Total Customers",
      },
      selectedCard: {
        label: "Selected",
      },
    },
    actions: {
      searchPlaceholder: "Search customers...",
      searchAriaLabel: "Search customers",
      filters: {
        onboarding: {
          label: "Onboarding",
          options: {
            approved: "Approved only",
            all: "All statuses",
            invited: "Invited",
            profileSubmitted: "Profile submitted",
            rejected: "Rejected",
          },
        },
        customerType: {
          label: "Customer Type",
          placeholder: "All types",
          options: {
            all: "All",
            customer: "Customer",
            supplier: "Supplier",
          },
        },
        state: {
          label: "State",
          placeholder: "All states",
        },
        city: {
          label: "City",
          placeholder: "All cities",
        },
        status: {
          label: "Status",
          options: {
            all: "All",
            active: "Active",
            inactive: "Inactive",
          },
        },
        reset: "Clear filters",
      },
      buttons: {
        add: "Add Customer",
        import: "Import Excel",
        export: "Export CSV",
        deleteSelected: "Delete Selected",
        refresh: "Refresh",
      },
      alerts: {
        noSelection: "Select at least one customer to continue.",
        noDataToExport: "No customers available to export",
      },
    },
    statuses: {
      active: "Active",
      inactive: "Inactive",
    },
    pagination: {
      pageOfTotal: "Page {current} of {total} — {count} total customers",
      previous: "Previous",
      next: "Next",
    },
    fallbacks: {
      customerName: "customer",
      errorTitle: "Error",
    },
    table: {
      columns: {
        select: "Select",
        company: "Tally Name",
        owner: "Owner",
        contact: "Contact Person",
        type: "Customer Type",
        location: "Location",
        status: "Status",
        createdAt: "Created",
        actions: "Actions",
      },
      emptyState: {
        defaultTitle: "No Customers Found",
        defaultMessage: "Add your first customer to get started.",
        filteredTitle: "No customers match your filters",
        filteredMessage: "Try adjusting your filters or search keywords.",
      },
      tooltips: {
        view: "View Customer Details",
        edit: "Edit Customer",
        activate: "Activate Customer",
        deactivate: "Deactivate Customer",
        delete: "Delete Customer",
      },
    },
    forms: {
      title: {
        add: "Add New Customer",
        edit: "Edit Customer",
      },
      subtitle: {
        add: "Complete the form to add a new customer record.",
        edit: "Update the customer information as needed.",
      },
      fields: {
        ownerName: {
          label: "Owner Name",
          placeholder: "Enter owner name",
          errors: {
            required: "Owner name is required",
          },
        },
        companyName: {
          label: "Company Name",
          placeholder: "Enter company name",
          errors: {
            required: "Company name is required",
          },
        },
        gstNumber: {
          label: "GST Number",
          placeholder: "Enter GST number",
          errors: {
            required: "GST number is required",
          },
        },
        contactPerson: {
          label: "Contact Person",
          placeholder: "Enter contact person name",
          errors: {
            required: "Contact person name is required",
          },
        },
        customerType: {
          label: "Customer Type",
          placeholder: "Select Customer",
          errors: {
            required: "Customer type is required",
          },
          options: {
            select: "Select Customer",
            customer: "Customer",
            supplier: "Supplier",
          },
        },
        address1: {
          label: "Address Line 1",
          placeholder: "Enter primary address",
          errors: {
            required: "Address Line 1 is required",
          },
        },
        address2: {
          label: "Address Line 2",
          placeholder: "Enter additional address info",
        },
        state: {
          label: "State",
          placeholder: "Enter state",
          errors: {
            required: "State is required",
          },
        },
        city: {
          label: "City",
          placeholder: "Enter city",
          errors: {
            required: "City is required",
          },
        },
        country: {
          label: "Country",
          placeholder: "Enter country",
          errors: {
            required: "Country is required",
          },
        },
        zipCode: {
          label: "ZIP Code",
          placeholder: "Enter ZIP or postal code",
          errors: {
            required: "ZIP Code is required",
            invalid: "Enter a valid ZIP or postal code",
          },
        },
        deliveryLocation: {
          label: "Delivery Location",
          placeholder: "Enter delivery location notes",
        },
        email: {
          label: "Email",
          placeholder: "Enter email address",
          errors: {
            invalid: "Enter a valid email address",
          },
        },
        mobileNumber: {
          label: "Mobile Number",
          placeholder: "Enter mobile number",
          errors: {
            invalid: "Enter a valid mobile number",
          },
        },
        phoneNumber1: {
          label: "Primary Phone Number",
          placeholder: "Enter primary phone number",
          errors: {
            invalid: "Enter a valid 10-digit phone number",
          },
        },
        phoneNumber2: {
          label: "Secondary Phone Number",
          placeholder: "Enter secondary phone number",
          errors: {
            invalid: "Enter a valid 10-digit phone number",
          },
        },
        tallyName: {
          label: "Tally Name",
          placeholder: "Enter tally account name",
          errors: {
            required: "Tally name is required",
          },
        },
        sisterCompany: {
          label: "Sister Company",
          placeholder: "Is this a sister company?",
        },
        creditPeriod: {
          label: "Credit Period (days)",
          placeholder: "Enter credit period in days",
          errors: {
            required: "Credit period is required",
          },
        },
        isActive: {
          label: "Active",
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Create Customer",
          edit: "Update Customer",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
    },
    details: {
      header: {
        sectionTitle: "Customer Details",
        fallbackTitle: "Customer Record",
      },
      loading: "Loading customer details...",
      empty: {
        title: "Customer data unavailable",
        message: "We couldn't load the selected customer. Please try again.",
      },
      fields: {
        ownerName: "Owner Name",
        companyName: "Company Name",
        gstNumber: "GST Number",
        contactPerson: "Contact Person",
        customerType: "Customer Type",
        address1: "Address Line 1",
        address2: "Address Line 2",
        state: "State",
        city: "City",
        country: "Country",
        zipCode: "ZIP Code",
        deliveryLocation: "Delivery Location",
        email: "Email",
        phoneNumber1: "Primary Phone",
        phoneNumber2: "Secondary Phone",
        tallyName: "Tally Name",
        creditPeriod: "Credit Period",
        status: "Status",
        createdAt: "Created At",
        updatedAt: "Last Updated",
      },
      buttons: {
        close: "Close",
      },
    },
    import: {
      title: "Import Customers",
      subtitle:
        "Upload an Excel file (.xls or .xlsx) to bulk import customers.",
      description:
        "Ensure the file matches the required template with column headers.",
      uploadLabel: "Upload Excel File",
      helper: "Accepted formats: .xls, .xlsx. Maximum size 5 MB.",
      status: {
        ready: "Ready to import",
        uploading: "Uploading file...",
        success: "Import completed successfully",
        error: "Import failed, please review the errors and try again.",
      },
      errors: {
        noFile: "Please select a file to import.",
        invalidType: "Only .xls or .xlsx files are supported.",
        uploadFailed: "Failed to import customers. Please try again later.",
      },

      buttons: {
        cancel: "Cancel",
        import: "Import Customers",
        importing: "Importing...",
        downloadTemplate: "Download Sample Excel Sheet",
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete Customer",
        message:
          "Are you sure you want to delete {name}? This action cannot be undone.",
      },
      deleteMultiple: {
        title: "Delete Customers",
        message:
          "Are you sure you want to delete {count} selected customers? This action cannot be undone.",
      },
      deactivate: {
        title: "Deactivate Customer",
        message: "Are you sure you want to deactivate {name}?",
      },
      activate: {
        title: "Activate Customer",
        message: "Are you sure you want to activate {name}?",
      },
      buttons: {
        confirm: "Confirm",
        delete: "Delete",
        activate: "Activate",
        deactivate: "Deactivate",
        cancel: "Cancel",
      },
    },
    notifications: {
      created: "Customer created successfully",
      updated: "Customer updated successfully",
      deleted: "Customer deleted successfully",
      statusUpdated: "Customer status updated",
      importSuccess: "Customers imported successfully",
      importFailed: "Customer import failed",
    },
    export: {
      filename: "customers_export",
      columns: {
        owner_name: "Owner Name",
        company_name: "Company Name",
        gst_number: "GST Number",
        contact_person: "Contact Person",
        customer_type: "Customer Type",
        state: "State",
        city: "City",
        email: "Email",
        phone_number_1: "Primary Phone",
        phone_number_2: "Secondary Phone",
        tally_name: "Tally Name",
        credit_period: "Credit Period",
        is_active: "Status",
        created_at: "Created At",
      },
    },
  },
  hsnCodes: {
    page: {
      header: {
        title: "HSN Codes Management",
        subtitle:
          "Manage Harmonized System of Nomenclature codes and their tax associations",
      },
      stats: {
        total: "Total HSN Codes",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Error",
      },
      accessDenied: {
        title: "Access Denied",
        message: "You do not have permission to access HSN codes management.",
      },
    },
    actions: {
      searchPlaceholder: "Search HSN codes...",
      buttons: {
        add: "Add HSN Code",
        export: "Export",
        bulkDelete: "Delete Selected",
        refresh: "Refresh",
      },
      filters: {
        hsnCode: "HSN Code",
        chapter: "Chapter",
        gstRate: "GST Rate",
        minRate: "Min Rate",
        maxRate: "Max Rate",
      },
    },
    table: {
      columns: {
        hsnCode: "HSN Code",
        description: "Description",
        tax: "Tax",
        actions: "Actions",
      },
      emptyState: {
        title: "No HSN Codes Found",
        message: "No HSN codes match your current search criteria.",
      },
      aria: {
        viewHsnCode: "View HSN code details",
        editHsnCode: "Edit HSN code",
        deleteHsnCode: "Delete HSN code",
        selectHsnCode: "Select HSN code",
        selectAll: "Select all HSN codes",
      },
    },
    forms: {
      title: {
        add: "Add HSN Code",
        edit: "Edit HSN Code",
      },
      fields: {
        hsnCode: {
          label: "HSN Code",
          placeholder: "Enter HSN code (e.g., 8703)",
          errors: {
            required: "HSN code is required",
            invalid: "HSN code must be exactly 4, 6, or 8 digits",
          },
        },
        description: {
          label: "Description",
          placeholder: "Enter HSN description",
          errors: {
            required: "Description is required",
            minLength: "Description must be at least 5 characters",
            duplicate: "Another HSN with the same description already exists",
          },
        },
        tax: {
          label: "Tax",
          placeholder: "Select tax configuration",
          errors: {
            required: "Tax ID is required",
            invalid: "Please select a valid tax configuration",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Add HSN Code",
          edit: "Update HSN Code",
          loading: {
            add: "Adding...",
            edit: "Updating...",
          },
        },
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete HSN Code",
        message:
          "Are you sure you want to delete this HSN code? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete HSN Codes",
        messageSingle: "Are you sure you want to delete the selected HSN code?",
        messageMultiple:
          "Are you sure you want to delete {count} selected HSN codes?",
        confirmLabel: "Delete Selected",
        loadingLabel: "Deleting...",
      },
      buttons: {
        cancel: "Cancel",
      },
    },
  },
  navigation: {
    appName: "Order Management System",
    pages: {
      dashboard: "Dashboard",
      paymentsTracking: "Payments Tracking",
      overview: "Overview",
      sales: "Sales",
      delivery: "Delivery",
      purchase: "Purchase",
      financial: "Financial",
      users: "Users",
      ipAddresses: "IP Addresses",
      customers: "Customers",
      warehouses: "Warehouses",
      inventory: "Inventory",
      products: "Products",
      purchaseOrders: "Purchase Orders",
      category: "Category",
      subCategory: "Sub Category",
      product: "Product",
      taxes: "Tax Management",
      hsnCodes: "HSN Codes",
      locations: "Locations",
      inquiries: "Inquiries",
      purchaseAdvice: "Purchase Advice",
      purchaseChallan: "Purchase Challan",
      purchaseDaybook: "Purchase Daybook",
      // Order related labels
      order: "Order",
      ordersLabel: "Orders",
      deliveryOrderLabel: "Delivery Orders",
      deliveryOrder: "Delivery Order",
      deliveryChallan: "Delivery Challan",
      invoiceSales: "Proforma Invoice",
      invoicePurchase: "Purchase Estimate",
      salesDaybook: "Sales Daybook",
      ordersDaily: "Daily Orders",
      reports: "Reports",
      tallyIntegration: "Tally Integration",
      // AI Advisor
      ai: "AI",
      aiChat: "AI Chat",
      aiPurchasePlan: "Purchase Plan",
      // Settings
      settings: "Settings",
      channelSettings: "Settings",
      tallySettings: "Tally Settings",
      scheduledReports: "Scheduled Reports",
      intelligence: "Analytics Intelligence",
    },
    pageSubtitles: {
      purchaseChallan: "Manage purchase challans",
      purchaseChallanPrinted: "Purchase challans that have been printed",
      purchaseOrders: "Manage purchase orders and suppliers",
    },
    pageMetrics: {
      totalChallans: "Total Challans",
      totalPrinted: "Total Printed",
      totalOrders: "Total Orders",
    },
    pageTitles: {
      purchaseChallanPrinted: "Purchase Challan Printed",
    },
    sidebar: {
      mobile: {
        openMenu: "Open menu",
        closeMenu: "Close menu",
      },
      collapse: {
        expand: "Expand sidebar",
        collapse: "Collapse sidebar",
      },
      logout: "Logout",
      profile: "Profile",
      userFallback: "User",
      loading: "Loading...",
    },
    badges: {
      role: "Role",
    },
  },
  orders: {},
  purchaseOrders: {},
  deliveryChallans: {
    page: {
      header: {
        title: "Delivery Challans",
        subtitle: "Manage delivery challans and track shipments",
      },
      stats: {
        total: "Total Challans",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Error",
      },
    },
    table: {
      columns: {
        select: "Select",
        orderNumber: "Order Number",
        serialNumber: "Serial Number",
        challanNumber: "Challan Number",
        date: "Date",
        challanDate: "Challan Date",
        customerName: "Customer Name",
        totalAmount: "Total Amount",
        status: "Status",
        actions: "Actions",
      },
      emptyState: {
        title: "No delivery challans found",
        message: "There are no delivery challans to display right now.",
      },
      empty: {
        title: "No delivery challans found",
        message: "There are no delivery challans to display right now.",
      },
      aria: {
        selectAll: "Select all delivery challans",
        selectChallan: "Select delivery challan",
        viewChallan: "View Delivery Challan",
        updateChallan: "Update Delivery Challan",
        printChallan: "Print Delivery Challan",
      },
      tooltips: {
        view: "View delivery challan",
        updateDeliveryChallan: "Update delivery challan",
        printDeliveryChallan: "Print Delivery Challan",
        markDelivered: "Mark Delivered",
      },
    },
    statuses: {
      pending: "Pending",
      in_transit: "In Transit",
      delivered: "Delivered",
      invoiced: "Invoiced",
    },
    messages: {
      updateSuccess: "Delivery challan updated successfully",
      updateFailed: "Failed to update delivery challan",
      markDeliveredSuccess: "Delivery challan marked as delivered",
      markDeliveredFailed: "Failed to mark delivery challan as delivered",
      confirmMarkDelivered: "Mark this delivery challan as delivered?",
    },
    actions: {
      searchPlaceholder: "Search delivery challans...",
      buttons: {
        export: "Export Challans",
        bulkDelete: "Delete Selected",
      },
    },
  },
  invoiceSales: {
    page: {
      header: {
        title: "Proforma Invoice",
        subtitle: "View sales daybook and invoice records",
      },
      stats: {
        total: "Total Records",
      },
      alerts: {
        errorTitle: "Error",
      },
    },
    table: {
      columns: {
        date: "Date",
        orderNumber: "Order Number",
        tallyName: "Tally Name",
        vehicleNumber: "Vehicle Number",
        supervisorName: "Supervisor",
        labourCount: "Labour",
        totalAmount: "Total Amount",
        remark: "Remarks",
        invoiceNumber: "Invoice Number",
        status: "Status",
      },
      emptyState: {
        title: "No sales records found",
        message:
          "There are no sales records to display for the selected date range.",
      },
      aria: {
        selectAll: "Select all sales records",
        selectRecord: "Select sales record",
      },
    },
    statuses: {
      pending: "Pending",
      in_transit: "In Transit",
      delivered: "Delivered",
      invoiced: "Invoiced",
    },
    actions: {
      searchPlaceholder: "Search sales records...",
      fromPlaceholder: "From",
      toPlaceholder: "To",
      buttons: {
        refresh: "Refresh",
      },
    },
  },
  salesDaybook: {
    page: {
      header: {
        title: "Sales Daybook",
        subtitle: "View and manage sales daybook records",
      },
      stats: {
        total: "Total Records",
      },
      alerts: {
        errorTitle: "Error",
      },
    },
    table: {
      columns: {
        date: "Date",
        orderNumber: "Order Number",
        tallyName: "Tally Name",
        vehicleNumber: "Vehicle Number",
        supervisorName: "Supervisor",
        labourCount: "Labour",
        totalAmount: "Total Amount",
        remark: "Remarks",
        invoiceNumber: "Invoice Number",
        status: "Status",
      },
      emptyState: {
        title: "No sales daybook records found",
        message:
          "There are no sales daybook records to display for the selected date range.",
      },
      aria: {
        selectAll: "Select all daybook records",
        selectRecord: "Select daybook record",
      },
    },
    statuses: {
      pending: "Pending",
      in_transit: "In Transit",
      delivered: "Delivered",
      invoiced: "Invoiced",
    },
    actions: {
      searchPlaceholder: "Search daybook records...",
      fromPlaceholder: "From",
      toPlaceholder: "To",
      buttons: {
        refresh: "Refresh",
      },
    },
  },
  profile: {
    title: "My Profile",
    subtitle: "Manage your personal information and account settings",
    loading: "Loading profile...",
    fields: {
      freight: { label: "Freight", placeholder: "Enter freight amount" },
      discountType: {
        label: "Discount Type",
        placeholder: "eg: percent | flat",
      },
      discountValue: {
        label: "Discount Value",
        placeholder: "Enter discount value",
      },
      loadingCost: { label: "Loading Cost", placeholder: "Enter loading cost" },
      gstOption: {
        label: "GST Option",
        placeholder: "gst inclusive | exclusive",
      },
      gstnNumber: { label: "GSTN Number", placeholder: "Enter GSTN number" },

      smsNotification: { label: "SMS Notification", placeholder: "" },
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      phoneNumber: "Phone Number",
      mobileNumber: "Mobile Number",
      userType: "User Type",
      language: "Language",
    },
    language: {
      label: "Language",
      english: "English",
      hindi: "हिंदी",
      updated: "Language updated",
    },
    buttons: {
      edit: "Edit Profile",
      cancel: "Cancel",
      save: "Save Changes",
      saving: "Saving...",
    },
    validation: {
      firstNameMin: "First name must be at least 2 characters",
      lastNameMin: "Last name must be at least 2 characters",
      emailInvalid: "Please enter a valid email address",
      phoneInvalid: "Phone number must be 10 digits",
      mobileInvalid: "Mobile number must be 10 digits",
    },
    note: "Your user type is managed by administrators and cannot be changed from this page. Contact your system administrator if you need role changes.",
    notifications: {
      updated: "Profile updated successfully",
    },
    errors: {
      fetchFailed: "Failed to fetch profile",
      updateFailed: "Failed to update profile",
    },
    resetPassword: {
      title: "Reset Password",
      fields: {
        currentPassword: "Current Password",
        newPassword: "New Password",
        confirmPassword: "Confirm New Password",
      },
      buttons: {
        reset: "Reset Password",
        resetting: "Resetting...",
        cancel: "Cancel",
      },
      notifications: {
        success: "Password reset successfully",
      },
      errors: {
        currentPasswordRequired: "Current password is required",
        newPasswordRequired: "New password is required",
        passwordTooShort: "Password must be at least 8 characters long",
        passwordMissingLowercase:
          "Password must contain at least one lowercase letter",
        passwordMissingUppercase:
          "Password must contain at least one uppercase letter",
        passwordMissingNumber: "Password must contain at least one number",
        passwordMissingSymbol:
          "Password must contain at least one special character",
        confirmPasswordRequired: "Please confirm your new password",
        confirmPasswordMismatch: "Passwords do not match",
      },
    },
    // Inline "Change Password" section within the user create/edit form.
    passwordReset: {
      heading: "Change Password",
      helper: "Fill both password fields above, then click to reset.",
      resetting: "Resetting...",
      submit: "Update Password Now",
      success: "Password reset successfully",
      error: "Failed to reset password",
      unexpectedError: "An unexpected error occurred",
    },
    submitFallbackError: "Failed to save user. Please check the details and try again.",
    removeWarehouse: "Remove",
    warehouseFallback: "Warehouse {id}",
  },
  login: {
    brand: {
      name: "Order Management System",
      alt: "Order Fulfillment",
    },
    header: {
      welcome: "Welcome back",
      subtitle: "Please sign in to your account",
    },
    form: {
      labels: {
        email: "Email or Mobile Number",
        password: "Password",
      },
      placeholders: {
        email: "Enter email or mobile number",
        password: "Enter your password",
      },
      rememberMe: "Remember me",
      submit: {
        default: "Sign in",
        loading: "Signing in...",
      },
      aria: {
        showPassword: "Show password",
        hidePassword: "Hide password",
      },
      validation: {
        emailRequired: "Email or mobile number is required",
        emailInvalid:
          "Please enter a valid email address or 10-digit mobile number",
        passwordRequired: "Password is required",
        passwordLength: "Password must be at least 8 characters long",
        passwordLowercase:
          "Password must contain at least one lowercase letter",
        passwordUppercase:
          "Password must contain at least one uppercase letter",
        passwordNumber: "Password must contain at least one number",
        passwordSymbol:
          "Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)",
      },
      errors: {
        unexpected: "An unexpected error occurred. Please try again.",
      },
    },
    messages: {
      loginSuccess: "Login successful:",
      loginError: "Login error:",
    },
  },
  locations: {
    defaults: {
      country: "India",
    },
    page: {
      header: {
        title: "Locations Management",
        subtitle:
          "Manage warehouse locations, offices, stores and distribution centers",
      },
      accessDenied: {
        title: "Access Denied",
        message: "You do not have permission to access locations management.",
      },
      stats: {
        total: "Total Locations",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Locations Error",
      },
    },
    actions: {
      searchPlaceholder: "Search locations...",
      buttons: {
        add: "Add Location",
        edit: "Edit Location",
        delete: "Delete Location",
        bulkDelete: "Delete Selected",
        export: "Export Locations",
        view: "View Details",
        activate: "Activate",
        deactivate: "Deactivate",
        refresh: "Refresh",
      },
      toolbar: {
        title: "Location Actions",
        description:
          "Manage locations with search, filters and bulk operations",
      },
    },
    table: {
      columns: {
        id: "ID",
        name: "Location Name",
        type: "Type",
        address: "Address",
        addressLine1: "Address Line 1",
        addressLine2: "Address Line 2",
        location: "Location",
        city: "City",
        state: "State",
        country: "Country",
        postalCode: "Postal Code",
        phone: "Phone",
        email: "Email",
        status: "Status",
        createdAt: "Created",
        actions: "Actions",
        map_url: "Map",
      },
      copy: {
        unnamedLocation: "Unnamed Location",
        noAddress: "No address provided",
        noPhone: "No phone",
        viewMap: "View Map",
      },
      status: {
        active: "Active",
        inactive: "Inactive",
      },
      aria: {
        selectAll: "Select all locations",
        selectRow: "Select",
      },
    },
    forms: {
      title: {
        add: "Add Location",
        edit: "Edit Location",
      },
      buttons: {
        cancel: "Cancel",
        save: "Save",
        previewMap: "Preview Map",
        submit: {
          add: "Save",
          loading: "Saving...",
        },
      },
      map: {
        title: "Select Location",
        buttons: {
          currentLocation: "Current Location",
          useLocation: "Use Location",
          close: "Close",
        },
        tooltips: {
          currentLocation: "Use current location",
        },
        errors: {
          geolocationNotSupported:
            "Geolocation is not supported by this browser.",
          permissionDenied: "Location access denied by user.",
          positionUnavailable: "Location information is unavailable.",
          timeout: "Location request timed out.",
          unknown: "An unknown error occurred while retrieving location.",
          mapLoad: "Failed to load map. Please check your internet connection.",
          selectedLocation: "Error getting selected location.",
        },
        instructions:
          'Click on the map or drag the marker to select a location. Use "Current Location" to center on your position, then click "Use Location" to confirm.',
        markerTitle: "Drag me to select location",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
      create: {
        title: "Add New Location",
        submit: "Create Location",
        submitting: "Creating...",
      },
      edit: {
        title: "Edit Location",
        submit: "Update Location",
        submitting: "Updating...",
      },
      fields: {
        name: {
          label: "Location Name",
          placeholder: "Enter location name",
          errors: {
            required: "Location name is required",
            minLength: "Location name must be at least 2 characters",
          },
        },
        locationType: {
          label: "Location Type",
          placeholder: "Select location type",
          errors: {
            required: "Location type is required",
          },
        },
        addressLine1: {
          label: "Address Line 1",
          placeholder: "Enter address line 1",
          errors: {
            required: "Address line 1 is required",
          },
        },
        addressLine2: {
          label: "Address Line 2",
          placeholder: "Enter address line 2 (optional)",
        },
        city: {
          label: "City",
          placeholder: "Enter city",
          errors: {
            required: "City is required",
          },
        },
        state: {
          label: "State",
          placeholder: "Select state",
          errors: {
            required: "State is required",
          },
        },
        country: {
          label: "Country",
          placeholder: "Enter country",
          errors: {
            required: "Country is required",
          },
        },
        postalCode: {
          label: "Postal Code",
          placeholder: "Enter postal code",
          errors: {
            invalid: "Postal code must be 5-6 digits",
          },
        },
        phone: {
          label: "Phone Number",
          placeholder: "Enter phone number",
          errors: {
            invalid: "Phone number format is invalid",
          },
        },
        email: {
          label: "Email",
          placeholder: "Enter email address",
          errors: {
            invalid: "Email format is invalid",
          },
        },
        description: {
          label: "Description",
          placeholder: "Enter location description (optional)",
        },
        deliveryLocation: {
          label: "Delivery Location",
          placeholder: "Enter specific delivery location or coordinates",
          errors: {
            required: "Delivery location is required",
          },
        },
        mapUrl: {
          label: "Map URL",
          placeholder: "https://maps.google.com/?q=...",
          errors: {
            invalid:
              "Please enter a valid URL (starting with http:// or https://)",
          },
        },
        isActive: {
          label: "Active Status",
        },
      },
      validation: {
        failed: "Please fix the form errors before submitting",
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete Location",
        message:
          "Are you sure you want to delete this location? This action cannot be undone.",
        confirmLabel: "Delete Location",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete Locations",
        messageSingle: "Are you sure you want to delete the selected location?",
        messageMultiple:
          "Are you sure you want to delete {count} selected locations?",
        confirmLabel: "Delete Locations",
        loadingLabel: "Deleting...",
      },
      toggleStatus: {
        activate: {
          title: "Activate Location",
          message: "Are you sure you want to activate this location?",
          confirmLabel: "Activate",
          loadingLabel: "Activating...",
        },
        deactivate: {
          title: "Deactivate Location",
          message: "Are you sure you want to deactivate this location?",
          confirmLabel: "Deactivate",
          loadingLabel: "Deactivating...",
        },
      },
      buttons: {
        cancel: "Cancel",
      },
    },
    details: {
      title: "Location Details",
      sections: {
        basic: "Basic Information",
        address: "Address Information",
        contact: "Contact Information",
        status: "Status Information",
      },
      fields: {
        id: "Location ID",
        name: "Location Name",
        type: "Location Type",
        addressLine1: "Address Line 1",
        addressLine2: "Address Line 2",
        city: "City",
        state: "State",
        country: "Country",
        postalCode: "Postal Code",
        phone: "Phone Number",
        email: "Email Address",
        description: "Description",
        isActive: "Status",
        createdAt: "Created Date",
        updatedAt: "Last Updated",
      },
    },
    messages: {
      loading: "Loading locations...",
      noData: "No locations found",
      noDataLong:
        'No locations have been added yet. Click "Add Location" to get started.',
      fetchError: "Failed to load locations",
      refreshSuccess: "Locations refreshed successfully",
      refreshError: "Failed to refresh locations",
      createSuccess: "Location created successfully",
      createError: "Failed to create location",
      updateSuccess: "Location updated successfully",
      updateError: "Failed to update location",
      deleteSuccess: "Location deleted successfully",
      deleteError: "Failed to delete location",
      bulkDeleteSuccess: "Locations deleted successfully",
      bulkDeleteError: "Failed to delete locations",
      statusUpdateSuccess: "Location status updated successfully",
      statusUpdateError: "Failed to update location status",
      exportSuccess: "Locations exported successfully",
      exportError: "Failed to export locations",
    },
    filters: {
      all: "All Locations",
      active: "Active Only",
      inactive: "Inactive Only",
      state: "Filter by State",
      status: "Status",
      city: "Filter by City",
      type: "Filter by Type",
    },
    types: {
      warehouse: "Warehouse",
      office: "Office",
      store: "Store",
      distribution_center: "Distribution Center",
      pickup_point: "Pickup Point",
      delivery_hub: "Delivery Hub",
      service_center: "Service Center",
      other: "Other",
    },
    // List of Indian states and union territories for filter dropdowns
    states: [
      { value: "ANDHRA_PRADESH", label: "Andhra Pradesh" },
      { value: "ARUNACHAL_PRADESH", label: "Arunachal Pradesh" },
      { value: "ASSAM", label: "Assam" },
      { value: "BIHAR", label: "Bihar" },
      { value: "CHHATTISGARH", label: "Chhattisgarh" },
      { value: "GOA", label: "Goa" },
      { value: "GUJARAT", label: "Gujarat" },
      { value: "HARYANA", label: "Haryana" },
      { value: "HIMACHAL_PRADESH", label: "Himachal Pradesh" },
      { value: "JHARKHAND", label: "Jharkhand" },
      { value: "KARNATAKA", label: "Karnataka" },
      { value: "KERALA", label: "Kerala" },
      { value: "MADHYA_PRADESH", label: "Madhya Pradesh" },
      { value: "MAHARASHTRA", label: "Maharashtra" },
      { value: "MANIPUR", label: "Manipur" },
      { value: "MEGHALAYA", label: "Meghalaya" },
      { value: "MIZORAM", label: "Mizoram" },
      { value: "NAGALAND", label: "Nagaland" },
      { value: "ODISHA", label: "Odisha" },
      { value: "PUNJAB", label: "Punjab" },
      { value: "RAJASTHAN", label: "Rajasthan" },
      { value: "SIKKIM", label: "Sikkim" },
      { value: "TAMIL_NADU", label: "Tamil Nadu" },
      { value: "TELANGANA", label: "Telangana" },
      { value: "TRIPURA", label: "Tripura" },
      { value: "UTTAR_PRADESH", label: "Uttar Pradesh" },
      { value: "UTTARAKHAND", label: "Uttarakhand" },
      { value: "WEST_BENGAL", label: "West Bengal" },
      // Union Territories
      { value: "ANDAMAN_NICOBAR", label: "Andaman and Nicobar Islands" },
      { value: "CHANDIGARH", label: "Chandigarh" },
      {
        value: "DADRA_NAGAR_HAVELI_DAMAN_DIU",
        label: "Dadra and Nagar Haveli and Daman and Diu",
      },
      { value: "DELHI", label: "Delhi" },
      { value: "JAMMU_KASHMIR", label: "Jammu and Kashmir" },
      { value: "LADAKH", label: "Ladakh" },
      { value: "LAKSHADWEEP", label: "Lakshadweep" },
      { value: "PUDUCHERRY", label: "Puducherry" },
    ],
  },
  warehouses: {
    page: {
      header: {
        title: "Warehouses",
        subtitle: "Manage warehouses and inventory locations",
      },
      stats: {
        total: "Total Warehouses",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Warehouses Error",
      },
    },
    actions: {
      searchPlaceholder: "Search warehouses...",
      buttons: {
        add: "Add Warehouse",
        refresh: "Refresh",
        bulkDelete: "Delete Selected",
      },
      alerts: {
        noSelection: "Select at least one warehouse to continue.",
      },
    },
    table: {
      columns: {
        select: "Select",
        name: "Warehouse Name",
        total_capacity: "Total Capacity (m²)",
        address: "Address",
        google_maps_url: "Map",
        type: "Type",
        location: "Location",
        manager: "Manager",
        phone: "Phone",
        email: "Email",
        status: "Status",
        createdAt: "Created",
        actions: "Actions",
      },
      emptyState: {
        title: "No Warehouses Found",
        message: "There are no warehouses to display.",
      },
      aria: {
        selectAll: "Select all warehouses",
        viewWarehouse: "View Warehouse",
        editWarehouse: "Edit Warehouse",
        deleteWarehouse: "Delete Warehouse",
        openMap: "Open map",
      },
    },
    forms: {
      title: {
        add: "Add New Warehouse",
        edit: "Edit Warehouse",
      },
      fields: {
        name: {
          label: "Warehouse Name",
          placeholder: "Enter warehouse name",
          errors: {
            required: "Warehouse name is required",
          },
        },
        code: { label: "Code", placeholder: "e.g., WH-001" },
        address: { label: "Address", placeholder: "Enter address" },
        city: { label: "City", placeholder: "Enter city" },
        state: { label: "State", placeholder: "Select state" },
        pincode: { label: "PIN Code", placeholder: "Enter PIN code" },
        country: { label: "Country", placeholder: "Enter country" },
        postalCode: { label: "Postal Code", placeholder: "Enter postal code" },
        contact_person: {
          label: "Contact Person",
          placeholder: "Enter contact person name",
        },
        contact_phone: {
          label: "Contact Phone",
          placeholder: "Enter contact phone",
        },
        contact_email: {
          label: "Contact Email",
          placeholder: "Enter contact email",
        },
        manager: { label: "Manager", placeholder: "Enter manager name" },
        phone: { label: "Phone", placeholder: "Enter phone number" },
        email: { label: "Email", placeholder: "Enter email address" },
        total_capacity: {
          label: "Total Capacity (m²)",
          placeholder: "e.g., 500 m²",
        },
        google_maps_url: {
          label: "Google Maps URL",
          placeholder: "Select location on map",
        },
        isActive: { label: "Active" },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Create Warehouse",
          edit: "Update Warehouse",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
    },
    view: {
      header: {
        sectionTitle: "Warehouse Details",
        fallbackTitle: "Warehouse Information",
      },
      loading: "Loading warehouse details...",
      empty: {
        title: "No Warehouse Found",
        message: "The warehouse details could not be loaded.",
      },
      fields: {
        warehouseName: "Warehouse Name",
        totalCapacity: "Total Capacity (m²)",
        status: "Status",
        fullAddress: "Full Address",
        googleMaps: "Google Maps",
        viewOnGoogleMaps: "View on Google Maps",
      },
      buttons: {
        close: "Close",
      },
      sections: {
        basicInformation: "Basic Information",
        addressInformation: "Address Information",
        location: "Location",
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete Warehouse",
        message:
          "Are you sure you want to delete this warehouse? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete Selected Warehouses",
        messageMultiple:
          "Are you sure you want to delete {count} selected warehouses? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      buttons: {
        cancel: "Cancel",
      },
    },
    notifications: {
      created: "Warehouse {id} created successfully",
      updated: "Warehouse {id} updated successfully",
      deleted: "Warehouse {id} deleted successfully",
      bulkDeleted: "{count} warehouses deleted successfully",
    },
    alerts: {
      fetchFailed: "Failed to fetch warehouses",
      createFailed: "Failed to create warehouse",
      updateFailed: "Failed to update warehouse",
      deleteFailed: "Failed to delete warehouse",
      noSelection: "Select at least one warehouse to continue",
    },
    messages: {
      loading: "Loading warehouses...",
      noData: "No warehouses found",
    },
  },
  warehouseInventory: {
    import: {
      config: {
        templateFilename: "inventory_import_template",
        inputId: "inventory-import-input",
      },
      success: "Import complete — {balances} balance(s) set, {coils} coil(s) added, {minimums} minimum stock level(s) set, {mappings} product(s) added to a warehouse.",
      noChanges: "Nothing changed — every row already matches what is recorded.",
    },
    notifications: {
      created: "Inventory {id} created successfully",
      updated: "Inventory {id} updated successfully",
      deleted: "Inventory {id} deleted successfully",
      transferred: "Stock transferred successfully",
      stockUpdated: "Stock for {id} updated successfully",
      lowStock: "Low Stock",
    },
    messages: {
      loading: "Loading inventory...",
      noData: "No inventory found",
      fetchError: "Failed to load inventory",
    },
    table: {
      columns: {
        warehouse_name: "Warehouse",
        warehouse: "Warehouse",
        address: "Address",
        product_name: "Product",
        product_id: "Product ID",
        total_stock_all_warehouses: "Total Stock",
        total_pending_all_warehouses: "Total Pending",
        total_pending_purchase_all_warehouses: "Total Pending Purchase",
        total_available_all_warehouses: "Total Available",
        warehouse_count: "Warehouses",
        available_inventory: "Available Stock",
        stock_quantity: "Stock Qty",
        minimum_stock: "Min Stock",
        pending_order: "Pending Order",
        actions: "Actions",
      },
      emptyState: {
        title: "No Inventory Found",
        message: "There are no inventory items to display.",
      },
      aria: {
        viewInventory: "View Inventory",
        editInventory: "Edit Inventory",
        adjustStock: "Adjust Stock",
        deleteInventory: "Delete Inventory",
      },
    },
    actions: {
      searchPlaceholder: "Search inventory...",
      filters: {
        warehouseName: "Warehouse",
        all: "All",
        subCategory: "Sub Category",
        subCategoryAll: "All sub categories",
        productName: "Product",
        lowStock: "Low Stock",
        lowStockOptions: {
          all: "All",
          yes: "Only Low Stock",
          no: "Exclude Low Stock",
        },
      },
      buttons: {
        import: "Import",
        refresh: "Refresh",
        add: "Add Inventory",
        transferStock: "Transfer Stock",
        exportBySubcategory: "Export by Sub Category",
      },
    },
    exportBySubcategory: {
      title: "Export Inventory by Sub Category",
      subtitle: "Download a CSV with one row per product and one column per warehouse.",
      subCategoryLabel: "Sub Categories",
      subCategoryPlaceholder: "Search sub categories...",
      subCategoryHint: "Leave empty to export every sub category.",
      selectedSuffix: "selected",
      noSubCategories: "No matching sub categories",
      warehouseLabel: "Warehouses",
      warehousePlaceholder: "All warehouses in your scope",
      warehouseHint: "Leave empty to include every warehouse you can access.",
      valueLabel: "Quantity",
      valueStock: "Stock (on-hand)",
      valueAvailable: "Available (stock − pending)",
      cancel: "Cancel",
      export: "Export CSV",
      exporting: "Exporting...",
      loadingSubCategories: "Loading sub categories...",
      success: "Inventory export downloaded successfully.",
      error: "Failed to export inventory.",
      subCategoryFallback: "Sub Category {id}",
      warehouseFallback: "Warehouse {id}",
      removeChip: "Remove {label}",
    },
    bulkEdit: {
      enterMode: "Bulk Edit",
      exitMode: "Exit Bulk Edit",
      saveAll: "Save All",
      discard: "Discard",
      discardConfirm: "Discard",
      discardTitle: "Discard changes?",
      discardMessage: "You have unsaved changes. Are you sure you want to exit bulk edit mode?",
      dirtyCount: "{count} changes across {products} products",
      savingProgress: "Saving {current} of {total}...",
      footerHint: "Edit stock, min-stock and pending values per warehouse, then click Save All.",
      hiddenHint: "{n} product(s) with edits are hidden by current filter — clear filter to review.",
      allSuccess: "Saved {count} changes across {products} products.",
      allFailed: "Failed to save any changes.",
      partialFailureTitle: "Partial Save",
      partialFailureBody: "{succeeded} of {total} products saved. Failed:\n\n",
      lowStockLabel: "LOW STOCK",
      noWarehouses: "No warehouse data available for this product.",
    },
    view: {
      header: {
        sectionTitle: "Warehouse Inventory",
        fallbackTitle: "Inventory Summary",
      },
      loading: "Loading inventory details...",
      empty: {
        title: "Inventory data unavailable",
        message: "We could not load the inventory details. Please try again.",
      },
      fields: {
        warehouseName: "Warehouse",
        warehouseAddress: "Address",
        warehouseCapacity: "Total Capacity (m²)",
        totalProducts: "Total Products",
        totalStockUnits: "Total Stock Units",
        lowStockProducts: "Low Stock Products",
      },
      productTable: {
        columns: {
          warehouse: "Warehouse Name",
          productName: "Product",
          productSize: "Size",
          stockQuantity: "Stock Quantity",
          pendingPurchaseOrder: "Pending Purchase Order",
          minimumStock: "Minimum Stock",
          lowStock: "Low Stock",
          availableStock: "Stock Available",
        },
      },
      buttons: {
        close: "Close",
      },
    },
    forms: {
      addWarehouse: {
        placeholder: "Add another warehouse…",
        button: "Add Warehouse",
        remove: "Remove warehouse",
      },
      title: {
        add: "Add Warehouse Inventory",
        edit: "Edit Warehouse Inventory",
      },
      fields: {
        warehouse_id: {
          label: "Warehouse",
          placeholder: "Select warehouse",
          errors: {
            required: "Warehouse is required",
          },
        },
        product_size_id: {
          label: "Product",
          placeholder: "Select product",
          errors: {
            required: "Product is required",
          },
        },
        stock_quantity: {
          label: "Stock Quantity",
          placeholder: "Enter stock quantity",
          errors: {
            required: "Stock quantity is required",
            invalid: "Stock quantity must be a valid positive number",
          },
        },
        minimum_stock: {
          label: "Minimum Stock",
          placeholder: "Enter minimum stock level",
          errors: {
            required: "Minimum stock is required",
            invalid: "Minimum stock must be a valid positive number",
          },
        },
        pending_order: {
          label: "Pending Order",
          placeholder: "Enter pending order quantity",
          errors: {
            invalid: "Pending order must be a valid number",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Create Inventory",
          edit: "Update Inventory",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
      loading: {
        warehouses: "Loading warehouses...",
        products: "Loading products...",
        inventory: "Loading inventory details...",
      },
    },
    adjustForm: {
      title: "Adjust Stock",
      fields: {
        quantity: {
          label: "Quantity",
          placeholder:
            "Enter quantity to add/remove (use negative for removal)",
          errors: {
            required: "Quantity is required",
            invalid: "Quantity must be a valid number (not zero)",
          },
        },
        reason: {
          label: "Reason",
          placeholder: "Enter reason for stock adjustment",
          errors: {
            required: "Reason is required",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: "Adjust Stock",
        loading: "Adjusting...",
      },
      labels: {
        adjustingFor: "Adjusting stock for:",
        atWarehousePrefix: "at",
        currentStock: "Current Stock:",
        quantityHint: "Use positive numbers to add stock, negative to remove",
        unknownProduct: "Unknown Product",
        unknownWarehouse: "Unknown Warehouse",
      },
    },
    page: {
      header: {
        title: "Inventory",
        subtitle: "Manage inventory stock levels and product distribution",
      },
      stats: {
        total: "Total Items",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Inventory Error",
      },
    },
    transferForm: {
      title: "Transfer Inventory",
      fields: {
        from_warehouse_id: {
          label: "From Warehouse",
          placeholder: "Select source warehouse",
          errors: {
            required: "Source warehouse is required",
          },
        },
        to_warehouse_id: {
          label: "To Warehouse",
          placeholder: "Select destination warehouse",
          errors: {
            required: "Destination warehouse is required",
            sameWarehouse:
              "Destination warehouse must be different from source warehouse",
          },
        },
        product_size_id: {
          label: "Product",
          placeholder: "Select product",
          errors: {
            required: "Product is required",
          },
        },
        quantity: {
          label: "Quantity",
          placeholder: "Enter quantity to transfer",
          errors: {
            required: "Quantity is required",
            invalid: "Quantity must be a positive number",
          },
        },
        reason: {
          label: "Transfer Reason",
          placeholder: "Enter reason for transfer",
          errors: {
            required: "Transfer reason is required",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: "Transfer Stock",
        loading: "Transferring...",
      },
      loading: {
        options: "Loading transfer options...",
      },
    },
  },
  productCatalogue: {
    actions: {
      searchPlaceholder: "Search products...",
      alerts: {
        noDataToExport: "No products available to export",
      },
      downloadPrefix: "product_catalogue_export_",
      buttons: {
        add: "Add Product",
        bulkDelete: "Delete Selected",
        import: "Import Excel",
        export: "Export Excel",
        refresh: "Refresh",
        downloadTemplate: "Download Template",
      },
      toolbar: {
        title: "Product Actions",
        searchPlaceholder: "Search products...",
      },
    },
    page: {
      header: {
        title: "Sub Category",
        subtitle: "Manage product inventory, pricing, and specifications",
      },
      stats: {
        total: "Total Products",
        totalProducts: "Total Products",
        totalCategories: "Categories",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Error",
      },
      accessDenied: {
        title: "Access Denied",
        message: "You must be a Super Admin to view this page.",
      },
    },
    confirm: {
      deleteSingle: {
        title: "Delete Product",
        message:
          "Are you sure you want to delete {name}? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      deleteBulk: {
        title: "Delete Selected Products",
        messageSingle:
          "Are you sure you want to delete the selected product? This action cannot be undone.",
        messageMultiple:
          "Are you sure you want to delete {count} selected products? This action cannot be undone.",
        confirmLabel: "Delete",
        loadingLabel: "Deleting...",
      },
      buttons: {
        cancel: "Cancel",
      },
    },
    table: {
      columns: {
        select: "Select",
        productName: "Product Name",
        description: "Description",
        category: "Category",

        hsnCode: "HSN Code",
        gstRate: "GST Rate",
        actions: "Actions",
      },
      copy: {
        unnamedProduct: "Unnamed product",
        noCategory: "No category",

        noHsnCode: "N/A",
        noGstRate: "0%",
        noDescription: "No description",
      },
      emptyState: {
        title: "No Products Found",
        message: "There are no products to display.",
      },
      aria: {
        selectAll: "Select all products",
        selectProduct: "Select product",
        viewProduct: "View Product",
        editProduct: "Edit Product",
        deleteProduct: "Delete Product",
      },
    },
    forms: {
      title: {
        add: "Add New Sub Category",
        edit: "Edit Sub Category",
      },
      fields: {
        productName: {
          label: "Product Name",
          placeholder: "Enter product name",
          errors: {
            required: "Product name is required",
            minLength: "Product name must be at least 2 characters",
            maxLength: "Product name cannot exceed 100 characters",
          },
        },
        description: {
          label: "Description",
          placeholder: "Enter product description",
          errors: {
            maxLength: "Description cannot exceed 500 characters",
          },
        },

        productCategoryId: {
          label: "Category",
          placeholder: "Select Category",
          errors: {
            required: "Category is required",
          },
        },
        hsnCodeId: {
          label: "HSN Code",
          placeholder: "Select HSN code",
          errors: {
            required: "HSN code is required",
          },
        },
      },
      buttons: {
        cancel: "Cancel",
        submit: {
          add: "Create Product",
          edit: "Update Product",
          loading: {
            add: "Creating...",
            edit: "Updating...",
          },
        },
      },
      validation: {
        loadingOptions: "Loading options...",
        noCategories: "No categories available",
        noHsnCodes: "No HSN codes available",
        selectCategory: "Select a category",
        selectHsnCode: "Select HSN code",
        dropdownError: "Failed to load dropdown options",
      },
    },
    view: {
      header: {
        sectionTitle: "Product Details",
        fallbackTitle: "Product Profile",
      },
      loading: "Loading product details...",
      empty: {
        title: "Product data unavailable",
        message: "We couldn't load the selected product. Please try again.",
      },
      fields: {
        productName: "Product Name",
        description: "Description",

        category: "Category",
        hsnCode: "HSN Code",
        hsnDescription: "HSN Description",
        gstRate: "GST Rate",
        id: "Product ID",
        createdAt: "Created At",
        updatedAt: "Last Updated",
      },
      buttons: {
        close: "Close",
      },
    },
    notifications: {
      created: "{name} has been created successfully",
      updated: "{name} has been updated successfully",
      deleted: "{name} has been deleted successfully",
      bulkDeletedSingle: "{product} has been deleted successfully",
      bulkDeletedMultiple: "{name} products have been deleted successfully",
      imported: "Products imported successfully",
      exported: "Products exported successfully",
    },
    alerts: {
      fetchFailed: "Failed to fetch products",
      unexpected: "An unexpected error occurred",
      createFailed: "Failed to create product",
      updateFailed: "Failed to update product",
      deleteFailed: "Failed to delete product",
      exportFailed: "Failed to export products",
      importFailed: "Failed to import products",
      noSelection: "Select at least one product to continue",
      categoriesFetchFailed: "Failed to fetch Categories",
      hsnCodesFetchFailed: "Failed to fetch HSN codes",
    },
    import: {
      title: "Import Products",
      subtitle: "Upload an Excel file to bulk import products",
      dropzone: {
        title: "Drag and drop your Excel file here",
        subtitle: "or click to browse files",
        fileTypes: "Accepts .xlsx, .xls files",
      },
      buttons: {
        downloadSample: "Download Sample Template",
        import: "Import Products",
        cancel: "Cancel",
      },
      progress: {
        uploading: "Uploading file...",
        processing: "Processing products...",
        completed: "Import completed successfully",
      },
      errors: {
        invalidFile: "Please select a valid Excel file",
        noFile: "Please select a file to import",
        invalidType: "Please select a valid Excel file (.xlsx or .xls)",
        uploadFailed: "Failed to upload file",
        processingFailed: "Failed to process file",
      },
      status: {
        ready: "Ready to import",
        uploading: "Uploading...",
        success: "Import Successful",
        error: "Import Failed",
      },
      notifications: {
        success: "Products imported successfully",
        error: "Failed to import products",
      },
      config: {
        templateHeaders: [
          "Alias Name",
          "Product Size",
          "Product Weight",
          "Standard Length",
          "Product Thickness",
          "Category",
          "Product Name",
        ],
        templateFilename: "product_import_template",
        inputId: "product-import-input",
      },
    },
    export: {
      status: {
        success: "Export Successful",
        error: "Export Failed",
      },
      notifications: {
        success: "Products exported successfully",
        error: "Failed to export products",
      },
    },
  },

  // Categories Management
  productCategories: {
    title: "Categories",
    description: "Manage Category classifications and tax rates",
    page: {
      header: {
        title: "Category",
        subtitle: "Manage your Categories",
      },
      stats: {
        totalCategories: "Total Categories",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Error",
      },
      accessDenied: {
        title: "Access Denied",
        message: "You do not have permission to view this page.",
      },
    },

    table: {
      gstRate: "GST Rate",
      columns: {
        select: "Select",
        name: "Category Name",
        actions: "Actions",
      },
      aria: {
        selectAll: "Select all categories",
        selectCategory: "Select category",
        viewCategory: "View category",
        editCategory: "Edit category",
        deleteCategory: "Delete category",
      },
      copy: {
        unnamedCategory: "Unnamed Category",
        noGstRate: "N/A",
      },
      emptyState: {
        title: "No categories found",
        message: "Get started by creating your first Category.",
      },
    },

    form: {
      name: "Category Name",
      tax: "Tax Rate",
      selectTax: "Select tax rate",
      namePlaceholder: "Enter category name",
      addTitle: "Add Category",
      editTitle: "Edit Category",
      addDescription: "Create a new Category with tax information",
      editDescription: "Update Category information",
    },

    validation: {
      nameRequired: "Category name is required",
      nameMinLength: "Category name must be at least 2 characters",
      nameMaxLength: "Category name cannot exceed 100 characters",
      taxRequired: "Tax selection is required",
    },

    actions: {
      add: "Add Category",
      buttons: {
        add: "Add Category",
        import: "Import",
        export: "Export",
        refresh: "Refresh",
        deleteSelected: "Delete Selected",
      },
      alerts: {
        noDataToExport: "No categories to export",
      },
      searchPlaceholder: "Search categories...",
      downloadPrefix: "product_categories",
    },

    search: {
      placeholder: "Search categories...",
    },

    import: {
      title: "Import Categories",
      description:
        "Upload an Excel file to import multiple Categories at once.",
      nameDescription: "Name of the Category",
      taxPercentage: "Tax Percentage",
      taxPercentageDescription: "Tax percentage (e.g., 18% for 18 percent)",
      instruction1: "1. Download the template file",
      instruction2: "2. Fill in category names and tax percentages",
      instruction3: "3. Save the file and upload it here",
      instruction4: "4. Tax percentages must match existing tax records",
      instruction5: "5. Each category name must be unique",
    },

    confirmDialog: {
      deleteTitle: "Delete Category",
      deleteMessage:
        'Are you sure you want to delete "{name}"? This action cannot be undone.',
      bulkDeleteTitle: "Delete Categories",
      bulkDeleteMessage:
        "Are you sure you want to delete {count} categories? This action cannot be undone.",
      confirmLabel: "Delete",
      loadingLabel: "Deleting...",
      errorTitle: "Error",
      buttons: {
        cancel: "Cancel",
      },
    },

    details: {
      title: "Category Details",
      description: "View Category information",
      id: "ID",
      name: "Name",
      tax: "Tax",
      createdAt: "Created At",
      updatedAt: "Updated At",
      createdBy: "Created By",
      updatedBy: "Updated By",
      status: "Status",
    },

    messages: {
      noCategories: "No categories found",
      noSelection: "No categories selected",
      createSuccess: "{name} Category created successfully",
      updateSuccess: "{name} Category updated successfully",
      deleteSuccess: "{name} Category deleted successfully",
      bulkDeleteSuccess: "{name} Categories deleted successfully",
      importSuccess: "Categories imported successfully",
      exportSuccess: "Categories exported successfully",
      templateDownloadSuccess: "Template downloaded successfully",
    },

    errors: {
      fetchFailed: "Failed to load categories",
      createFailed: "Failed to create category",
      updateFailed: "Failed to update category",
      deleteFailed: "Failed to delete category",
      noIdToDelete: "No category id available to delete",
      bulkDeleteFailed: "Failed to delete categories",
      importFailed: "Failed to import categories",
      exportFailed: "Failed to export categories",
      templateDownloadFailed: "Failed to download template",
    },
  },
  purchaseAdvice: {
    statuses: {
      unloading: "Unloading",
    },
    table: {
      columns: {
        poNumber: "PO Number",
        date: "Date",
        adviceNumber: "Advice Number",
        supplierName: "Supplier",
        supervisorName: "Supervisor",
        tallyName: "Tally Name",
        vehicleNumber: "Vehicle Number",
        totalQuantity: "Total Quantity",
        status: "Status",
        statusActions: "Status Actions",
        actions: "Actions",
      },
      tooltips: {
        view: "View Advice",
        print: "Print Advice",
        edit: "Edit Advice",
        delete: "Delete Advice",
        confirm: "Confirm Order",
        assignSupervisor: "Assign Supervisor",
        startUnloading: "Start Unloading",
        completeLoading: "Complete Loading",
        completeUnloading: "Complete Unloading",
        generateChallan: "Generate Challan",
        markReceived: "Mark Received",
        complete: "Mark Complete",
        adviceReceived: "Advice received",
      },
      empty: {
        title: "No Purchase Advice Found",
        message: "There are no purchase advice records to display.",
      },
    },
    actions: {
      searchPlaceholder: "Search purchase advice...",
      filters: {
        status: "Status",
        all: "All Status",
      },
    },
    view: {
      header: {
        sectionTitle: "Purchase Advice Details",
        fallbackTitle: "Purchase Advice",
      },
      loading: "Loading Purchase Advice details...",
      empty: {
        title: "Purchase Advice data unavailable",
        message:
          "We couldn't load the selected Purchase Advice. Please try again.",
      },
      fields: {
        poNumber: "PO Number",
        serialNumber: "Serial Number",
        adviceNumber: "Advice Number",
        date: "Date",
        adviceDate: "Advice Date",
        supplierName: "Supplier Name",
        tallyName: "Tally Name",
        paymentTerms: "Payment Terms",
        supplierInvoiceNumber: "Supplier Invoice No",
        supplierInvoiceDate: "Supplier Invoice Date",
        totalQuantity: "Total Quantity",
        pendingQuantity: "Pending Quantity",
        presentShipping: "Present Shipping",
        totalAmount: "Total Amount",
        vehicleNumber: "Vehicle Number",
        status: "Status",
        remark: "Remark",
        items: "Items",
      },
      items: {
        product: "Product",
        size: "Size",
        unit: "Unit",
        length: "Length",
        quantity: "Qty",
        actualQuantity: "Actual Qty",
        actualPieces: "Actual Pieces",
        poItemQuantity: "PO Item Qty",
        standardLength: "Std Length",
        standardWeight: "Std Weight",
        price: "Price",
        amount: "Amount",
        remark: "Remark",
        hsn: "HSN",
        gst: "GST %",
      },
      buttons: {
        close: "Close",
      },
    },
    edit: {
      header: {
        title: "Edit Purchase Advice",
      },
      loading: "Loading advice details...",
      error: "Failed to load advice details",
      fields: {
        adviceDate: "Advice Date",
        paymentTerms: "Payment Terms",
        supplierInvoiceNumber: "Supplier Invoice Number",
        supplierInvoiceDate: "Supplier Invoice Date",
        presentShipping: "Present Shipping",
        remark: "Remark",
        items: "Items",
        product: "Product",
        size: "Size",
        quantity: "Quantity",
        price: "Price",
      },
      buttons: {
        cancel: "Cancel",
        save: "Save Changes",
      },
    },
  },
  purchaseChallan: {
    table: {
      columns: {
        poNumber: "PO Number",
        challanNumber: "Challan Number",
        tallyName: "Tally Name",
        supplierInvoiceNumber: "Supplier Invoice No",
        supplierInvoiceDate: "Supplier Invoice Date",
        totalAmount: "Total Amount",
        status: "Status",
        actions: "Actions",
      },
      tooltips: {
        view: "View Challan",
        print: "Print Challan",
        edit: "Edit Challan",
      },
      empty: {
        title: "No Purchase Challans Found",
        message: "There are no purchase challan records to display.",
      },
    },
    actions: {
      searchPlaceholder: "Search purchase challans...",
      filters: {
        all: "All Status",
        status: "Status",
      },
    },
    statuses: {
      pending: "Pending",
      ready_for_challan: "Ready for Challan",
      confirmed: "Confirmed",
      advice_created: "Advice Created",
      loading: "Loading",
      loaded: "Loaded",
      in_transit: "In Transit",
      received: "Received",
      completed: "Completed",
      cancelled: "Cancelled",
      rejected: "Rejected",
      challan_generated: "Challan Generated",
    },
    view: {
      header: {
        sectionTitle: "Purchase Challan Details",
        fallbackTitle: "Purchase Challan",
      },
      loading: "Loading Purchase Challan details...",
      empty: {
        title: "Purchase Challan data unavailable",
        message:
          "We couldn't load the selected Purchase Challan. Please try again.",
      },
      fields: {
        poNumber: "PO Number",
        serialNumber: "Serial Number",
        challanNumber: "Challan Number",
        date: "Date",
        challanDate: "Challan Date",
        supplierName: "Supplier Name",
        tallyName: "Tally Name",
        discountType: "Discount Type",
        discountValue: "Discount Value",
        discountAmount: "Discount Amount",
        emptyTruckWeight: "Empty Truck Weight",
        finalTruckWeight: "Final Truck Weight",
        truckWeight: "Truck Weight",
        loadingCost: "Loading Cost",
        freight: "Freight",
        subtotal: "Subtotal",
        totalAmount: "Total Amount",
        actualQuantity: "Actual Quantity",
        vehicleNumber: "Vehicle Number",
        unloadedByName: "Unloaded By",
        remark: "Remark",

        supplierInvoiceNumber: "Supplier Invoice No",
        supplierInvoiceDate: "Supplier Invoice Date",
        status: "Status",
        items: "Items",
        item_product: "Product",
        item_size: "Size",
        item_unit: "Unit",
        item_length: "Length",
        item_qty: "Qty",
        item_price: "Price",
        item_amount: "Amount",
        item_remark: "Remark",
        item_hsn: "HSN",
        item_gst_percentage: "GST %",
        item_gst_amount: "GST Amount",
        item_actual_qty: "Actual Qty",
        item_invoice_qty: "Invoice Qty",
        item_avg_weight: "Avg Weight",
        item_avg_qty: "Avg Qty",
      },
      messages: {
        no_items_available: "No items available",
      },
      buttons: {
        close: "Close",
      },
    },
    edit: {
      title: "Edit Purchase Advice Challan",
      notifications: {
        success: "Purchase Challan updated successfully",
      },
      errors: {
        fetchFailed: "Failed to load challan details",
        updateFailed: "Failed to update purchase challan",
      },
      fields: {
        emptyTruckWeight: {
          label: "Empty Truck Weight",
          placeholder: "Enter empty truck weight",
        },
        finalTruckWeight: {
          label: "Final Truck Weight",
          placeholder: "Enter final truck weight",
        },
        loadingCost: {
          label: "Loading Cost",
          placeholder: "Enter loading cost",
        },
        freight: {
          label: "Freight",
          placeholder: "Enter freight",
        },
        vehicleNumber: {
          label: "Vehicle Number",
          placeholder: "Enter vehicle number",
        },
        remark: {
          label: "Remark",
          placeholder: "Enter remark",
        },
      },
      buttons: {
        cancel: "Cancel",
        save: "Save Changes",
        saving: "Saving...",
      },
    },
  },
  purchaseDaybook: {
    page: {
      header: {
        title: "Purchase Daybook",
        subtitle: "View and manage daily purchase entries",
      },
    },
    actions: {
      searchPlaceholder: "Search daybook...",
      fromPlaceholder: "From Date",
      toPlaceholder: "To Date",
      filters: {
        all: "All Statuses",
        status: "Status",
      },
    },
    table: {
      headers: {
        serialNumber: "Sr. No.",
        date: "Date",
        poNumber: "PO Number",
        tallyName: "Tally Name",
        vehicleNumber: "Vehicle No",
        deliveryLocation: "Delivery Location",
        orderFor: "Order For",
        unloadedBy: "Unloaded By",
        labourCount: "Labour Count",
        actualQuantity: "Actual Qty",
        totalAmount: "Total Amount",
        paymentTerms: "Payment Terms",
        remark: "Remark",
        status: "Status",
      },
      empty: {
        title: "No entries found",
        message: "Try adjusting your search or filters",
      },
      supplierInvoiceNumber: "Supplier Invoice Number",
      supplierInvoiceDate: "Supplier Invoice Date",
      presentShipping: "Present Shipping",
      remark: "Remark",
      items: "Items",
      product: "Product",
      size: "Size",
      quantity: "Quantity",
      price: "Price",
    },
    buttons: {
      cancel: "Cancel",
      save: "Save Changes",
    },
  },
  invoicePurchase: {
    page: {
      header: {
        title: "Purchase Estimate",
        subtitle: "View and manage daily purchase invoices",
      },
    },
    actions: {
      searchPlaceholder: "Search entries...",
      fromPlaceholder: "From Date",
      toPlaceholder: "To Date",
      filters: {
        all: "All Statuses",
        status: "Status",
      },
    },
    table: {
      headers: {
        serialNumber: "Sr. No.",
        date: "Date",
        poNumber: "PO Number",
        tallyName: "Tally Name",
        vehicleNumber: "Vehicle No",
        deliveryLocation: "Delivery Location",
        orderFor: "Order For",
        unloadedBy: "Unloaded By",
        labourCount: "Labour Count",
        actualQuantity: "Actual Qty",
        totalAmount: "Total Amount",
        paymentTerms: "Payment Terms",
        remark: "Remark",
        status: "Status",
      },
      empty: {
        title: "No entries found",
        message: "Try adjusting your search or filters",
      },
      supplierInvoiceNumber: "Supplier Invoice Number",
      supplierInvoiceDate: "Supplier Invoice Date",
      presentShipping: "Present Shipping",
      remark: "Remark",
      items: "Items",
      product: "Product",
      size: "Size",
      quantity: "Quantity",
      price: "Price",
    },
    buttons: {
      cancel: "Cancel",
      save: "Save Changes",
    },
  },
};

// Alias orders for cleaner component usage and backward compatibility
STRINGS.orders = {
  forms: {
    ...STRINGS.common.forms,
    title: {
      add: "Add Order",
      edit: "Edit Order",
    },
    loading: {
      customers: "Loading customers...",
      locations: "Loading locations...",
    },
    labels: {
      customerMode: "Customer Mode",
    },
    errors: {
      customerRequired: "Customer is required",
      locationRequired: "Delivery location is required",
      sourceTypeRequired: "Source type is required",
      warehouseRequired: "Warehouse is required",
      supplierRequired: "Supplier is required",
      itemsRequired: "At least one item required",
      itemProductSizeRequired: "Product required",
      itemQuantityRequired: "Quantity required",
      itemPiecesRequired: "Pieces required (enter how many)",
      itemPriceRequired: "Price required",
      itemPriceInvalid: "Enter a valid price (0 or more)",
      itemUnitRequired: "Unit is required",
      itemLengthRequired: "Length is required",
      itemLengthInvalid: "Length must be a valid number",
      freightInvalid: "Freight must be a valid number",
      discountTypeInvalid: "Input should be 'fixed' or 'percentage'",
      discountValueInvalid: "Discount value must be a valid number",
      loadingCostInvalid: "Loading cost must be a valid number",
      gstOptionInvalid: "Input should be 'with_gstn', 'percentage' or 'none'",
      gstnNumberInvalid: "GSTN number must be valid",
      gstPercentageInvalid: "GST percentage must be a valid number",
      tcsPercentageInvalid: "TCS percentage must be a valid number",
    },
    customerModes: {
      existing: "Existing Customer",
      new: "New Customer",
    },
    fields: {
      loadingCost: "Loading Cost",
      loadingCostNote: "Additional cost for loading",
      sourceType: {
        label: "Source Type",
        options: {
          warehouse: "Warehouse",
          supplier: "Supplier",
        },
      },
      gstApplicable: "GST Applicable",

      unit: "Unit",
      length: "Length",
      itemRemark: "Remark",
    },
    placeholders: {
      selectSource: "Select Source",
      selectWarehouse: "Select Warehouse",
      selectSupplier: "Select Supplier",
      noSupplier: "No suppliers available",
      selectProductSize: "Select Product",
    },
    headings: {
      customerInformation: "Customer Information",
      orderDetails: "Order Details",
    },
    buttons: {
      addItem: "Add Item",
      back: "Back",
      save: "Save",
      next: "Next",
    },
  },
  view: {
    loading: "Loading details...",
    header: {
      sectionTitle: "Order Details",
      fallbackTitle: "Order",
    },
    empty: {
      title: "No Order Found",
      message: "The requested order could not be found.",
    },
    fields: {
      orderNumber: "Order Number",
      date: "Date",
      customerName: "Customer",
      tallyName: "Tally Name",
      deliveryLocation: "Delivery Location",
      expectedDeliveryDate: "Expected Delivery",
      totalItems: "Total Items",
      subtotal: "Subtotal",
      discountAmount: "Discount",

      totalAmount: "Total Amount",
      freight: "Freight",
      remark: "Remark",
      status: "Status",
      items: "Items",
      item_product: "Product",
      item_size: "Size",
      item_qty: "Qty",
      item_price: "Price",
      item_amount: "Amount",
      item_hsn: "HSN",
    },
    buttons: {
      close: "Close",
    },
  },
  actions: {
    buttons: {
      add: "Add Order",
    },
    searchPlaceholder: "Search orders...",
  },
  statuses: {
    pending: "Pending",
    confirmed: "Confirmed",
    loading: "Loading",
    loaded: "Loaded",
    dispatched: "Dispatched",
    delivered: "Delivered",
    cancelled: "Cancelled",
    invoiced: "Invoiced",
  },
  table: {
    columns: {
      orderNumber: "Order Number",
      date: "Date",
      customer: "Customer",
      tallyName: "Tally Name",
      deliveryLocation: "Delivery Location",
      totalAmount: "Total Amount",
      status: "Status",
      actions: "Actions",
    },
    tooltips: {
      view: "View Order",
      edit: "Edit Order",
      delete: "Delete Order",
      assignPersonnel: "Assign Delivery Personnel",
      startTruckLoading: "Start Truck Loading",
      completeTruckLoading: "Complete Truck Loading",
    },
  },
};

STRINGS.inquiries = {
  table: {
    statuses: {
      pending: "Pending",
      approved: "Approved",
      confirmed: "Confirmed",
      rejected: "Rejected",
      cancelled: "Cancelled",
      completed: "Completed",
      converted: "Converted",
    },
    columns: {
      select: "Select",
      customerName: "Customer",
      gst: "GST",
      total: "Total",
      status: "Status",
      deliveryDate: "Delivery Date",
      customerType: "Customer Type",
      priceUpdatedBy: "Price Updated By",
      actions: "Actions",
    },
    customerTypes: {
      new: "New",
      existing: "Existing",
    },
    tooltips: {
      view: "View Inquiry",
      approve: "Approve Inquiry",
      reject: "Reject Inquiry",
      edit: "Edit Inquiry",
      delete: "Delete Inquiry",
      history: "Price change history",
    },
    aria: {
      selectAll: "Select all inquiries",
      selectInquiry: "Select inquiry for",
      viewInquiry: "View inquiry",
      editInquiry: "Edit inquiry",
      deleteInquiry: "Delete inquiry",
    },
    copy: {
      noCustomer: "Unknown Customer",
      noQuantity: "0",
      noPrice: "₹0.00",
      yes: "Yes",
      no: "No",
      newCustomerLabel: "Customer: New",
      existingCustomerLabel: "Customer: Existing",
      statuses: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        cancelled: "Cancelled",
        completed: "Completed",
        converted: "Converted",
      },
    },
    emptyState: {
      title: "No Inquiries Found",
      message: "There are no inquiries to display at the moment.",
    },
  },
  view: {
    header: {
      sectionTitle: "Inquiry Details",
      fallbackTitle: "Inquiry",
    },
    fields: {
      customerName: "Customer Name",
      customerCompany: "Company",
      deliveryLocation: "Delivery Location",
      productName: "Product Name",
      productAlias: "Product Alias",
      category: "Category",
      hsnCode: "HSN Code",
      quantity: "Quantity",
      units: "Units",
      price: "Price",
      freight: "Freight",
      subtotal: "Subtotal",
      gstAmount: "GST Amount",
      totalWithoutFreight: "Total (without freight)",
      total: "Total",
      expectedDeliveryDate: "Expected Delivery",
      status: "Status",
      remark: "Remark",
      isNewCustomer: "Customer Type",
      createdBy: "Created By",
      items: "Items",
    },
    empty: {
      title: "Inquiry data unavailable",
      message: "We couldn't load the selected inquiry. Please try again.",
    },
    loading: "Loading inquiry details...",
    copy: {
      noData: "N/A",
      noPrice: "₹0.00",
    },
  },
  actions: {
    buttons: {
      refresh: "Refresh",
      add: "Add Inquiry",
    },
    searchPlaceholder: "Search inquiries...",
  },
};

STRINGS.aiAdvisor = {
  chat: {
    title: "AI Chat",
      subtitle: "Ask about your recent sales and purchase trends",
      newChat: "New chat",
      newChatTitle: "New chat",
      threadsHeader: "Conversations",
      emptyThreads: "No conversations yet. Start a new chat.",
      emptyMessages: "Ask a question to get started.",
      inputPlaceholder: "Ask about your sales/purchase trends…",
      send: "Send",
      generatePlan: "Generate plan",
      thinking: "Analyzing your trends…",
      rename: "Rename",
      delete: "Delete",
      deleteConfirm: "Delete this conversation? This cannot be undone.",
      windowWeeks: "Last N weeks",
      subCategory: "Sub Categories",
      subCategoryAll: "All sub categories",
      subCategoryPlaceholder: "All sub categories",
      privacyNote:
        "This assistant only analyses your anonymised sales and purchase trends. It never sees customer, supplier, price, or personal data.",
      basedOn: "Based on recent sales/purchase trends",
      noData:
        "There's no challaned activity in this period to analyse. Try a wider date range.",
    },
    plan: {
      title: "Purchase Plan",
      subtitle: "An AI buying plan from your recent sales and purchase trends",
      generate: "Generate purchase plan",
      generating: "Building your purchase plan…",
      windowWeeks: "Window (weeks)",
      subCategory: "Sub Categories",
      subCategoryPlaceholder: "All sub categories",
      asOf: "As of",
      productsAnalysed: "Products analysed",
      empty: "Generate a plan to see buying suggestions for the coming weeks.",
      advisoryNote:
        "Decision-support, not autopilot. Reflects only the last N weeks of challaned data.",
      noData:
        "There's no challaned activity in this period to analyse. Try a wider window.",
    },
    errors: {
      notEnabled: "The AI advisor is not enabled for this environment.",
      forbidden: "You don't have access to the AI advisor.",
      serviceBusy: "The AI service is busy right now. Please try again shortly.",
      generic: "Something went wrong. Please try again.",
    },
};

STRINGS.channelSettings = {
    pageTitle: "Settings",
    pageSubtitle: "Configuration that applies across the whole system. Super Admin only.",
    title: "Channel Settings",
    subtitle: "Control which notification channels are active. Changes take effect within ~60 seconds across all servers.",
    superAdminOnlyBanner: "View only — Super Admin access required to change these settings.",
    channels: {
      email: {
        label: "Email",
        description: "Send order confirmations, quotes, and challan notifications via email",
      },
      whatsapp: {
        label: "WhatsApp",
        description: "Send notifications via WhatsApp to customer/supplier phone numbers",
      },
    },
    status: {
      enabled: "Enabled",
      disabled: "Disabled",
    },
    notifications: {
      updated: "{channel} channel {state}",
      updateFailed: "Failed to update {channel} channel",
    },
};

STRINGS.tallySettings = {
    title: "Tally Settings",
    subtitle: "Configure the account ledger and voucher type used per warehouse when the Tally importer posts sales and purchase challans.",
    superAdminOnlyBanner: "View only — Super Admin access required to change these settings.",
    noWarehousesFound: "No warehouses found.",
    warehouseFallback: "Warehouse {id}",
    warehouseFallbackGeneric: "warehouse",
    notConfiguredBadge: "NOT CONFIGURED",
    fields: {
      salesAccountLedger: { label: "Sales Account Ledger", placeholder: "e.g. Sales-Chakan" },
      salesVoucherType: { label: "Sales Voucher Type", placeholder: "e.g. Sale CHK" },
      purchaseAccountLedger: { label: "Purchase Account Ledger", placeholder: "e.g. Purchase-Chakan" },
      purchaseVoucherType: { label: "Purchase Voucher Type", placeholder: "e.g. Purchase CHK" },
    },
    charCounterSuffix: "Clear to unset",
    ariaUnsavedChange: "Unsaved change",
    notifications: {
      saved: "Tally settings saved for {name}",
      saveFailed: "Failed to save Tally settings",
    },
};

STRINGS.movementAnalytics = {
  labels: {
    purchased: "Purchased",
    sold: "Sold",
    netInflow: "Net Inflow",
    netOutflow: "Net Outflow",
    productsSuffix: "products",
    posSuffix: "POs",
    ordersSuffix: "orders",
    busiestSku: "Busiest SKU · ",
    combinedInOut: "combined in+out",
    netInflowText: "Net inflow",
    netOutflowText: "Net outflow",
    kgIn: "kg in",
    kgOut: "kg out",
    subCategoryFallback: "Sub Category",
    exporting: "Exporting…",
    productFallback: "Product",
  },
  headings: {
    productsSizesClickToSeeOrders: "Products (sizes) — click to see orders",
    productMovement: "Product Movement",
  },
  emptyStates: {
    noProductsMoved: "No products moved for this sub category in the period.",
    noRecordsInPeriod: "No records in this period.",
    loadingProductMovement: "Loading product movement...",
    noProductMovement: "No product movement for the selected filters.",
  },
  columns: {
    size: "Size",
    purchase: "Purchase",
    sales: "Sales",
    net: "Net",
    poNo: "PO No.",
    orderNo: "Order No.",
    product: "Product",
    date: "Date",
    challans: "Challans",
    weight: "Weight",
    purchaseKg: "Purchase (kg)",
    salesKg: "Sales (kg)",
    netKg: "Net (kg)",
    rank: "#",
  },
  sections: {
    salesOrders: "Sales Orders",
    purchaseOrders: "Purchase Orders",
    flowOverview: "Flow Overview",
    topMover: "Top Mover",
    biggestBuySellGap: "Biggest Buy/Sell Gap",
  },
  subtitles: {
    parentOrders: "Parent orders & POs (weight only)",
    productSizeSplit: "Product (size) split — weight only",
    purchaseVsSalesPerProduct: "Purchase vs sales per product — click a row to drill down",
  },
  sortOptions: {
    totalMovement: "Total Movement",
    purchase: "Purchase",
    sales: "Sales",
    biggestGap: "Biggest Gap",
  },
  cards: {
    purchaseInbound: "Purchase (Inbound)",
    salesOutbound: "Sales (Outbound)",
    netInflow: "Net Inflow",
    netOutflow: "Net Outflow",
    boughtMoreByWeight: "Bought more by weight",
    soldMoreByWeight: "Sold more by weight",
    productsMoved: "Products Moved",
    boughtLabel: "bought",
    soldLabel: "sold",
  },
  filters: {
    from: "From",
    to: "To",
    sortBy: "Sort By",
    apply: "Apply",
  },
  buttons: {
    exportCsv: "Export CSV",
  },
};

STRINGS.supervisorAnalytics = {
  tabs: {
    summary: "Summary",
    loadingActivity: "Loading Activity",
    unloadingActivity: "Unloading Activity",
    workload: "Workload",
    leaderboard: "Leaderboard",
  },
  pagination: {
    totalLabels: {
      loadingRecords: "loading records",
      unloadingRecords: "unloading records",
      supervisors: "supervisors",
    },
  },
  leaderboard: {
    title: "Supervisor Leaderboard",
    subtitle: "Ranked by total completed activity",
    supervisorsCountSuffix: "supervisors",
  },
  columns: {
    supervisor: "Supervisor",
    assigned: "Assigned",
    completed: "Completed",
    totalWeight: "Total Weight",
    loadingCompleted: "Loading ✓",
    unloadingCompleted: "Unloading ✓",
  },
  labels: {
    completedSuffix: "completed",
  },
  filters: {
    selectSupervisor: "Select Supervisor",
    allStatuses: "All Statuses",
    loaded: "Loaded",
    inTransit: "In Transit",
    delivered: "Delivered",
    pending: "Pending",
    unloaded: "Unloaded",
    assigned: "Assigned",
    loading: "Loading",
    status: "Status",
    fromDate: "From Date",
    toDate: "To Date",
    apply: "Apply",
    loadingText: "Loading...",
  },
  summaryCards: {
    loadingActivity: "Loading Activity",
    unloadingActivity: "Unloading Activity",
    totalAssigned: "Total Assigned",
    loadingOrdersAssigned: "Loading orders assigned",
    unloadingOrdersAssigned: "Unloading orders assigned",
    completed: "Completed",
    successfullyLoaded: "Successfully loaded",
    successfullyUnloaded: "Successfully unloaded",
    pending: "Pending",
    yetToBeLoaded: "Yet to be loaded",
    yetToBeUnloaded: "Yet to be unloaded",
    weightLoaded: "Weight Loaded",
    weightUnloaded: "Weight Unloaded",
  },
  workloadDetail: {
    statusAssigned: "Assigned",
    statusLoading: "Loading",
    statusLoaded: "Loaded",
    statusPending: "Pending",
    statusInTransit: "In Transit",
    statusDelivered: "Delivered",
    tileAssigned: "Assigned",
    tileLoading: "Loading",
    tileLoaded: "Loaded",
    pendingLoadWt: "Pending Load Wt.",
    alreadyLoaded: "Already Loaded",
    kg: "kg",
    subOrders: "Sub-Orders ({count})",
    noSubOrders: "No sub-orders",
    loading: "Loading…",
  },
};

STRINGS.challanAnalytics = {
  tabs: {
    summary: "Summary",
    products: "Products",
    customers: "Customers",
    dailyTrend: "Daily Trend",
    challanList: "Challan List",
  },
  pagination: {
    totalLabels: {
      products: "products",
      customers: "customers",
      challans: "challans",
    },
  },
  cards: {
    totalChallans: "Total Challans",
    totalWeight: "Total Weight",
    printed: "Printed",
    subs: {
      successfullyDelivered: "Successfully delivered",
      currentlyMoving: "Currently moving",
      loadedNotDispatched: "Loaded, not dispatched",
      cancelledChallans: "Cancelled challans",
      awaitingAction: "Awaiting action",
    },
  },
  labels: {
    uniqueCustomersSuffix: "unique customers",
    notPrintedSuffix: "not printed",
    groupedByPrefix: "Grouped by ",
    productSizeSku: "Product Size (SKU)",
    productCatalogue: "Product Catalogue",
    productsCountSuffix: "products",
    customersCountSuffix: "customers",
    daysCountSuffix: "days",
    product: "Product",
    challans: "Challans",
    qty: "Qty",
    weight: "Weight",
  },
  sections: {
    overview: "Overview",
    byStatus: "By Status",
    topProduct: "Top Product",
  },
  tables: {
    productBreakdown: "Product Breakdown",
    customerBreakdown: "Customer Breakdown",
    dailyTrend: "Daily Trend",
  },
  subtitles: {
    perCustomer: "Challan summary per customer",
    dailyActivity: "Challan activity day by day",
  },
  columns: {
    skuSize: "SKU / Size",
    qtyUnits: "Qty / Units",
    weightT: "Weight (T)",
  },
  emptyStates: {
    loadingProducts: "Loading products...",
    noProductData: "No product data found for the selected filters.",
    loadingCustomers: "Loading customers...",
    noCustomerData: "No customer data found for the selected filters.",
    loadingDaily: "Loading daily data...",
    noDailyData: "No daily data found for the selected filters.",
  },
};

STRINGS.salesAnalytics = deepMerge(STRINGS.salesAnalytics, {
  tabs: {
    summary: "Summary",
    orders: "Orders",
    dailyTrend: "Daily Trend",
    leaderboard: "Leaderboard",
    deliveries: "Deliveries",
  },
  pagination: {
    totalLabels: {
      orders: "orders",
      subOrders: "sub-orders",
    },
  },
  sections: {
    byWarehouse: "By Warehouse",
    byCategory: "By Category",
    salesTrend: "Sales Trend",
    topProducts: "Top Products",
    orderStatusDistribution: "Order Status Distribution",
  },
  subtitles: {
    quantityDistribution: "Quantity distribution",
    byQuantityOrdered: "By quantity ordered this period",
    dailyActivity: "Sales activity day by day",
  },
  cards: {
    totalOrders: "Total Orders",
    totalWeight: "Total Weight",
    totalQuantity: "Total Quantity",
    subs: {
      inSelectedPeriod: "In selected period",
      dispatchedWeight: "Dispatched weight",
      unitsOrdered: "Units ordered",
    },
  },
  filters: {
    allSalesStaff: "All Sales Staff",
  },
  chart: {
    weightTon: "Weight (Ton)",
  },
  emptyStates: {
    loadingDeliveries: "Loading deliveries…",
    noActiveDeliverySubOrders: "No active delivery sub-orders",
  },
});

STRINGS.deliveryAnalytics = deepMerge(STRINGS.deliveryAnalytics, {
  sections: {
    challanOverview: "Challan Overview",
    challanTrend: "Challan Trend",
    deliveryPerformance: "Delivery Performance",
    personnelPerformance: "Personnel Performance",
  },
  subtitles: {
    orderFulfilmentBreakdown: "Order fulfilment breakdown",
    supervisorsActivity: "Delivery supervisors activity this period",
  },
  cards: {
    totalChallans: "Total Challans",
    totalWeight: "Total Weight",
    subs: {
      inSelectedPeriod: "In selected period",
      dispatchedWeight: "Dispatched weight",
    },
  },
  labels: {
    deliveryRate: "Delivery Rate",
    deliveries: "Deliveries",
  },
  chart: {
    weightTon: "Weight (Ton)",
  },
});

STRINGS.inventoryAnalytics = deepMerge(STRINGS.inventoryAnalytics, {
  sections: {
    warehouseSummary: "Warehouse Summary",
    warehouseDetails: "Warehouse Details",
  },
  subtitles: {
    warehouseDetails: "Stock, pending orders, and alerts by warehouse",
  },
  labels: {
    stock: "Stock",
    available: "Available",
    lowStock: "Low Stock",
    outOfStock: "Out of Stock",
    warehousesCountSuffix: "warehouses",
  },
  charts: {
    lowStock: {
      subtitle: "Products below minimum stock level",
    },
    warehouseStock: {
      subtitle: "Stock quantity vs pending orders per warehouse",
    },
  },
});

STRINGS.assignedToChallan = {
  page: {
    title: "Operations",
    subtitle: "Track how long sub-orders sit between supervisor assignment and challan generation — the live backlog plus historical duration stats. Warehouse is selected from the sidebar.",
  },
  tabs: {
    liveBacklog: "Live Backlog",
    durationStats: "Duration Stats",
  },
  pagination: {
    totalLabels: {
      subOrders: "sub-orders",
    },
  },
  cards: {
    completed: "Completed",
    subOrdersChallaned: "Sub-orders challaned",
    average: "Average",
    meanAssignToChallan: "Mean assign → challan",
    median: "Median",
    outlierResistant: "Outlier-resistant",
    fastest: "Fastest",
    shortestWindow: "Shortest window",
    slowest: "Slowest",
    longestWindow: "Longest window",
    totalPending: "Total Pending",
    awaitingChallan: "Assigned, awaiting challan",
    over1Day: "Over 1 Day",
    waiting24h: "Waiting more than 24h",
    over7Days: "Over 7 Days",
    criticallyStuck: "Critically stuck",
  },
  sections: {
    durationOverview: "Duration Overview",
    bySupervisor: "By Supervisor",
    dailyTrend: "Daily Trend",
    backlogOverview: "Backlog Overview",
    agingBuckets: "Aging Buckets",
  },
  subtitles: {
    avgDurationPerSupervisor: "Average duration per supervisor",
    perChallanDate: "Average duration per challan date",
  },
  labels: {
    firstAssignmentToChallan: "(first assignment → challan generated)",
  },
  values: {
    unassigned: "Unassigned",
  },
  emptyStates: {
    loadingDuration: "Loading duration stats...",
    noBreakdown: "No breakdown data for the selected period.",
  },
  buckets: {
    today: "Today (<1d)",
    oneToTwoDays: "1–2 Days",
    twoToSevenDays: "2–7 Days",
    overSevenDays: "Over 7 Days",
  },
  filters: {
    unitType: "Unit Type",
    groupBy: "Group By",
    options: {
      all: "All",
      kg: "Kg",
      meter: "Meter",
      none: "None",
      day: "Day",
      supervisor: "Supervisor",
    },
  },
};

STRINGS.vehicleTracking = {
  columns: {
    orderId: "Order ID",
    subOrderId: "Sub Order ID",
    vehicleNumber: "Vehicle Number",
    supervisorName: "Supervisor Name",
    location: "Location",
    orderDate: "Order Date",
  },
  emptyStates: {
    noData: "No vehicle tracking data found",
    noTracking: "No tracking information available for this vehicle.",
  },
  emptyMessage: "No vehicle tracking data found",
  values: {
    notAssigned: "Not assigned",
  },
  filters: {
    searchPlaceholder: "Search Vehicle Number...",
  },
  modal: {
    title: "Vehicle Tracking Details",
    orderPrefix: "Order #",
    subOrderPrefix: "Sub-Order #",
  },
  sections: {
    vehiclePersonnel: "Vehicle & Personnel",
    loadingWeights: "Loading Weights",
    orderContext: "Order Context",
    customerDelivery: "Customer & Delivery",
    materialWeights: "Material Weights",
    associatedDocuments: "Associated Documents",
  },
  labels: {
    vehicleNumber: "Vehicle Number",
    driverContact: "Driver Contact",
    supervisor: "Supervisor",
    emptyTruckWeight: "Empty Truck Weight",
    finalTruckWeight: "Final Truck Weight",
    vehicleRemark: "Vehicle Remark",
    orderNumber: "Order Number",
    subOrderNumber: "Sub Order Number",
    orderDate: "Order Date",
    orderStatus: "Order Status",
    subOrderStatus: "Sub Order Status",
    totalAmount: "Total Amount",
    customerName: "Customer Name",
    tallyName: "Tally Name",
    deliveryLocation: "Delivery Location",
    totalOrderWeight: "Total Order Weight",
    subOrderWeight: "Sub Order Weight",
    unitType: "Unit Type",
    challanNumber: "Challan Number",
    challanDate: "Challan Date",
    invoiceNumber: "Invoice Number",
    invoiceDate: "Invoice Date",
  },
};

STRINGS.dashboard = deepMerge(STRINGS.dashboard, {
  title: "Dashboard",
  orderStatus: {
    title: "Order Status",
    breakdownTitle: "Order Status Breakdown",
    totalPrefix: "Total: ",
    ordersSuffix: "orders",
  },
  kpi: {
    sections: {
      todayActivity: "Today's Activity",
      thisMonth: "This Month",
      operations: "Operations",
      inventoryAlerts: "Inventory Alerts",
    },
    labels: {
      weightDispatched: "Weight Dispatched",
      ordersPlaced: "Orders Placed",
      delivered: "Delivered",
      orders: "Orders",
      pendingOrders: "Pending Orders",
      inTransit: "In Transit",
      expectedDeliveries: "Expected Deliveries",
      pendingPurchaseOrders: "Pending Purchase Orders",
      lowStockAlerts: "Low Stock Alerts",
      outOfStock: "Out of Stock",
    },
    subs: {
      today: "Today",
      month: "Month",
      awaitingDispatch: "Awaiting dispatch",
      onTheWay: "On the way",
      incomingToday: "Incoming today",
      purchase: "Purchase",
      belowMinimum: "Below minimum",
      itemsUnavailable: "Items unavailable",
    },
    trend: {
      vsLastMonth: "{value}% vs last month",
    },
  },
  charts: {
    salesTrend: {
      title: "Sales Trend",
      noData: "No data available",
      orders: "Orders",
    },
  },
});

STRINGS.customerWarehouses = {
  headings: {
    title: "Customer Warehouses",
    fallback: "Warehouses",
    details: "Warehouse Details",
    updateDetails: "Update Warehouse Details",
    optionalStep: "Optional Step",
    editWarehouse: "Edit Warehouse",
    assign: "Assign Customer Warehouse",
  },
  labels: {
    useCustomerAddress: "Use customer's address",
    needsDetails: "needs a few details",
    useExistingLocation: "Use existing delivery location",
    setAsDefault: "Set as Default Warehouse",
    countLabel: "warehouse{s}",
    stateCity: "State / City",
  },
  buttons: {
    addWarehouse: "Add Warehouse",
    setAsDefault: "Set as Default",
    viewOnMap: "View on Map",
    openInMaps: "Open in Google Maps",
    saveAndAddAnother: "Save & Add Another",
    updateWarehouse: "Update Warehouse",
    saveWarehouse: "Save Warehouse",
  },
  messages: {
    loading: "Loading warehouses...",
    noWarehousesFound: "No warehouses found",
    noWarehousesAssigned: "No warehouses have been assigned to this customer yet.",
    fromLocation: "(from location)",
    selectToAutofill: "— select a location to auto-fill address details",
    enterManually: "— enter location details manually",
    addedToList: "{name} warehouse has been added to list",
    assigned: "{name} warehouse has been assigned successfully",
    updated: "{name} warehouse has been updated successfully",
    deleteConfirm: 'Are you sure you want to delete the warehouse "{name}"? This action cannot be undone.',
  },
  errors: {
    updateFailed: "Failed to update warehouse",
    createFailed: "Failed to create warehouse",
    loadFailed: "Failed to load warehouses",
  },
};

STRINGS.bulkInventoryEdit = {
    dialogs: {
      deleteInventoryTitle: "Delete Inventory Item",
      deleteInventoryMessage: "Are you sure you want to delete {name}? This action cannot be undone.",
      deleteFailedTitle: "Delete Failed",
      deleteFailedMessage: "Failed to delete inventory",
      saveFailedTitle: "Save Failed",
      saveFailedMessage: "Failed to save inventory",
      adjustFailedTitle: "Adjust Failed",
      adjustFailedMessage: "Failed to adjust stock",
      partialUpdateErrorsTitle: "Partial Update Errors",
      updateFailedTitle: "Update Failed",
      updateFailedMessage: "Failed to update warehouse distribution",
      transferFailedTitle: "Transfer Failed",
      transferFailedMessage: "Failed to transfer stock",
      saveErrorsTitle: "Save Errors",
      partialSaveTitle: "Partial Save",
    },
    fallbacks: {
      inventory: "Inventory {id}",
      warehouse: "Warehouse {id}",
      product: "Product {id}",
      subCategory: "Sub Category {id}",
    },
    footer: {
      dirtyCount: "{count} changes across {products} products",
      savingProgress: "Saving {current} of {total}...",
      hiddenHint: "{n} product(s) with edits are hidden by current filter — clear filter to review.",
      footerHint: "Edit stock, min-stock and pending values per warehouse, then click Save All.",
    },
    buttons: {
      bulkEdit: "Bulk Edit",
      exitBulkEdit: "Exit Bulk Edit",
      saveAll: "Save All",
      discard: "Discard",
    },
    discardConfirm: {
      title: "Discard changes?",
      message: "You have unsaved changes. Are you sure you want to exit bulk edit mode?",
    },
    messages: {
      allSuccess: "Saved {count} changes across {products} products.",
      allFailed: "Failed to save any changes.",
      partialSaveBody: "{succeeded} of {total} products saved. Failed:\n\n",
    },
    pagination: {
      pageOfTotal: "Page {current} of {total} — {count} total",
      previous: "Previous",
      next: "Next",
    },
    perWarehouseTable: {
      warehouse: "Warehouse",
      stockQty: "Stock Qty",
      minStock: "Min Stock",
      pendingOrder: "Pending Order",
      lowStock: "LOW STOCK",
      noWarehouses: "No warehouse data available for this product.",
    },
};

STRINGS.common.export = {
  title: "Export",
  fromDate: "From Date",
  toDate: "To Date",
  format: "Format",
  csv: "CSV",
  excel: "Excel",
  exporting: "Exporting...",
  exportButton: "Export",
  cancel: "Cancel",
};

STRINGS.forgotPassword = {
  errors: {
    emailRequired: "Email is required",
    invalidEmail: "Please enter a valid email address",
  },
  messages: {
    resetLinkSent: "Password reset link sent to your email.",
    resetLinkFailed: "Failed to send password reset email.",
  },
  placeholders: {
    email: "Enter your email address",
  },
  buttons: {
    sendResetLink: "Send Reset Link",
  },
};

STRINGS.deliveryChallans.table.tooltips = {
  ...STRINGS.deliveryChallans.table.tooltips,
  printChallan: "Print Challan",
  sendChallanEmail: "Send challan via Email",
  sendChallanWhatsApp: "Send challan via WhatsApp",
  auditLogs: "Audit Logs",
};

STRINGS.deliveryChallans.actions.confirmLabels = {
  yesDelivered: "Yes, Delivered",
};

STRINGS.inquiries.errors = {
  failedToLoad: "Failed to load inquiry",
  failedToApprove: "Failed to approve inquiry",
  approvalErrorTitle: "Approval Error",
  failedToReject: "Failed to reject inquiry",
  rejectionErrorTitle: "Rejection Error",
  deleteFailed: "Delete failed",
  deletionErrorTitle: "Deletion Error",
  failedToConvert: "Failed to convert inquiry",
  conversionErrorTitle: "Conversion Error",
  operationFailed: "Operation failed",
  formErrorTitle: "Form Error",
  successTitle: "Success",
};

STRINGS.deliveryOrders.errors = {
  perPieceWeightRequired: 'Enter a per-piece weight for "{product}" — its stock is kept in kilograms, so without one the challan would not deduct any stock.',
  errorTitle: "Error",
  failedToFetchOrderDetails: "Failed to fetch order details",
  failedToGenerateChallan: "Failed to generate challan",
};

STRINGS.deliveryChallans.errors = {
  errorTitle: "Error",
  exportFailed: "Export failed",
  failedToFetchOrderDetails: "Failed to fetch order details",
  paymentsExceedTotal: "Receipts recorded (₹{{paid}}) now exceed the challan total (₹{{total}}). Adjust them from Payments Tracking.",
};
STRINGS.deliveryChallans.labels = STRINGS.deliveryChallans.labels || {};
STRINGS.deliveryChallans.labels.vehicleNumber = "Vehicle Number";
STRINGS.deliveryChallans.export = STRINGS.deliveryChallans.export || {};
STRINGS.deliveryChallans.export.title = "Export Delivery Challans";

STRINGS.profile.superDelete = {
  successMessage: "Successfully deleted all orders and purchase orders.",
  failureMessage: "Failed to super delete.",
};

STRINGS.users.errors = {
  errorTitle: "Error",
  noUserIdToDelete: "No user id available to delete",
  exportFailed: "Export failed",
  exportErrorTitle: "Export Error",
};

STRINGS.purchaseChallan.errors = STRINGS.purchaseChallan.errors || {};
STRINGS.purchaseChallan.errors.exportFailed = "Export failed";

STRINGS.productCatalogue.errors = STRINGS.productCatalogue.errors || {};
STRINGS.productCatalogue.errors.noProductIdToDelete = "No product id available to delete";

STRINGS.productSizes.errors = STRINGS.productSizes.errors || {};
STRINGS.productSizes.errors.noProductSizeIdToDelete = "No product size id available to delete";

STRINGS.superAdminDelete = {
  warning: "WARNING: This will delete ALL {typeLabel} and their associated sub-entities. This action cannot be undone.",
  step1Label: "Step 1: Super Admin Password",
  passwordPlaceholder: "Enter password to continue",
  confirmDeletionHeading: "Confirm Deletion",
  typeToConfirmPrefix: "Please type ",
  typeToConfirmWord: "DELETE",
  typeToConfirmSuffix: " in the box below to confirm deleting ",
  copyPasteDisabledNote: "(Copy-paste is disabled)",
  confirmPlaceholder: 'Type "DELETE" here',
  titleStep1: "Super Admin Delete (1/2)",
  titleStep2: "Super Admin Delete (2/2)",
  nextButton: "Next",
  confirmDeleteAllButton: "Confirm Delete All",
  cancelButton: "Cancel",
};

STRINGS.auditLogs = {
  hideDetails: "Hide Details",
  showDetails: "Show Details",
  entityLabels: {
    order: "Order",
    purchase_order: "Purchase Order",
    delivery_sub_order: "Delivery Sub-Order",
    purchase_advice: "Purchase Advice",
    inquiry: "Inquiry",
  },
  heading: "Audit Logs",
  tabs: {
    auditLog: "Audit Log",
    timeline: "Timeline",
  },
  failedToLoad: "Failed to load logs: {error}",
  noLogsFound: "No logs found",
  close: "Close",
  entry: "entry",
  entries: "entries",
  yes: "Yes",
  no: "No",
  itemsCount: "{count} item{plural}",
  itemFallback: "Item",
  productFallback: "Product #{id}",
  userFallback: "User #{id}",
  valueLabel: "Value",
  noFieldChanges: "No field changes",
  qtyLabel: "qty",
  sections: {
    added: "Added",
    removed: "Removed",
    modified: "Modified",
  },
};

// Shared text for the downloadable import templates' "Instructions" sheet
// (src/utils/xlsxExport.js) and the per-column notes shown there.
STRINGS.importTemplate = {
  sheetName: "Instructions",
  heading: "How to fill this template",
  intro1: '• Fill your data in the "Sheet1" tab, starting from row 2 (keep row 1 headers as-is).',
  intro2: '• "Required" columns must not be left blank.',
  intro3: '• "Allowed values" columns accept only the listed values — copy them exactly (case-sensitive).',
  columns: {
    column: "Column",
    required: "Required",
    allowedValues: "Allowed values",
    example: "Example",
    notes: "Notes",
  },
  yes: "Yes",
  no: "No",
  notes: {
    // Customers
    customerTypeAllowed: "Exactly one of the allowed values (case-sensitive).",
    phoneTenDigit: "10-digit phone number.",
    gstin: "15-character GSTIN. Optional — leave blank if you do not have it.",
    pinCode: "6-digit PIN code.",
    email: "Valid email address.",
    creditPeriod: "Number of days (integer).",
    // Product sizes / catalogue
    weightKg: "Weight in kg (number).",
    weightKgPerPiece: "Weight in kg (number). Weight of one piece at standard length.",
    standardLengthM: "Standard length in metres (number).",
    wholeNumber: "Whole number (integer).",
    coilTracked: "Leave blank or FALSE for normal stock. TRUE keeps stock as individual coil rolls — only if coil tracking is switched on for your company.",
    matchWarehouse: "Must match the name of an ACTIVE warehouse you are assigned to. Case does not matter.",
    matchProduct: "Must match an existing product's Alias Name. Case does not matter.",
    stockAbsolute: "ORDINARY products only. The closing balance in whole kg, never negative — it REPLACES the current stock, it is not added to it. Leave blank to keep the current balance (a product new to the warehouse starts at 0); type 0 to empty it. Always blank for coil-tracked products.",
    coilBatch: "COIL-TRACKED products only. One row per physical coil; the batch number off its tag. Must be unique per product per warehouse.",
    coilWeight: "COIL-TRACKED products only. This coil's weight in whole kg.",
    coilLength: "COIL-TRACKED products only. This coil's length in metres.",
    coilOptional: "Coil rows only. Optional.",
    coilReceivedDate: "Coil rows only. Optional — defaults to today.",
    minimumStock: "Optional re-order level in whole kg.",
    matchCategory: "Must match an existing Category name. Case does not matter.",
    matchSubCategory: "Must match an existing Sub Category (product catalogue) name. Case does not matter.",
    // Product category
    categoryNameUnique: "Category name. Must be unique.",
    taxPercentage: "Tax percentage (e.g. 18 for 18%). Must match an existing tax record.",
  },
};

// Consolidated user-facing error / status messages surfaced from hooks, services
// and analytics pages (previously hardcoded English strings).
STRINGS.hookErrors = {
  common: {
    unexpected: "An unexpected error occurred",
    dateRangeRequired: "Both from and to dates are required",
  },
  noSelection: {
    users: "No users selected",
    customers: "No customers selected",
    sales: "No sales selected",
    taxes: "No taxes selected for deletion",
    locations: "No locations selected for deletion",
    hsnCodes: "No HSN codes selected for deletion",
    customer: "No customer selected",
    userIds: "No user IDs provided",
  },
  users: {
    fetchList: "Failed to fetch users",
    fetchOne: "Failed to fetch user",
  },
  customers: {
    fetchList: "Failed to load customers",
    fetchOne: "Failed to load customer",
  },
  customerWarehouses: {
    fetchList: "Failed to load warehouses",
    fetchOne: "Failed to load warehouse",
  },
  labours: {
    fetchList: "Failed to fetch labours",
  },
  taxes: {
    fetchList: "Failed to fetch taxes",
    fetchOne: "Failed to fetch tax",
    create: "Failed to create tax",
    update: "Failed to update tax",
    deleteOne: "Failed to delete tax",
    deleteMany: "Failed to delete taxes",
  },
  locations: {
    fetchList: "Failed to fetch locations",
    fetchOne: "Failed to fetch location",
    create: "Failed to create location",
    update: "Failed to update location",
    deleteOne: "Failed to delete location",
    deleteMany: "Failed to delete locations",
    toggleStatus: "Failed to toggle location status",
    export: "Failed to export locations",
  },
  hsnCodes: {
    fetchList: "Failed to fetch HSN codes",
    fetchOne: "Failed to fetch HSN code",
    create: "Failed to create HSN code",
    update: "Failed to update HSN code",
    deleteOne: "Failed to delete HSN code",
    deleteMany: "Failed to delete HSN codes",
  },
  products: {
    fetchList: "Failed to fetch products",
    fetchOne: "Failed to fetch product",
  },
  orders: {
    fetchList: "Failed to fetch orders",
    fetchDetails: "Failed to fetch order details",
  },
  deliveryOrders: {
    fetchList: "Failed to fetch delivery orders",
  },
  ipAddresses: {
    fetchOne: "Failed to fetch IP address",
  },
  statesCities: {
    fetchStates: "Failed to fetch states",
    fetchCities: "Failed to fetch cities",
  },
  sales: {
    fetchData: "Failed to fetch sales data",
    bulkDelete: "Failed to bulk delete sales",
    export: "Failed to export",
    exportFailed: "EXPORT_FAILED",
    importFailed: "IMPORT_FAILED",
    exportSuccess: "Export completed successfully",
    importSuccess: "Import completed successfully",
    emailSuccess: "Email sent successfully",
    whatsappSuccess: "WhatsApp sent successfully",
  },
  purchaseAdvice: {
    markAllAdvised: "Failed to mark all advised",
  },
  challanAnalytics: {
    fetchList: "Failed to fetch challan list",
    fetchDaily: "Failed to fetch daily data",
    fetchSummary: "Failed to fetch summary",
  },
  movementAnalytics: {
    fetchProducts: "Failed to fetch movement products",
    fetchSummary: "Failed to fetch movement summary",
    fetchProductSplit: "Failed to fetch product split",
    fetchProductOrders: "Failed to fetch product orders",
  },
  salesAnalytics: {
    fetchSummary: "Failed to fetch summary",
    fetchDaily: "Failed to fetch daily data",
    fetchLeaderboard: "Failed to fetch leaderboard",
  },
  supervisorAnalytics: {
    fetchSummary: "Failed to fetch summary",
    fetchLoading: "Failed to fetch loading data",
    fetchUnloading: "Failed to fetch unloading data",
    fetchLeaderboard: "Failed to fetch leaderboard",
  },
  assignedToChallan: {
    fetchPendingBacklog: "Failed to fetch pending backlog",
    fetchDurationStats: "Failed to fetch duration stats",
  },
  analyticsPage: {
    fetchFailed: "Failed to fetch analytics data",
  },
};

// Shared form placeholders / inline labels that were previously hardcoded.
STRINGS.commonForm = {
  referencePartyPlaceholder: "Enter reference party name (Optional)",
  loading: "Loading...",
  selectProduct: "Select product",
  skipAndNext: "Skip & Next",
  internalRemarksOptional: "Internal remarks (optional)",
  shortRemarkOptional: "Short remark / description (optional)",
};

// Service-surfaced success / status messages.
STRINGS.serviceMessages = {
  auth: {
    passwordUpdated: "Password updated successfully",
    passwordResetEmailSent: "Password reset email sent",
    passwordReset: "Password reset successfully",
  },
};

STRINGS.ordersModule = {
    common: {
        symbols: {
            required: '*',
            emDash: '—',
            currency: '₹',
        },
        buttons: {
            refresh: 'Refresh',
            delete: 'Delete',
            cancel: 'Cancel',
            ok: 'OK',
            loading: 'Marking...',
            save: 'Save',
        },
        units: {
            kg: 'kg',
            m: 'm',
        },
        loading: 'Loading...',
        dialogs: {
            delete: {
                confirm: 'Delete',
                cancel: 'Cancel',
            },
        },
        status: {
            error: 'Error',
            success: 'Success',
            processing: 'Processing...',
        },
    },
    orders: {
        page: {
            header: {
                title: 'Orders',
                subtitle: 'Manage sales orders, delivery and invoices',
            },
            stats: {
                total: 'Total Orders',
                selected: 'Selected',
            },
            alerts: {
                errorTitle: 'Error',
            },
        },
        table: {
            columns: {
                select: 'Select',
                orderNumber: 'Order Number',
                serialNumber: 'Serial Number',
                date: 'Date',
                customer: 'Customer',
                tallyName: 'Tally Name',
                deliveryLocation: 'Delivery Location',
                totalWeight: 'Total Weight',
                vehicleNumber: 'Vehicle Number',
                totalAmount: 'Total Amount',
                status: 'Status',
                statusActions: 'Update Status',
                actions: 'Actions',
            },
            emptyState: {
                title: 'No orders found',
                message: 'There are no orders to display right now.',
            },
            empty: {
                title: 'No orders found',
                message: 'There are no orders to display right now.',
            },
            aria: {
                selectAll: 'Select all orders',
                selectOrder: 'Select order',
                viewOrder: 'View Order',
                editOrder: 'Edit Order',
                deleteOrder: 'Delete Order',
            },
            tooltips: {
                view: 'View order',
                edit: 'Edit order',
                delete: 'Delete order',
                assignPersonnel: 'Assign personnel',
                changePersonnel: 'Change personnel',
                startTruckLoading: 'Start truck loading',
                completeTruckLoading: 'Complete truck loading',
                updateDeliveryChallan: 'Update delivery challan',
                printDeliveryChallan: 'Print Delivery Challan',
                printDeliveryOrder: 'Print Delivery Order',
                markDelivered: 'Mark Delivered',
                generateInvoice: 'Generate Invoice',
                printInvoice: 'Print Invoice',
            },
        },

        statuses: {
            pending: 'Pending',
            confirmed: 'Confirmed',
            delivery_assigned: 'Delivery Assigned',
            loading: 'Truck Loading',
            loaded: 'Truck Loaded',
            in_transit: 'In Transit',
            delivered: 'Delivered',
            invoiced: 'Invoiced',
        },
        messages: {
            confirmMarkDelivered: 'Mark this delivery challan as delivered?',
            markDeliveredSuccess: 'Order marked delivered',
            markDeliveredFailed: 'Failed to mark delivered',
            invoiceGenerated: 'Invoice generated',
            invoiceGenerateFailed: 'Failed to generate invoice',
            confirmOrderSuccess: 'Order confirmed successfully',
            confirmOrderFailed: 'Failed to confirm order',
            completeSubOrders: 'Complete the suborders',
        },
        actions: {
            searchPlaceholder: 'Search orders...',
            buttons: {
                add: 'Add Order',
                export: 'Export Orders',
                bulkDelete: 'Delete Selected',
                confirm: 'Confirm',
                sendEmail: 'Send Email',
            },
            challan: {
                title: 'Delivery Challan',
                selectDateLabel: 'Select Challan Date',
                onlyPastNote: 'Only today or past dates are allowed.',
                labels: {
                    expenses: 'Expenses',
                    discount: 'Discount',
                    remark: 'Remark',
                    subOrder: 'Sub Order #',
                    customer: 'Customer',
                    supervisor: 'Supervisor',
                    vehicle: 'Vehicle',
                    itemsCount: 'Items Count',
                    totalQty: 'Total Qty',
                    subtotal: 'Subtotal',
                    freight: 'Freight',
                    loading: 'Loading',
                    gst: 'GST',
                    totalAmount: 'Total Amount',
                    items: 'Items',
                    challanDetails: 'Challan Details',
                },
                placeholders: {
                    expenseRemark: 'Reason for expense...',
                    discountRemark: 'Reason for discount...',
                    amount: '0.00',
                },
                buttons: {
                    cancel: 'Cancel',
                    generate: 'Generate GRN',
                    generating: 'Generating...'
                }
            },
        },

        daily: {
            page: {
                header: {
                    title: 'Daily Orders',
                    subtitle: "View and manage today's order reports",
                },
            },
            alerts: {
                viewTitle: 'Order Details',
            },
            viewMessageTemplate: 'Order {order_number} - Customer: {customer_name}',
        },
        confirm: {
            deleteSingle: {
                title: 'Delete Order',
                message: 'Are you sure you want to delete this order? This action cannot be undone.',
                confirmLabel: 'Delete',
            },
            deleteBulk: {
                title: 'Delete Selected Orders',
                messageMultiple: 'Are you sure you want to delete {count} selected orders? This action cannot be undone.',
                confirmLabel: 'Delete',
            },
            confirmOrder: {
                title: 'Confirm Order',
                message: 'Are you sure you want to confirm this order?',
                confirmLabel: 'Confirm Order',
            },
            cancelOrder: {
                title: 'Cancel Order',
                message: 'Are you sure you want to cancel this order? This action cannot be undone.',
                confirmLabel: 'Cancel Order',
            },
            sendConfirmEmail: {
                title: 'Send Confirmation',
                message: 'Send order confirmation to the customer?',
                confirmLabel: 'Send',
                loading: 'Sending...',
                cancel: 'Cancel',
                success: 'Order confirmation sent successfully',
                error: 'Failed to send order confirmation',
            },
        },
        view: {
            header: {
                sectionTitle: 'Order Details',
                fallbackTitle: 'Order',
            },
            loading: 'Loading order details...',
            empty: {
                title: 'Order data unavailable',
                message: 'We couldn\'t load the selected order. Please try again.',
            },
            fields: {
                orderNumber: 'Order Number',
                challanNumber: 'Challan Number',
                challanDate: 'Challan Date',
                challanGenerated: 'Challan Generated',
                invoiceNumber: 'Invoice Number',
                invoiceDate: 'Invoice Date',
                invoiced: 'Invoiced',
                serialNumber: 'Serial Number',
                date: 'Date',
                expectedDeliveryDate: 'Expected Delivery Date',
                customerName: 'Customer',
                supervisorName: 'Supervisor',
                deliveryBoyName: 'Delivery Boy',
                vehicleNumber: 'Vehicle Number',
                driverContact: 'Driver Contact',
                tallyName: 'Tally Name',
                deliveryLocation: 'Delivery Location',
                sourceType: 'Source Type',
                warehouseName: 'Warehouse',
                supplierName: 'Supplier',
                totalItems: 'Total Items',
                subtotal: 'Subtotal',
                discountType: 'Discount Type',
                discountValue: 'Discount Value',
                discountAmount: 'Discount Amount',

                gstOption: 'GST Option',
                gstIncluded: 'GST Included',
                gstnNumber: 'GSTN Number',
                gstPercentage: 'GST Percentage',
                totalGstAmount: 'Total GST Amount',
                freight: 'Freight',
                freightGstAmount: 'Freight GST Amount',
                loadingCost: 'Loading Cost Per Kg',
                loadingCostGstAmount: 'Loading Cost GST Amount',
                totalLoadingCost: 'Total Loading Cost',
                emptyTruckWeight: 'Empty Truck Weight',
                finalTruckWeight: 'Final Truck Weight',
                truckWeight: 'Truck Weight',
                totalWeight: 'Total Weight',
                presentShipping: 'Present Shipping',
                pendingOrder: 'Pending Order',
                actualQuantity: 'Actual Quantity',
                remark: 'Remark',
                status: 'Status',
                totalAmount: 'Total Amount',
                createdBy: 'Created By',
                edited: 'Edited',
                isNewCustomer: 'New Customer',
                contactPerson: 'Contact Person',
                mobileNumber: 'Mobile Number',
                items: 'Items',
                item_product: 'Product',
                item_size: 'Size',
                item_category: 'Category',
                item_unit: 'Unit',
                item_length: 'Length',
                item_qty: 'Order Quantity',
                item_qty_in_pieces: 'Quantity in Pieces',
                item_price: 'Price',
                item_actual_price: 'Actual Price',
                item_rate: 'Rate',
                item_amount: 'Amount',
                item_amount_incl_gst: 'Amount (Incl. GST)',
                item_hsn: 'HSN',
                item_hsn_description: 'HSN Description',
                item_gst_percent: 'GST %',
                item_gst_amount: 'GST Amount',
                item_order_weight: 'Order Weight',
                item_actual_weight: 'Actual Weight',
                item_per_piece_weight: 'Per Piece Weight',
                item_actual_pieces: 'Actual Pieces',
                item_present_shipping: 'Present Shipping',
                item_pending_order: 'Pending Order',
                item_actual_quantity: 'Actual Quantity',
                item_remark: 'Remark',
                item_product_weight: 'Product Weight',
                item_standard_length: 'Standard Length',
                item_standard_weight: 'Standard Weight',
            },
            noItems: 'No items available',
            buttons: {
                close: 'Close',
            },
        },
        notifications: {
            created: 'Order {id} created successfully',
            updated: 'Order {id} updated successfully',
            deleted: 'Order {id} deleted successfully',
            bulkDeleted: '{count} orders deleted successfully',
            statusUpdated: 'Order {id} status updated successfully',
            approved: 'Order {id} approved successfully',
            rejected: 'Order {id} rejected successfully',
        },
        alerts: {
            fetchFailed: 'Failed to load order',
            deleteFailed: 'Failed to delete order',
            operationFailed: 'Operation failed',
            updateFailed: 'Failed to update order',
        },
        forms: {
            title: {
                add: 'Create Order',
                edit: 'Edit Order',
            },
            labels: {
                customerMode: 'Customer Mode',
            },
            headings: {
                customerInformation: 'Customer Information',
                orderDetails: 'Order Details',
            },
            customerModes: {
                existing: 'Existing Customer',
                new: 'New Customer',
            },
            loading: {
                customers: 'Loading customers...',
                locations: 'Loading locations...',
            },
            placeholders: {
                selectProductSize: 'Select size',
                selectSource: 'Select source',
                selectWarehouse: 'Select warehouse',
                selectSupplier: 'Select supplier',
                noSupplier: 'No supplier is there',
            },
            fields: {
                customerName: { label: 'Customer', placeholder: 'Select customer' },
                deliveryLocation: { label: 'Delivery Location', placeholder: 'Select delivery location' },
                freight: { label: 'Freight', placeholder: 'e.g., 500.00' },
                discountType: { label: 'Discount Type', placeholder: 'e.g., percentage', options: { fixed: { label: 'Fixed' }, percentage: { label: 'Percentage' } } },
                discountValue: { label: 'Discount Value', placeholder: 'e.g., 5.0' },
                expectedDeliveryDate: { label: 'Expected Delivery Date' },
                sourceType: { label: 'Source Type', placeholder: 'Select source', options: { warehouse: { label: 'Warehouse' }, supplier: { label: 'Supplier' } } },
                warehouse: { label: 'Warehouse' },
                loadingCost: { label: 'Loading Cost', placeholder: 'e.g., 200.00' },
                loadingCostNote: 'per kg',
                gstOption: { label: 'GST Option', placeholder: 'e.g., with_gstn', options: { withGstn: { label: 'With GSTN' }, withoutGstn: { label: 'Without GSTN' }, percentage: { label: 'GST Percentage' }, none: { label: 'None' } } },
                gstnNumber: { label: 'GSTN Number', placeholder: '27AAAAA0000A1Z5' },
                gstPercentage: { label: 'GST Percentage', placeholder: 'e.g., 18.0' },
                tcsApplicable: { label: 'TCS Applicable' },
                tcsPercentage: { label: 'TCS Percentage', placeholder: 'e.g., 0.1' },
                smsNotification: { label: 'SMS Notification' },
                remark: { label: 'Remark', placeholder: 'Any notes or remarks' },
                items: {
                    label: 'Items',
                    approxWeight: 'Approx Weight',
                    loadingCost: 'Loading Cost',
                    amount: 'Amount',
                },
                item_qty: { label: 'Quantity', placeholder: 'e.g., 100' },
                item_price: { label: 'Price', placeholder: 'e.g., 500.00' },
                itemRemark: { label: 'Item Remark', placeholder: 'Item notes' },
                unit: { label: 'Units of Quantity', placeholder: 'e.g., PCS' },
                length: { label: 'Length', placeholder: 'e.g., 6.0' },
                pieces: { label: 'Pieces', placeholder: 'e.g., 50' },
                totalAmount: { label: 'Total Amount' },
                gstApplicable: { label: 'GST Included' },
                addItem: 'Add Item',
            },
            buttons: {
                next: 'Next',
                back: 'Back',
                addItem: 'Add Item',
                save: 'Save',
                cancel: 'Cancel',
            },
            messages: {
                noStock: 'No stock available in selected warehouse',
            },
            errors: {
                customerRequired: 'Customer is required',
                locationRequired: 'Delivery location is required',
                sourceTypeRequired: 'Source type is required',
                warehouseRequired: 'Warehouse is required',
                supplierRequired: 'Supplier is required',
                itemsRequired: 'At least one item required',
                itemProductSizeRequired: 'Product required',
                itemQuantityRequired: 'Quantity required',
                itemPiecesRequired: 'Pieces required (enter how many)',
                itemPriceRequired: 'Price required',
                itemPriceInvalid: 'Enter a valid price (0 or more)',
                itemUnitRequired: 'Unit is required',
                itemLengthRequired: 'Length is required',
                itemLengthInvalid: 'Length must be a valid number',
                freightInvalid: 'Freight must be a valid number',
                discountTypeInvalid: "Input should be 'fixed' or 'percentage'",
                discountValueInvalid: 'Discount value must be a valid number',
                loadingCostInvalid: 'Loading cost must be a valid number',
                gstOptionInvalid: "Input should be 'with_gstn', 'percentage' or 'none'",
                gstnNumberInvalid: 'GSTN number must be valid',
                gstPercentageInvalid: 'GST percentage must be a valid number',
                tcsPercentageInvalid: 'TCS percentage must be a valid number',
            },
            assignPersonnel: {
                title: 'Assign Delivery Personnel',
                subtitle: 'Choose delivery supervisor and delivery boy for this order.',
                labels: {
                    supervisor: 'Delivery Supervisor',
                    deliveryBoy: 'Delivery Boy',
                    vehicleNumber: 'Vehicle Number',
                    driverContact: 'Driver Contact',
                    vehicleNumberHint: 'Formats: KA-53-1234, RJ14CA4241, RJ 14 CA 1424, RJ-14-CA-2414'
                },
                buttons: {
                    cancel: 'Cancel',
                    assign: 'Assign',
                    assigning: 'Assigning...'
                },
                messages: {
                    loadingUsers: 'Loading users...',
                    selectAtLeastOne: 'Select at least one person',
                    invalidOrder: 'Invalid order',
                    assignFailed: 'Failed to assign personnel',
                    vehicleRequired: 'Vehicle number is required',
                    invalidVehicleNumber: 'Please enter a valid vehicle number.',
                    driverContactRequired: 'Driver contact is required',
                    invalidDriverContact: 'Please enter a valid number.'
                }
            },
            changePersonnel: {
                title: 'Change Delivery Personnel',
                subtitle: 'Update delivery supervisor and delivery boy for this order.',
                buttons: {
                    cancel: 'Cancel',
                    change: 'Change',
                    changing: 'Changing...'
                }
            },
            startTruck: {
                title: 'Start Truck Loading',
                subtitle: 'Enter truck details and assign labours for loading.',
                labels: {
                    vehicleNumber: 'Vehicle Number',
                    driverContact: 'Driver Contact',
                    emptyTruckWeight: 'Empty Truck Weight (kg)',
                    labours: 'Labours',
                    remark: 'Remark'
                },
                placeholders: {
                    vehicleNumber: 'e.g., KA-53-1234',
                    driverContact: 'e.g., 9876543210',
                    emptyTruckWeight: 'e.g., 5000',
                    remark: 'e.g., Check tire pressure'
                },
                buttons: {
                    cancel: 'Cancel',
                    start: 'Start Loading',
                    starting: 'Starting...'
                },
                messages: {
                    loadingUsers: 'Loading labours...',
                    selectAtLeastOneLabour: 'Select at least one labour',
                    vehicleRequired: 'Vehicle number is required',
                    invalidDriverContact: 'Please enter a valid driver contact number',
                    invalidEmptyTruckWeight: 'Please enter a valid empty truck weight',
                    formErrors: 'Please fix the highlighted fields',
                    invalidOrder: 'Invalid order',
                    startFailed: 'Failed to start truck loading'
                }
            },
            completeTruckLoading: {
                title: 'Complete Truck Loading',
                subtitle: 'Update item quantities and enter final truck weight.',
                labels: {
                    orderId: 'Order ID',
                    truckWeight: 'Final Truck Weight (kg)',
                    productName: 'Product Name',
                    quantity: 'Quantity',
                    actualQuantity: 'Actual Quantity',
                    items: 'Items',
                    emptyTruckWeight: 'Empty Truck Weight (kg)',
                    theoreticalPerUnitWeight: 'Theo. Per Unit (kg)',
                    actualPerUnitWeight: 'Act. Per Unit (kg)',
                    loadingCost: 'Loading Cost',
                    unknownProduct: 'Unknown Product',
                    unitLabel: 'Unit',
                    lengthLabel: 'Length',
                    piecesLabel: 'Pieces',
                    approxWeight: 'Approx Weight',
                    totalLoadingCost: 'Total Loading Cost',
                    approxPerUnitWeight: 'Approx. Per Unit Weight',
                    approxPerPieceWeight: 'Approx Per Piece Weight',
                    actualPerUnitWeightLabel: 'Adjusted Per Unit Weight',
                    actualPieces: 'Actual Pieces',
                    perPieceWeight: 'Per Piece Weight',
                    adjustedQuantity: 'Adjusted Qty',
                    // unitLabel: 'Unit',
                },
                placeholders: {
                    emptyTruckWeight: 'Enter empty truck weight',
                    truckWeight: 'Enter final truck weight'
                },
                buttons: {
                    cancel: 'Cancel',
                    complete: 'Complete Loading',
                    completing: 'Completing...',
                    saveDraft: 'Save Draft',
                    saving: 'Saving...',
                },
                messages: {
                    loadingOrder: 'Loading order details...',
                    invalidOrder: 'Invalid order ID',
                    completeFailed: 'Failed to complete truck loading',
                    noItems: 'No items found in order',
                    invalidWeight: 'Please enter a valid truck weight',
                    invalidQuantity: 'Please enter valid quantities for all items',
                    weightsAdjusted: 'Weights adjusted by factor: {factor}'
                }
            },
            updateDeliveryChallan: {
                title: 'Update Delivery Challan',
                subtitle: 'Update truck weights, costs, and delivery details.',
                labels: {
                    emptyTruckWeight: 'Empty Truck Weight (kg)',
                    finalTruckWeight: 'Final Truck Weight (kg)',
                    loadingCost: 'Loading Cost',
                    freight: 'Freight',
                    vehicleNumber: 'Vehicle Number',
                    remark: 'Remark'
                },
                placeholders: {
                    emptyTruckWeight: 'e.g., 5100.0',
                    finalTruckWeight: 'e.g., 15200.0',
                    loadingCost: 'e.g., 250.0',
                    freight: 'e.g., 550.0',
                    vehicleNumber: 'e.g., KA-53-1234',
                    remark: 'e.g., Delivered on time'
                },
                buttons: {
                    cancel: 'Cancel',
                    update: 'Update Challan',
                    updating: 'Updating...'
                },
                messages: {
                    loadingOrder: 'Loading order details...',
                    invalidOrder: 'Invalid order ID',
                    updateFailed: 'Failed to update delivery challan',
                    updateSuccess: 'Delivery challan updated successfully',
                    emptyTruckWeightRequired: 'Empty truck weight is required',
                    finalTruckWeightRequired: 'Final truck weight is required',
                    vehicleNumberRequired: 'Vehicle number is required',
                    invalidEmptyTruckWeight: 'Please enter a valid empty truck weight',
                    invalidFinalTruckWeight: 'Please enter a valid final truck weight',
                    invalidLoadingCost: 'Please enter a valid loading cost',
                    invalidFreight: 'Please enter a valid freight value'
                }
            },
        },
    },
    locations: {
        defaults: {
            country: 'India',
        },
    },
    customers: {
        forms: {
            fields: {
                ownerName: { label: 'Owner Name', placeholder: 'Enter owner name' },
                companyName: { label: 'Company Name', placeholder: 'Enter company name' },
                gstNumber: { label: 'GST Number', placeholder: 'Enter GST number' },
                tallyName: { label: 'Tally Name', placeholder: 'Enter tally name' },
                contactPerson: { label: 'Contact Person', placeholder: 'Enter contact person' },
                phoneNumber1: { label: 'Phone Number 1', placeholder: 'Enter phone number' },
                sisterCompany: { label: 'Sister Company', placeholder: 'Enter sister company' },
                email: { label: 'Email', placeholder: 'Enter email' },
                address1: { label: 'Address Line 1', placeholder: 'Enter address' },
                city: { label: 'City', placeholder: 'Enter city' },
                state: { label: 'State', placeholder: 'Enter state' },
                country: { label: 'Country', placeholder: 'Enter country' },
                zipCode: { label: 'Zip Code', placeholder: 'Enter zip code' },
                creditPeriod: { label: 'Credit Period', placeholder: 'Enter credit period' },
            },
        },
    },
    productSizes: {
        forms: {
            fields: {
                product_size: { label: 'Product', placeholder: 'Select product size' },
            },
        },
    },
};

STRINGS.inquiriesModule = {
  inquiries: {
    notifications: {
      created: "Inquiry {id} created successfully",
      updated: "Inquiry {id} updated successfully",
      deleted: "Inquiry {id} deleted successfully",
      bulkDeleted: "{count} inquiries deleted successfully",
      statusUpdated: "Inquiry {id} status updated successfully",
      approved: "Inquiry {id} approved successfully",
      rejected: "Inquiry {id} rejected successfully",
      emailSent: "Email sent successfully",
    },
    table: {
      columns: {
        customerName: "Customer",
        customerCompany: "Company",
        productAlias: "Product",
        quantity: "Quantity",
        price: "Price",
        includeGst: "Include GST",
        total: "Total",
        status: "Status",
        expectedDeliveryDate: "Expected Delivery",
        isNewCustomer: "Customer Type",
        actions: "Actions",
        closeApi: "Convert / Close",
      },
      copy: {
        noCustomer: "N/A",
        noCompany: "N/A",
        noProduct: "N/A",
        noQuantity: "0",
        noPrice: "₹0.00",
        noTotal: "₹0.00",
        yes: "Yes",
        no: "No",
        newCustomerLabel: "Customer: New",
        existingCustomerLabel: "Customer: Existing",
        statuses: {
          pending: "Pending",
          approved: "Approved",
          rejected: "Rejected",
          cancelled: "Cancelled",
          completed: "Completed",
          converted: "Converted",
        },
      },
      customerTypes: {
        new: "New",
        existing: "Existing",
      },
      emptyState: {
        title: "No Inquiries Found",
        message: "There are no inquiries to display.",
      },
      aria: {
        selectAll: "Select all inquiries",
        selectInquiry: "Select inquiry",
        viewInquiry: "View Inquiry",
        editInquiry: "Edit Inquiry",
        deleteInquiry: "Delete Inquiry",
      },
      actions: {
        buttons: {
          view: "View",
          edit: "Edit",
          delete: "Delete",
          refresh: "Refresh",
          add: "Add Inquiry",
          convert: "Convert",
          close: "Close",
          quote: "Send Quote",
        },
        searchPlaceholder: "Search inquiries...",
      },
    },
    view: {
      header: {
        sectionTitle: "Inquiry Details",
        fallbackTitle: "Inquiry",
      },
      fields: {
        customerName: "Customer Name",
        customerCompany: "Company",
        deliveryLocation: "Delivery Location",
        productName: "Product Name",
        productAlias: "Product Alias",
        category: "Category",
        hsnCode: "HSN Code",
        quantity: "Quantity",
        units: "Units",
        length: "Length",
        price: "Price",
        freight: "Freight",
        subtotal: "Subtotal",
        gstAmount: "GST Amount",
        totalWithoutFreight: "Total (without freight)",
        total: "Total",
        expectedDeliveryDate: "Expected Delivery",
        status: "Status",
        remark: "Remark",
        isNewCustomer: "Customer Type",
        createdBy: "Created By",
      },
      empty: {
        title: "Inquiry data unavailable",
        message: "We couldn't load the selected inquiry. Please try again.",
      },
      loading: "Loading inquiry details...",
      copy: {
        noData: "N/A",
        noPrice: "₹0.00",
      },
    },
    actions: {
      searchPlaceholder: "Search inquiries...",
      buttons: {
        add: "Add Inquiry",
        refresh: "Refresh",
        approve: "Approve",
        reject: "Reject",
        bulkDelete: "Delete Selected",
      },
      alerts: {
        noSelection: "Select at least one inquiry to continue.",
      },
    },
    dialogs: {
      approve: {
        title: "Approve Inquiry",
        message:
          "Are you sure want to approve it? This inquiry will be converted to a order.",
        confirm: "Okay",
        cancel: "Cancel",
      },
      reject: {
        title: "Reject Inquiry",
        message: "Are you sure want to reject it?",
        confirm: "Reject",
        cancel: "Cancel",
      },
      sendQuote: {
        title: "Send Quote",
        message: "Are you sure you have updated the quantity/prices?",
        confirm: "Send Quote",
        loading: "Sending...",
        cancel: "Cancel",
      },
    },
    page: {
      header: {
        title: "Inquiries",
        subtitle: "Manage customer inquiries and quotes",
      },
      stats: {
        total: "Total Inquiries",
        selected: "Selected",
      },
      alerts: {
        errorTitle: "Inquiries Error",
      },
    },
  },
  inquiriesForm: {
    title: {
      add: "Create Inquiry",
      edit: "Edit Inquiry",
      convert: "Convert to Order",
    },
    steps: {
      customerInfo: "Customer Details",
      orderInfo: "Order Details",
    },
    fields: {
      mode: {
        label: "Customer Type",
        options: {
          new: "New Customer",
          existing: "Existing Customer",
        },
      },
      customer_id: {
        label: "Customer",
        placeholder: "Select customer",
      },
      location_id: {
        label: "Delivery Location",
        placeholder: "Select delivery location",
      },
      freight: { label: "Freight", placeholder: "e.g., 500.00" },
      expected_delivery_date: { label: "Expected Delivery Date" },
      product_size_id: { label: "Product", placeholder: "Select Product" },
      units: { label: "Units", placeholder: "e.g., pieces" },
      length: { label: "Length", placeholder: "e.g., 6.00" },
      quantity: { label: "Quantity", placeholder: "e.g., 10.000" },
      price: {
        label: "Price",
        placeholder: "e.g., 850.50",
        note: "This price is per unit",
      },
      include_gst: { label: "Include GST" },
      gst: { label: "GST (%)", placeholder: "e.g., 18.00" },
      // Approval/Conversion fields
      sourceType: {
        label: "Source Type",
        placeholder: "Select source",
        options: {
          warehouse: { label: "Warehouse" },
          supplier: { label: "Supplier" },
        },
      },
      warehouse_id: { label: "Warehouse", placeholder: "Select warehouse" },
      supplier_id: { label: "Supplier", placeholder: "Select supplier" },
      loading_cost_per_kg: {
        label: "Loading Cost (per kg)",
        placeholder: "e.g., 2.00",
      },

      remark: { label: "Remark", placeholder: "Any notes or remarks" },
      // New customer fields
      customer: {
        owner_name: { label: "Owner Name", placeholder: "New Customer Name" },
        company_name: { label: "Company Name", placeholder: "New Company Ltd" },
        gst_number: { label: "GST Number", placeholder: "27AAAAA0000A1Z5" },
        contact_person: {
          label: "Contact Person",
          placeholder: "Contact Person",
        },
        customer_type: { label: "Customer Type", placeholder: "Customer" },
        address_1: { label: "Address Line 1", placeholder: "123 Street" },
        state: { label: "State", placeholder: "Maharashtra" },
        city: { label: "City", placeholder: "Mumbai" },
        country: { label: "Country", placeholder: "India" },
        zip_code: { label: "ZIP Code", placeholder: "400001" },
        email: { label: "Email", placeholder: "newcustomer@example.com" },
        phone_number_1: { label: "Phone Number", placeholder: "9876543210" },
        tally_name: { label: "Tally Name", placeholder: "New Customer Tally" },
        credit_period: { label: "Credit Period (days)", placeholder: "30" },
      },
    },
    buttons: {
      next: "Next",
      back: "Back",
      submit: "Submit",
      cancel: "Cancel",
      convert: "Convert",
      addItem: "Add Item",
    },
    validation: {
      required: "This field is required",
      invalidNumber: "Please enter a valid number",
      invalidEmail: "Please enter a valid email address",
    },
    loading: {
      submitting: "Submitting...",
      loadingDetails: "Loading inquiry details...",
      converting: "Converting...",
    },
    notifications: {
      converted: "Inquiry converted to order successfully",
    },
    locationModal: {
      title: "Add Location",
      fields: {
        state: { label: "State", placeholder: "Select State" },
        city: { label: "City", placeholder: "Enter City" },
        deliveryLocation: {
          label: "Delivery Location",
          placeholder: "Detailed delivery location",
        },
      },
      buttons: {
        cancel: "Cancel",
        save: "Save Location",
      },
      messages: {
        success: "Location added successfully",
        error: "Failed to add location",
      },
      validation: {
        stateRequired: "State is required",
        cityRequired: "City is required",
        locationRequired: "Delivery location is required",
      },
      addButtonLabel: "Add Location",
    },
  },
};

STRINGS.deliveryOrdersModule = {
    common: {
        symbols: {
            required: '*',
            emDash: '—',
            currency: '₹',
        },
        buttons: {
            close: 'Close',
            download: 'Download',
            sendChallanEmail: 'Send Challan',
            sendingEmail: 'Sending...',
        },
        units: {
            kg: 'kg',
            m: 'm',
        },
    },
    page: {
        header: {
            title: 'Delivery Orders',
            subtitle: 'Manage delivery sub-orders and track status',
        },
        stats: {
            total: 'Total',
        }
    },
    table: {
        columns: {
            subOrderNumber: 'Sub Order Number',
            orderNumber: 'Order Number',
            customer: 'Customer',
            tallyName: 'Tally Name',
            location: 'Location',
            totalQty: 'Total Qty',
            status: 'Status',
            actions: 'Actions',
        },
        actions: {
            view: 'View Details',
        },
        empty: {
            title: 'No delivery orders',
            message: 'No delivery orders found at the moment.',
        }
    },
    actions: {
        searchPlaceholder: 'Search orders...',
        refresh: 'Refresh',
        assignSupervisor: 'Assign Supervisor',
        editSupervisor: 'Edit Supervisor',
        startLoading: 'Start Loading',
        completeLoading: 'Complete Loading',
        generateChallan: 'Generate Challan',
        markInTransit: 'Mark In Transit',
        markDelivered: 'Mark Delivered',
        confirmMarkDelivered: 'Are you sure you want to mark this as delivered?',
        markDeliveredSuccess: 'Order marked delivered successfully',
        markDeliveredFailed: 'Failed to mark delivered',
        orderDelivered: 'Order Delivered',
        editTruckWeight: 'Edit Truck Weight',
        operationFailed: 'Operation failed',
        sendChallanEmail: 'Send Challan',
        sendChallanEmailConfirm: 'Are you sure you want to send the challan to the customer?',
        sendChallanEmailSuccess: 'Challan sent successfully',
        sendChallanEmailFailed: 'Failed to send challan',
        cancelDO: 'Cancel DO',
        cancelDOConfirm: 'Cancel this delivery order? This reverses its inventory and cannot be undone. Other sub-orders of the same order are not affected.',
        cancelDOReasonPlaceholder: 'Reason (optional) — internal remark, not printed',
        cancelDOPaidWarning: "₹{{amount}} has already been received against this challan. Cancelling leaves those receipts unapplied — reverse or re-apply them from Payments Tracking.",
        cancelDOSuccess: 'Delivery order cancelled successfully',
        cancelDOFailed: 'Failed to cancel delivery order',
        cancelDOSuperAdminOnly: 'Only a super admin can cancel after the challan is generated',
        filters: {
            status: 'Status',
            customer: 'Customer',
            all: 'All',
            supervisor: "Supervisor",
            allSupervisors: "All Supervisors",
        },
        status: {
            pending: 'Pending',
            ready_for_challan: 'Ready for Challan',
            supervisor_assigned: 'Supervisor Assigned',
            loading: 'Loading',
            loaded: 'Loaded',
            in_transit: 'In Transit',
            delivered: 'Delivered',
            approved: 'Approved',
            completed: 'Completed',
            rejected: 'Rejected',
            cancelled: 'Cancelled',
            failed: 'Failed',
            delivery_assigned: 'Delivery Assigned',
        }
    },
    forms: {
        assignPersonnel: {
            title: 'Assign Delivery Personnel',
            editTitle: 'Edit Delivery Personnel',
            subtitle: 'Choose delivery supervisor and delivery boy for this order.',
            labels: {
                supervisor: 'Delivery Supervisor',
                deliveryBoy: 'Delivery Boy',
                vehicleNumber: 'Vehicle Number',
                driverContact: 'Driver Contact',
                vehicleNumberHint: 'Formats: KA-53-1234, RJ14CA4241, RJ 14 CA 1424, RJ-14-CA-2414'
            },
            buttons: {
                cancel: 'Cancel',
                assign: 'Assign',
                assigning: 'Assigning...',
                change: 'Change',
                changing: 'Changing...',
            },
            messages: {
                loadingUsers: 'Loading users...',
                selectAtLeastOne: 'Select at least one person',
                invalidOrder: 'Invalid order',
                assignFailed: 'Failed to assign personnel',
                vehicleRequired: 'Vehicle number is required',
                invalidVehicleNumber: 'Please enter a valid vehicle number.',
                driverContactRequired: 'Driver contact is required',
                invalidDriverContact: 'Please enter a valid number.'
            }
        },
        startTruck: {
            title: 'Start Truck Loading',
            subtitle: 'Enter truck details and select labours to start loading.',
            labels: {
                vehicleNumber: 'Vehicle Number',
                driverContact: 'Driver Mobile',
                emptyTruckWeight: 'Empty Truck Weight (KG)',
                labours: 'Select Labours',
                remark: 'Remark'
            },
            placeholders: {
                vehicleNumber: 'e.g., KA-53-1234',
                driverContact: 'e.g., 9876543210',
                emptyTruckWeight: 'Enter weight in KG',
                selectLabour: 'Search and select labours...'
            },
            buttons: {
                cancel: 'Cancel',
                start: 'Start Loading',
                starting: 'Starting...'
            },
            messages: {
                loadingUsers: 'Loading labours...',
                selectAtLeastOneLabour: 'Please select at least one labour',
                invalidOrder: 'Invalid order',
                startFailed: 'Failed to start loading',
                vehicleRequired: 'Vehicle number is required',
                invalidEmptyTruckWeight: 'Please enter a valid empty truck weight',
                invalidDriverContact: 'Please enter a valid 10-digit mobile number',
                noLaboursSelected: 'No labours selected yet'
            }
        },
        challan: {
            title: 'Delivery Challan',
            subtitle: 'Review sub-order details and generate delivery challan.',
            selectDateLabel: 'Challan Date',
            onlyPastNote: 'Only today or past dates are allowed.',
            labels: {
                expenses: 'Expenses',
                expenseGst: 'Expense GST',
                discount: 'Discount',
                discountGst: 'Discount GST',
                remark: 'Remark',
                subOrder: 'Sub Order #',
                customer: 'Customer',
                supervisor: 'Supervisor',
                vehicle: 'Vehicle',
                itemsCount: 'Items Count',
                totalQty: 'Total Qty',
                subtotal: 'Subtotal',
                freight: 'Freight',
                loading: 'Loading',
                gst: 'GST',
                totalAmount: 'Total Amount',
                items: 'Items',
                challanDetails: 'Challan Details',
                requested: 'Requested',
                actualQty: 'Actual Qty',
                weight: 'Weight',
                price: 'Price',
                amount: 'Amount',
            },
            placeholders: {
                expenseRemark: 'Reason for expense...',
                discountRemark: 'Reason for discount...',
                amount: '0.00',
            },
            buttons: {
                cancel: 'Cancel',
                generate: 'Generate GRN',
                generating: 'Generating...'
            },
            messages: {
                fetching: 'Loading sub-order details...',
                fetchError: 'Failed to fetch order details',
            }
        },
        completeTruckLoading: {
            title: 'Complete Truck Loading',
            subtitle: 'Update item quantities and enter final truck weight.',
            labels: {
                orderId: 'Order ID',
                truckWeight: 'Final Truck Weight (kg)',
                productName: 'Product Name',
                quantity: 'Quantity',
                actualQuantity: 'Actual Quantity',
                items: 'Items',
                emptyTruckWeight: 'Empty Truck Weight (kg)',
                theoreticalPerUnitWeight: 'Theo. Per Unit (kg)',
                actualPerUnitWeight: 'Act. Per Unit (kg)',
                actualWeight: 'Actual Weight',
                weightKg: 'Total Weight (kg)',
                lengthM: 'Total Length (m)',
                itemTotalWeight: 'Item Total Weight',
                itemTotalLength: 'Item Total Length',
                combinedTotalWeight: 'Combined Total Weight',
                loadingCost: 'Loading Cost',
                unknownProduct: 'Unknown Product',
                unitLabel: 'Unit',
                lengthLabel: 'Length',
                piecesLabel: 'Pieces',
                approxWeight: 'Approx Weight',
                totalQuantity: 'Total Weight (m)',
                totalLoadingCost: 'Total Loading Cost',
                approxPerUnitWeight: 'Approx. Per Unit Weight',
                approxPerPieceWeight: 'Approx Per Piece Weight',
                actualPerUnitWeightLabel: 'Adjusted Per Unit Weight',
                actualPieces: 'Actual Pieces',
                perPieceWeight: 'Per Piece Weight',
                adjustedQuantity: 'Adjusted Qty',
                remark: 'Remark',
            },
            messages: {
                loadingOrder: 'Fetching order details...',
                invalidOrder: 'Order details not found or invalid.',
                loadingTimeout: 'Connection timed out. Retrying...',
                noItems: 'No items found in this sub-order.',
                submitting: 'Saving loading details...',
                success: 'Truck loading completed successfully.',
                error: 'Failed to complete loading.',
                weightsAdjusted: 'Scale weights adjusted by factor: {factor}',
                dontLoadPartial: '* Don\'t load partial',
                noPartialLoadingError: 'Item must be either fully loaded or completely omitted. Pending quantity must be 0 or the full total value.',
                invalidWeight: 'Please enter a valid weight.',
                invalidQuantity: 'Please enter valid pieces and per-piece weight values.',
                completeFailed: 'Failed to complete truck loading.',
            },
            placeholders: {
                emptyTruckWeight: 'Enter empty truck weight',
                truckWeight: 'Enter final truck weight',
                remark: 'Optional remark...'
            },
            buttons: {
                cancel: 'Cancel',
                complete: 'Complete Loading',
                completing: 'Completing...',
                saveDraft: 'Save Draft',
                saving: 'Saving...',
            },
            multiStep: {
                weightEntriesTitle: 'Weight Entries',
                saveIntermediateWeight: 'Save Intermediate Weight (Optional)',
                saveButton: 'Save',
                resetAll: 'Reset All',
                stepLabel: 'Step',
                truckWeightLabel: 'Truck Weight',
                weightDiffLabel: 'Weight Added',
                itemsCountLabel: 'items',
                savedInStep: 'Saved in Step',
                willBeSaved: 'Will be saved',
                itemsToSave: '{n} item(s) will be linked to this weight entry',
                noItemsToSave: 'Fill actual quantity for items to save them',
                intermediateWeightPlaceholder: 'Enter current truck weight',
                confirmResetTitle: 'Confirm Reset',
                confirmResetMessage: 'Are you sure you want to reset all weight entries? This will unlock all items and clear their values.',
                confirmResetButton: 'Reset',
                cancelButton: 'Cancel',
                entrySavedSuccess: 'Weight entry saved',
                maxEntriesReached: 'Maximum 10 weight entries allowed',
            },
        },
        updateTruckWeight: {
            title: 'Edit Truck Weight',
            subtitle: 'Update empty and final truck weights for this loading.',
            labels: {
                emptyWeight: 'Empty Truck Weight (kg)',
                finalWeight: 'Final Truck Weight (kg)',
            },
            placeholders: {
                emptyWeight: 'Enter empty weight',
                finalWeight: 'Enter final weight',
            },
            buttons: {
                cancel: 'Cancel',
                update: 'Update Weight',
                updating: 'Updating...',
            },
            messages: {
                success: 'Truck weights updated successfully',
                error: 'Failed to update truck weights',
            }
        }
    },
    view: {
        fields: {
            orderNumber: 'Order Number',
            challanNumber: 'Challan Number',
            challanDate: 'Challan Date',
            customerName: 'Customer',
            deliveryLocation: 'Delivery Location',
            supervisorName: 'Supervisor',
            vehicleNumber: 'Vehicle Number',
            driverContact: 'Driver Contact',
            expectedDeliveryDate: 'Expected Delivery Date',
            subtotal: 'Subtotal',
            freight: 'Freight',
            loading: 'Handling Charges',
            gst: 'GST',
            expenses: 'Expenses',
            expenseGst: 'Expense GST',
            discount: 'Discount',
            discountGst: 'Discount GST',
            totalPayable: 'Total Payable',
            totalWeight: 'Total Weight',
            additionalInformation: 'Additional Information',
            hsnCodes: 'HSN Codes',
            subOrderNumber: 'Sub Order #',
            discountAmount: 'Discount Amount',
            totalAmount: 'Total Amount',
            items: 'Items',
            item_product: 'Product',
            item_amount: 'Amount',
            loadingInfo: 'Loading Info',
            metrics: 'Metrics',
            status: 'Status',
            emptyTruckWeight: 'Empty Truck Weight',
            finalTruckWeight: 'Final Truck Weight',
            calculatedTruckWeight: 'Calculated Truck Weight',
            loadingCostPerKg: 'Loading Cost (per kg)',
            remark: 'Remark',
            vehicleRemark: 'Vehicle Remark',
            price: 'Price',
            noItems: 'No items found',
            headerTitle: 'Delivery Details',
            item_qty: 'Order Quantity',
            item_weight: 'Weight',
            item_length: 'Length',
            item_actual_quantity: 'Actual Quantity',
            item_remark: 'Remark',
        }
    }
};

STRINGS.deliveryChallanModule = {
  common: {
    symbols: {
      required: "*",
      emDash: "—",
      currency: "₹",
    },
    buttons: {
      close: "Close",
      download: "Download",
      sendChallanEmail: "Send Challan",
      sendingEmail: "Sending...",
    },
    units: {
      kg: "kg",
      m: "m",
    },
  },
  page: {
    header: {
      title: "Delivery Challan",
      subtitle: "Track in-transit and delivered orders",
    },
    stats: {
      total: "Total",
    },
  },
  table: {
    columns: {
      subOrderNumber: "Sub Order Number",
      challanNumber: "Delivery Challan Number",
      orderNumber: "Order Number",
      customer: "Customer",
      tallyName: "Tally Name",
      location: "Location",
      totalQty: "Total Qty",
      status: "Status",
      actions: "Actions",
    },
    actions: {
      view: "View Details",
    },
    empty: {
      title: "No delivery challans",
      message: "No in-transit or delivered orders found at the moment.",
    },
  },
  actions: {
    searchPlaceholder: "Search challans...",
    refresh: "Refresh",
    markDelivered: "Mark Delivered",
    confirmMarkDelivered: "Are you sure you want to mark this as delivered?",
    markDeliveredSuccess: "Order marked delivered successfully",
    markDeliveredFailed: "Failed to mark delivered",
    orderDelivered: "Order Delivered",
    operationFailed: "Operation failed",
    sendChallanEmail: "Send Challan",
    sendChallanEmailConfirm: "Are you sure you want to send the challan to the customer?",
    sendChallanEmailSuccess: "Challan sent successfully",
    sendChallanEmailFailed: "Failed to send challan",
    editChallan: "Edit Challan",
    editChallanSuccess: "Challan updated successfully",
    editChallanFailed: "Failed to update challan",
    filters: {
      status: "Status",
      all: "All",
    },
    status: {
      in_transit: "In Transit",
      delivered: "Delivered",
    },
  },
  editModal: {
    title: "Edit Delivery Challan",
    subtitle: "Update vehicle and weight details",
    labels: {
      vehicleNumber: "Vehicle Number",
      emptyWeight: "Empty Truck Weight (kg)",
      finalWeight: "Final Truck Weight (kg)",
      loadingCost: "Loading Cost Per KG",
      freight: "Freight",
      freightType: "Freight Type",
      pricingType: "Pricing Type",
      remark: "Remark",
      orderRemark: "Order Remark",
      itemRemark: "Item Remark",
    },
    placeholders: {
      remark: "Add a remark...",
    },
    buttons: {
      save: "Save Changes",
      saving: "Saving...",
      cancel: "Cancel",
    }
  }
};

STRINGS.purchaseOrdersModule = {
    common: {
        symbols: {
            required: '*',
            emDash: '—',
            currency: '₹',
        },
        buttons: {
            refresh: 'Refresh',
            delete: 'Delete',
            cancel: 'Cancel',
            ok: 'OK',
            save: 'Save',
        },
        units: {
            kg: 'kg',
            m: 'm',
        },
        loading: 'Loading...',
        table: {
            empty: {
                title: 'No purchase orders found',
                message: 'There are no purchase orders to display right now.',
            },
        },
    },
    purchaseOrders: {
        page: {
            header: {
                title: 'Purchase Orders',
                subtitle: 'Manage purchase orders, deliveries and invoices',
            },
        },
        table: {
            columns: {
                poNumber: 'PO Number',
                date: 'Date',
                supplier: 'Supplier',
                deliveryLocation: 'Delivery Location',
                totalAmount: 'Total Amount',
                status: 'Status',
                statusActions: 'Status Actions',
                actions: 'Actions',
            },
            tooltips: {
                view: 'View Purchase Order',
                edit: 'Edit Purchase Order',
                delete: 'Delete Purchase Order',
                confirm: 'Confirm Purchase Order',
                cancel: 'Cancel Purchase Order',
                complete: 'Mark Complete',
                printChallan: 'Print Challan',
                generateAdvice: 'Generate Advice',
                assignSupervisor: 'Assign Supervisor',
                startLoading: 'Start Loading',
                completeLoading: 'Complete Loading',
                generateChallan: 'Generate Challan',
                markReceived: 'Mark Received',
                goToAdvice: 'Go to Advice',
                markAllAdvised: 'Mark All Advised',
            },
            messages: {
                notAllReceived: 'All order have not been received yet.',
                purchaseCompleted: 'Purchase Completed',
                supervisorUnloading: 'Supervisor will do it',
                purchaseCancelled: 'Purchase order cancelled',
            },
        },
        statuses: {
            pending: 'Pending',
            ready_for_challan: 'Ready for Challan',
            confirmed: 'Confirmed',
            partial_advice_created: 'Partial Advice Created',
            advice_created: 'Advice Created',
            supervisor_assigned: 'Supervisor Assigned',
            loading: 'Loading',
            loaded: 'Loaded',
            in_progress: 'In Progress',
            in_transit: 'In Transit',
            unloading: 'Unloading',
            unloaded: 'Unloaded',
            received: 'Received',
            challan_generated: 'Challan Generated',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
        actions: {
            searchPlaceholder: 'Search purchase orders...',
            buttons: {
                add: 'Add Purchase Order',
            },
            filters: {
                all: 'All',
                status: 'Status',
                supplier: 'Supplier',
                location: 'Location',
            },
        },
        view: {
            header: {
                sectionTitle: 'Purchase Order Details',
                fallbackTitle: 'Purchase Order',
            },
            loading: 'Loading Purchase Order details...',
            empty: {
                title: 'Purchase Order data unavailable',
                message: 'We couldn\'t load the selected Purchase Order. Please try again.',
            },
            fields: {
                poNumber: 'PO Number',
                date: 'Date',
                supplierName: 'Supplier Name',
                contactPerson: 'Contact Person',
                mobileNumber: 'Mobile Number',
                warehouseName: 'Warehouse',
                deliveryLocation: 'Delivery Location',
                expectedDeliveryDate: 'Expected Delivery Date',
                status: 'Status',
                remark: 'Remark',
                subtotal: 'Subtotal',
                discountType: 'Discount Type',
                discountValue: 'Discount Value',
                discountAmount: 'Discount Amount',
                gstIncluded: 'GST Included',
                pricingOption: 'Pricing Option',

                totalAmount: 'Total Amount',
                freight: 'Freight',
                loadingCost: 'Loading Cost',
                paymentTerms: 'Payment Terms',
                adviceNumber: 'Advice Number',
                adviceDate: 'Advice Date',
                adviceGenerated: 'Advice Generated',
                challanNumber: 'Challan Number',
                challanDate: 'Challan Date',
                challanGenerated: 'Challan Generated',
                supplierInvoiceNumber: 'Supplier Invoice No',
                supplierInvoiceDate: 'Supplier Invoice Date',
                vehicleNumber: 'Vehicle Number',
                emptyTruckWeight: 'Empty Truck Weight',
                finalTruckWeight: 'Final Truck Weight',
                truckWeight: 'Truck Weight',
                presentShipping: 'Present Shipping',
                unloadedByName: 'Unloaded By',
                totalQuantity: 'Total Quantity',
                pendingQuantity: 'Pending Quantity',
                actualQuantity: 'Actual Quantity',
                totalItems: 'Total Items',
                items: 'Items',
                item_product: 'Product',
                item_size: 'Size',
                item_qty: 'Qty',
                item_pieces: 'Pieces',
                item_price: 'Price',
                item_amount: 'Amount',
                item_hsn: 'HSN',
                item_gst_percentage: 'GST %',
                item_remark: 'Remark',
            },
            advices: {
                title: 'Advices',
                labels: {
                    adviceNumber: 'Advice No',
                    date: 'Date',
                    status: 'Status',
                    quantityInAdvice: 'Advice Qty',
                    actualReceived: 'Received',
                    vehicleNumber: 'Vehicle',
                    challanNumber: 'Challan',
                },
                empty: "Advices aren't generated yet.",
            },
            messages: {
                no_items_available: 'No items available',
            },
            buttons: {
                close: 'Close',
            },
        },
        confirm: {
            cancel: {
                title: 'Cancel Purchase Order',
                message: 'Are you sure you want to cancel Purchase Order {poNumber}?',
                confirmLabel: 'Yes, Cancel',
                cancelLabel: 'No, Keep',
            },
            confirm: {
                title: 'Confirm Purchase Order',
                message: 'Are you sure you want to confirm Purchase Order {poNumber}?',
                confirmLabel: 'Yes, Confirm',
                cancelLabel: 'Cancel',
            },
        },
        confirmations: {
            complete: {
                title: 'Mark as Complete',
                message: 'Are you sure you want to mark this purchase order as complete? This action cannot be undone.',
                confirm: 'Yes, Mark Complete',
            },
        },
        forms: {
            title: {
                add: 'Add Purchase Order',
                edit: 'Edit Purchase Order',
            },
            headings: {
                supplierInformation: 'Supplier Information',
                orderDetails: 'Order Details',
            },
            customerModes: {
                existing: 'Existing Supplier',
                new: 'New Supplier',
            },
            labels: {
                customerMode: 'Supplier Mode',
            },
            adviceForm: {
                title: 'Generate Purchase Advice',
                headings: {
                    items: 'Items to Advise',
                },
                fields: {
                    adviceDate: {
                        label: 'Advice Date',
                        placeholder: 'Select advice date',
                    },
                    paymentTerms: {
                        label: 'Payment Terms',
                        placeholder: 'Enter payment terms',
                    },
                    vehicleNumber: {
                        label: 'Vehicle Number',
                        placeholder: 'e.g., KA-53-1234',
                        hint: 'Formats: KA-53-1234, RJ14CA4241, RJ 14 CA 1424, RJ-14-CA-2414',
                    },
                    supplierInvoiceNumber: {
                        label: 'Supplier Invoice Number',
                        placeholder: 'Enter invoice number',
                    },
                    supplierInvoiceDate: {
                        label: 'Supplier Invoice Date',
                        placeholder: 'Select invoice date',
                    },
                    remark: {
                        label: 'Remark',
                        placeholder: 'Add any additional notes here...',
                    },
                },
                buttons: {
                    generate: 'Generate Advice',
                    cancel: 'Cancel',
                },
                table: {
                    columns: {
                        product: 'Product',
                        orderedQty: 'Ordered Qty',
                        pendingQty: 'Pending Qty',
                        adviceQty: 'Advice Qty',
                        remainingQty: 'Remaining Qty',
                    },
                },
                errors: {
                    adviceDateRequired: 'Advice Date is required',
                    vehicleNumberRequired: 'Vehicle Number is required',
                    invalidQuantity: 'Invalid quantity',
                    quantityExceeds: 'Cannot exceed ordered quantity',
                    supplierInvoiceNumberRequired: 'Supplier Invoice Number is required',
                },
            },
            assignSupervisor: {
                title: 'Assign Supervisor',
                subtitle: 'Assign delivery supervisor and driver details',
                labels: {
                    supervisor: 'Delivery Supervisor',
                    driverContact: 'Driver Contact',
                    vehicleRemark: 'Vehicle Remark',
                },
                placeholders: {
                    supervisor: 'Select supervisor',
                    driverContact: 'Enter driver contact',
                    vehicleRemark: 'Enter vehicle remark',
                },
                buttons: {
                    assign: 'Assign Supervisor',
                    assigning: 'Assigning...',
                    cancel: 'Cancel',
                },
                messages: {
                    loadingUsers: 'Loading supervisors...',
                    invalidAdvice: 'Invalid advice ID',
                    selectSupervisor: 'Please select a supervisor',
                    driverContactRequired: 'Driver contact is required',
                    invalidDriverContact: 'Invalid driver contact number',
                    assignFailed: 'Failed to assign supervisor',
                },
            },
            startTruckLoadingForm: {
                title: 'Start Truck Loading',
                fields: {
                    poNumber: 'PO Number',
                    adviceNumber: 'Advice Number',
                    vehicleNumber: {
                        label: 'Vehicle Number',
                        placeholder: 'Enter vehicle number',
                    },
                    emptyWeight: {
                        label: 'Empty Truck Weight (kg)',
                        placeholder: 'Enter weight in kg',
                    },
                    remark: {
                        label: 'Remark',
                        placeholder: 'Enter remark',
                    },
                },
                buttons: {
                    start: 'Start Loading',
                    cancel: 'Cancel',
                },
                errors: {
                    vehicleNumberRequired: 'Vehicle Number is required',
                    emptyWeightRequired: 'Empty Truck Weight is required',
                    invalidWeight: 'Invalid weight',
                },
            },
            completeLoadingForm: {
                title: 'Complete Truck Loading',
                subtitle: 'Review loaded items and finalize truck weight.',
                headings: {
                    items: 'Items Loaded',
                },
                fields: {
                    poNumber: 'PO Number',
                    adviceNumber: 'Advice Number',
                    emptyWeight: {
                        label: 'Empty Truck Weight (kg)',
                    },
                    finalWeight: {
                        label: 'Final Truck Weight (kg)',
                        placeholder: 'Enter weight in kg',
                    },
                },
                buttons: {
                    complete: 'Complete Loading',
                    cancel: 'Cancel',
                },
                errors: {
                    finalWeightRequired: 'Final Truck Weight is required',
                    invalidWeight: 'Invalid weight',
                    invalidQuantity: 'Invalid quantity',
                },
            },
            completeUnloadingForm: {
                title: 'Complete Truck Unloading',
                subtitle: 'Review unloaded items and finalize truck weight.',
                headings: {
                    items: 'Items Unloaded',
                },
                placeholders: {
                    finalWeight: 'Enter weight in kg',
                    truckWeight: 'Calculated truck weight',
                },
                fields: {
                    poNumber: 'PO Number',
                    adviceNumber: 'Advice Number',
                    loadedWeight: 'Loaded Weight',
                    emptyWeight: {
                        label: 'Empty Truck Weight (kg)',
                    },
                    finalWeight: {
                        label: 'Final Truck Weight (kg)',
                        placeholder: 'Enter weight in kg',
                    },
                    unloadedQty: 'Unloaded Qty',
                    ordered: 'Ordered',
                    approx: 'Approx',
                    totalWeight: 'Total Weight',
                },
                buttons: {
                    complete: 'Complete Unloading',
                    cancel: 'Cancel',
                },
                errors: {
                    finalWeightRequired: 'Final Truck Weight is required',
                    invalidWeight: 'Invalid weight',
                    invalidQuantity: 'Invalid quantity',
                },
            },
            startUnloadingForm: {
                title: 'Start Unloading',
                subtitle: 'Enter final truck weight to begin unloading process.',
                fields: {
                    poNumber: 'PO Number',
                    adviceNumber: 'Advice Number',
                    finalWeight: {
                        label: 'Loaded Truck Weight (kg)',
                        placeholder: 'Enter weight in kg',
                    },
                    remark: {
                        label: 'Remark',
                        placeholder: 'Enter remark',
                    },
                },
                buttons: {
                    start: 'Start Unloading',
                    cancel: 'Cancel',
                },
                errors: {
                    finalWeightRequired: 'Final Truck Weight is required',
                    invalidWeight: 'Invalid weight',
                },
            },
            generateChallanForm: {
                title: 'Generate Purchase Challan',
                subtitle: 'Review advice details and enter adjustments',
                fields: {
                    adviceNumber: 'Advice Number',
                    supplier: 'Supplier',
                    poNumber: 'PO Number',
                    challanDate: 'Challan Date',
                    expenses: 'Expenses',
                    expenseRemark: 'Expense Remark',
                    discount: 'Discount',
                    discountRemark: 'Discount Remark',
                    totalQuantity: 'Total Quantity',
                    totalValue: 'Total Value',
                },
                headings: {
                    details: 'Advice Details',
                    items: 'Items',
                    adjustments: 'Financial Adjustments',
                    summary: 'Financial Summary',
                },
                table: {
                    columns: {
                        product: 'Product',
                        size: 'Size',
                        requested: 'Requested',
                        actualQty: 'Actual Qty',
                        poQty: 'PO Item Qty',
                        rate: 'Rate',
                        total: 'Total Price',
                        remark: 'Remark',
                    },
                },
                summaryLabels: {
                    itemsTotal: 'Items Total',
                    gst: 'GST Total',
                    subtotal: 'Subtotal (Items + GST)',
                    expenses: 'Expenses (+)',
                    discount: 'Discount (-)',
                    grandTotal: 'Grand Total',
                },
                buttons: {
                    generate: 'Generate Challan',
                    generating: 'Generating...',
                    cancel: 'Cancel',
                },
                messages: {
                    loading: 'Loading advice details...',
                    fetchFailed: 'Failed to load advice details',
                },
                errors: {
                    dateRequired: 'Challan Date is required',
                },
            },
            markAsReceivedForm: {
                title: 'Mark as Received',
                subtitle: 'Verify received quantities and unloading details.',
                confirmMessage: 'Are you sure you want to mark this purchase advice as received?',
                headings: {
                    items: 'Items Received',
                },
                fields: {
                    unloadedBy: {
                        label: 'Unloaded By',
                        placeholder: 'Select Supervisor',
                    },
                    freight: {
                        label: 'Freight Cost',
                    },
                    loadingCost: {
                        label: 'Unloading Cost',
                    },
                    labours: {
                        label: 'Select Labours',
                    },
                    receivedQuantity: {
                        label: 'Received Quantity',
                    },
                    remark: {
                        label: 'Remark',
                        placeholder: 'Any remarks...',
                    },
                },
                messages: {
                    labourHelp: 'Select a labour from the dropdown to add them to the list.',
                    noLaboursSelected: 'No labours selected',
                    noItemsFound: 'No items found.',
                    orderedPrefix: 'Ordered:',
                    approxWeight: 'Approx Weight:',
                    unloadingCost: 'Unloading Cost:',
                },
                buttons: {
                    markReceived: 'Mark Received',
                    cancel: 'Cancel',
                },
                errors: {
                    unloadedByRequired: 'Unloaded By is required',
                    invalidQuantity: 'Invalid quantity',
                },
            },
            fields: {
                supplier: {
                    label: 'Supplier',
                    placeholder: 'Select supplier',
                },
                shippingDestination: {
                    label: 'Shipping Destination',
                    options: {
                        warehouse: 'Warehouse',
                        customer: 'Customer',
                    },
                },
                customer: {
                    label: 'Customer',
                    placeholder: 'Select customer',
                },
                warehouse: {
                    label: 'Warehouse',
                    placeholder: 'Select warehouse',
                },
                deliveryLocation: {
                    label: 'Delivery Location',
                    placeholder: 'Select delivery location',
                },
                contactPerson: {
                    label: 'Contact Person',
                    placeholder: 'Enter contact person name',
                },
                mobileNumber: {
                    label: 'Mobile Number',
                    placeholder: 'Enter mobile number',
                },
                expectedDeliveryDate: {
                    label: 'Expected Delivery Date',
                    placeholder: 'DD/MM/YYYY',
                },
                paymentTerms: {
                    label: 'Payment Terms',
                    placeholder: 'Enter payment terms',
                },
                discountType: {
                    label: 'Discount Type',
                    options: {
                        percentage: 'Percentage',
                        flat: 'Flat Amount',
                    },
                },
                discountValue: {
                    label: 'Discount Value',
                    placeholder: 'Enter discount',
                },
                gstIncluded: {
                    label: 'GST Included',
                },
                pricingOption: {
                    label: 'Pricing Option',
                    options: {
                        all_inclusive: 'All Inclusive',
                        plus_taxes: 'Plus Taxes',
                    },
                },
                tcsApplicable: {
                    label: 'TCS Applicable',
                },
                tcsPercentage: {
                    label: 'TCS Percentage',
                    placeholder: 'Enter TCS %',
                },
                smsNotification: {
                    label: 'SMS Notification',
                },
                remark: {
                    label: 'Remark',
                    placeholder: 'Enter remark',
                },
                items: {
                    label: 'Items',
                    productSize: {
                        label: 'Product Size',
                        placeholder: 'Select product size',
                    },
                    unit: {
                        label: 'Unit',
                        placeholder: 'Select unit',
                    },
                    length: {
                        label: 'Length',
                        placeholder: 'Enter length',
                    },
                    quantity: {
                        label: 'Quantity',
                        placeholder: 'Enter quantity',
                    },
                    price: {
                        label: 'Price',
                        placeholder: 'Enter price',
                    },
                    remark: {
                        label: 'Remark',
                        placeholder: 'Enter item remark',
                    },
                    approxWeight: 'Approx Weight',
                    pieces: {
                        label: 'Pieces',
                        placeholder: 'Qty',
                    },
                },
            },
            buttons: {
                next: 'Next',
                addItem: 'Add Item',
                submit: {
                    add: 'Create Purchase Order',
                    edit: 'Update Purchase Order',
                },
            },
            loading: {
                suppliers: 'Loading suppliers...',
                warehouses: 'Loading warehouses...',
                locations: 'Loading locations...',
            },
            errors: {
                supplierRequired: 'Supplier is required',
                customerRequired: 'Customer is required',
                warehouseRequired: 'Warehouse is required',
                locationRequired: 'Delivery location is required',
                productSizeRequired: 'Product size is required',
                quantityRequired: 'Quantity is required',
                priceRequired: 'Price is required',
            },
        },
    },
    purchaseChallanPrint: {
        title: 'Print Purchase Advice Challan',
        loading: 'Loading challan details...',
        noData: 'No data available',
        fields: {
            challanDate: 'Challan Date',
            vehicleNumber: 'Vehicle Number',
            driverContact: 'Driver Contact',
            emptyTruckWeight: 'Empty Truck Weight',
            finalTruckWeight: 'Final Truck Weight',
            orderFor: 'Order For',
        },
        items: {
            title: 'Items',
            qty: 'Qty',
            price: 'Price',
            amount: 'Amount',
        },
    },
    locations: {
        defaults: {
            country: 'India',
        },
    },
    customers: {
        forms: {
            fields: {
                ownerName: { label: 'Owner Name', placeholder: 'Enter owner name', errors: { required: 'Owner Name is required' } },
                companyName: { label: 'Company Name', placeholder: 'Enter company name', errors: { required: 'Company Name is required' } },
                tallyName: { label: 'Tally Name', placeholder: 'Enter tally name', errors: { required: 'Tally Name is required' } },
                gstNumber: { label: 'GST Number', placeholder: 'Enter GST number', errors: { required: 'GST Number is required' } },
                contactPerson: { label: 'Contact Person', placeholder: 'Enter contact person', errors: { required: 'Contact Person is required' } },
                phoneNumber1: { label: 'Phone Number 1', placeholder: 'Enter phone number', errors: { invalid: 'Invalid phone number' } },
                email: { label: 'Email', placeholder: 'Enter email', errors: { invalid: 'Invalid email' } },
                sisterCompany: { label: 'Sister Company', placeholder: 'Enter sister company', errors: { required: 'Sister Company is required' } },
                address1: { label: 'Address Line 1', placeholder: 'Enter address', errors: { required: 'Address is required' } },
                city: { label: 'City', placeholder: 'Enter city', errors: { required: 'City is required' } },
                state: { label: 'State', placeholder: 'Enter state', errors: { required: 'State is required' } },
                country: { label: 'Country', placeholder: 'Enter country', errors: { required: 'Country is required' } },
                zipCode: { label: 'Zip Code', placeholder: 'Enter zip code', errors: { required: 'Zip code is required' } },
                creditPeriod: { label: 'Credit Period', placeholder: 'Enter credit period', errors: { required: 'Credit period is required' } },
            },
        },
    },
};

STRINGS.orders = STRINGS.ordersModule.orders;
STRINGS.purchaseOrders = STRINGS.purchaseOrdersModule.purchaseOrders;

// i18n keys added to fill migration gaps
STRINGS.customers = deepMerge(STRINGS.customers || {}, {"notifications":{"bulkDeleted":"{count} customers deleted"}});
STRINGS.hsnCodes = deepMerge(STRINGS.hsnCodes || {}, {"notifications":{"created":"HSN code created successfully","updated":"HSN code updated successfully","deleted":"HSN code deleted successfully","bulkDeleted":"{count} HSN codes deleted"}});
STRINGS.inquiries = deepMerge(STRINGS.inquiries || {}, {"notifications":{"converted":"Inquiry converted to order successfully"}});
STRINGS.inquiriesModule = deepMerge(STRINGS.inquiriesModule || {}, {"inquiries":{"errors":{"noneSelected":"No inquiries selected"},"notifications":{"whatsappSent":"WhatsApp sent successfully"}}});
STRINGS.ipAddresses = deepMerge(STRINGS.ipAddresses || {}, {"notifications":{"added":"IP address added successfully","updated":"IP address updated successfully","removed":"IP address removed successfully","activated":"IP address activated","deactivated":"IP address deactivated","bulkAdded":"{count} IP addresses added"}});
STRINGS.locations = deepMerge(STRINGS.locations || {}, {"notifications":{"created":"Location created successfully","updated":"Location updated successfully","deleted":"Location deleted successfully","statusUpdated":"Location status updated","bulkDeletedByName":"Deleted: {names}"}});
STRINGS.orders = deepMerge(STRINGS.orders || {}, {"notifications":{"draftSaved":"Draft saved"}});
STRINGS.ordersModule = deepMerge(STRINGS.ordersModule || {}, {"orders":{"errors":{"noSelection":"No orders selected"}}});
STRINGS.productCatalogue = deepMerge(STRINGS.productCatalogue || {}, {"notifications":{"bulkDeletedByName":"Deleted: {names}"}});
// `tax_percentage` is not a product_categories column and the importer reads only
// `name`; the modal passes its own templateHeaders anyway, so this override was
// both dead and wrong. Left out rather than corrected.
STRINGS.purchaseAdvice = deepMerge(STRINGS.purchaseAdvice || {}, {"notifications":{"updated":"Purchase advice updated successfully"},"view":{"fields":{"receivedQty":"Received Qty"}}});
STRINGS.purchaseOrders = deepMerge(STRINGS.purchaseOrders || {}, {"forms":{"completeUnloadingForm":{"fields":{"loadedWeight":{"label":"Loaded Weight"}}},"startTruckLoadingForm":{"subtitle":"Enter truck loading details"}}});
STRINGS.taxes = deepMerge(STRINGS.taxes || {}, {"notifications":{"created":"Tax created successfully","updated":"Tax updated successfully","deleted":"Tax deleted successfully","bulkDeletedMultiple":"{count} taxes deleted","bulkDeletedSingle":"Tax deleted"}});
STRINGS.users = deepMerge(STRINGS.users || {}, {"forms":{"passwordReset":{"heading":"Change Password","helper":"Fill both password fields above, then click to reset.","resetting":"Resetting...","submit":"Update Password Now","success":"Password reset successfully","errorLabel":"Failed to reset password","unexpectedError":"An unexpected error occurred"}}});

STRINGS.users = deepMerge(STRINGS.users || {}, {"forms":{"removeWarehouse":"Remove","submitFallbackError":"Failed to save user. Please check the details and try again.","warehouseFallback":"Warehouse {id}"}});

STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"remarks":"Remarks","orderRemarks":"Order Remarks","vehicleNumber":"Vehicle Number","truckNumber":"Truck Number","truckNo":"Truck No.","updateStatus":"Update Status","status":"Status","location":"Location","amount":"Amount","qty":"Qty","totalItems":"Total Items","tallyName":"Tally Name","challanNo":"Challan No","serialNumber":"Serial Number","supervisor":"Supervisor","unloadedBy":"Unloaded By","orderedBy":"Ordered By","labours":"Labours","name":"Name","print":"Print","export":"Export","from":"From","perKg":"Per Kg","pending":"Pending","confirmedOrders":"Confirmed Orders","vehicleTracking":"Vehicle Tracking","aiAdvisor":"AI Advisor"});

STRINGS.navigation = deepMerge(STRINGS.navigation || {}, { pages: {"challan":"Challan","operations":"Operations","movement":"Movement","supervisorStaff":"Supervisor Staff","salesStaff":"Sales Staff","deliveryChallanNav":"Delivery Challan","deliveryChallanPrinted":"Delivery Challan Printed","purchaseChallanPrinted":"Purchase Challan Printed","confirmedOrders":"Confirmed Orders","vehicleTracking":"Vehicle Tracking"} });

STRINGS.navigation = deepMerge(STRINGS.navigation || {}, { pages: {"supervisorAnalytics":"Supervisor Analytics","warehouseInventory":"Warehouse Inventory","challanPrintVariance":"Challan Print Variance","challanAnalytics":"Challan Analytics","movementAnalysis":"Movement Analysis","myProfile":"My Profile"} });

STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"statusOptions":{"all":"All","pending":"Pending","confirmed":"Confirmed","deliveryAssigned":"Delivery Assigned","truckLoading":"Truck Loading","truckLoaded":"Truck Loaded","inTransit":"In Transit","delivered":"Delivered","invoiced":"Invoiced","readyForChallan":"Ready for Challan","supervisorAssigned":"Supervisor Assigned","unloading":"Unloading","unloaded":"Unloaded","active":"Active","inactive":"Inactive","allUsers":"All Users","fixed":"Fixed","edited":"Edited"},"warehouses":"Warehouses","orderRemarks":"Order Remarks","purchaseOrderRemarks":"Purchase Order Remarks","purchaseOrdersRemarks":"Purchase Orders Remarks","noPrintedChallans":"No Printed Challans","purchaseSummary":"Purchase Summary","orderStatus":"Order Status"});

STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"gstNo":"GST No.","address":"Address"});

STRINGS.portal = {"login":{"title":"Customer Portal","subtitle":"Sign in to view your orders and inquiries","backToStaff":"Staff login","identifierLabel":"Email or mobile","identifierPlaceholder":"you@example.com or 9876543210","passwordLabel":"Password","passwordPlaceholder":"Your password","show":"Show","hide":"Hide","submit":"Sign in","submitLoading":"Signing in…","errors":{"identifierRequired":"Enter your email or mobile.","invalid":"Incorrect email/mobile or password.","locked":"Too many failed attempts. Please wait ~15 minutes or contact support.","rateLimited":"Too many attempts. Please wait a minute and try again."}},"nav":{"portalName":"Customer Portal","purchases":"Purchase Orders","sales":"Sales Orders","inquiries":"Inquiries","logout":"Logout"},"common":{"search":"Search","searchPlaceholder":"Search…","status":"Status","allStatuses":"All statuses","from":"From","to":"To","view":"View","actions":"Actions","items":"Items","noItems":"No items","product":"Product","size":"Size","quantity":"Qty","ordered":"Ordered","actual":"Actual","pcs":"pcs","awaitingLoading":"After loading","price":"Price","amount":"Amount","gst":"GST Included","yes":"Yes","no":"No","remark":"Remark","optional":"optional","cancel":"Cancel","saving":"Saving…","remove":"Remove"},"purchases":{"title":"Purchase Orders","detailTitle":"Order","totalLabel":"total orders","columns":{"orderNumber":"Order No.","date":"Date","deliveryLocation":"Delivery Location","total":"Total","status":"Status","expectedDelivery":"Expected Delivery"},"statuses":{"pending":"Pending","confirmed":"Confirmed","delivery_assigned":"Delivery Assigned","loading":"Loading","loaded":"Loaded","in_transit":"In Transit","delivered":"Delivered","invoiced":"Invoiced","cancelled":"Cancelled"},"empty":{"title":"No purchases","message":"Orders you place will appear here."}},"sales":{"title":"Sales Orders","detailTitle":"Sales Order","totalLabel":"total sales orders","columns":{"poNumber":"Order No.","date":"Date","counterparty":"To","total":"Total","status":"Status","expectedDelivery":"Expected Delivery"},"statuses":{"pending":"Pending","confirmed":"Confirmed","partial_advice_created":"Partial Advice Created","advice_created":"Advice Created","loading":"Loading","loaded":"Loaded","in_progress":"In Progress","received":"Received","completed":"Completed","cancelled":"Cancelled"},"empty":{"title":"No sales orders","message":"Orders you supply will appear here."}},"inquiries":{"title":"My Inquiries","detailTitle":"Inquiry","totalLabel":"total inquiries","newInquiry":"New inquiry","pendingQuote":"Pending quotation","columns":{"id":"Inquiry #","created":"Created","items":"Items","expectedDelivery":"Expected Delivery","total":"Total","status":"Status","deliveryLocation":"Delivery Location"},"statuses":{"pending":"Pending","approved":"Approved","rejected":"Rejected","converted":"Converted"},"empty":{"title":"No inquiries","message":"Raise an inquiry to get a quote."},"create":{"title":"New Inquiry","note":"You request items; our team will quote them. No prices needed.","selectProduct":"Select product","productIdPlaceholder":"Product size ID","addItem":"Add item","expectedDelivery":"Expected delivery (optional)","locationId":"Delivery location ID (optional)","submit":"Submit inquiry","errorItems":"Add at least one item with a product and quantity.","productListUnavailable":"Product list is not available for portal accounts yet — enter the product size ID manually."}}};

STRINGS.portal = deepMerge(STRINGS.portal, {"common":{"unit":"Unit"},"inquiries":{"create":{"units":{"kg":"kg","meters":"meters","pieces":"pieces"}}}});

STRINGS.portal = deepMerge(STRINGS.portal, {"nav":{"profile":"Profile"},"common":{"loading":"Loading…"},"inquiries":{"create":{"searchProduct":"Search product…","noProducts":"No products found","searchLocation":"Search location…","noLocations":"No locations found"}},"profile":{"title":"My Profile","details":"Details","savedLocations":"Saved delivery locations","default":"Default","fields":{"company":"Company","owner":"Owner","contact":"Contact person","type":"Type","email":"Email","phone1":"Phone","phone2":"Alternate phone","gst":"GST number","address":"Address","deliveryLocation":"Delivery location"},"password":{"title":"Change password","current":"Current password","new":"New password","confirm":"Confirm new password","min8":"min 8 chars","submit":"Update password","saving":"Updating…","success":"Password updated.","errors":{"required":"Enter your current and new password.","short":"New password must be at least 8 characters.","mismatch":"New passwords do not match.","failed":"Failed to update password."}}}});

STRINGS.portal = deepMerge(STRINGS.portal, { inquiries: { create: {"deliveryLocation":"Delivery location (optional)","selectLocation":"Select location","locationIdPlaceholder":"Delivery location ID","locationListUnavailable":"Location list unavailable — enter the delivery location ID manually."} } });
STRINGS.login = deepMerge(STRINGS.login || {}, { tabs: {"staff":"Staff","customer":"Customer"} });
STRINGS.customers = deepMerge(STRINGS.customers || {}, { portalAccess: {"title":"Portal access","statusEnabled":"Enabled","statusDisabled":"Disabled","passwordSetShort":"Password set","noPassword":"No password","email":"Login email","mobile":"Login mobile","enable":"Enable portal login","updateIdentifiers":"Update email/mobile","disable":"Disable login","setPassword":"Set password","resetPassword":"Reset password","min8":"min 8 chars","save":"Save","enabled":"Portal login enabled.","disabled":"Portal login disabled.","passwordSet":"Password set.","errors":{"identifierRequired":"Enter an email or mobile.","passwordShort":"Password must be at least 8 characters."}} });

// Portal unit labels, keyed by the CANONICAL value the form now posts.
STRINGS.portal = deepMerge(STRINGS.portal || {}, {
  inquiries: { create: { units: { kg: "kg", m: "Meters", pcs: "Pieces" } } },
});

// Three units only — kg / m / pcs — the same set the backend stores and the
// customer portal posts. `meters` used to be offered alongside `m` for the same
// unit, which is how one unit ended up stored under two spellings.
STRINGS.ordersModule = deepMerge(STRINGS.ordersModule || {}, { common: { units: {"pcs":"pcs"}, lengthNote: "Length Note", lengthNotePlaceholder: "e.g. 5 feet" } });
STRINGS.purchaseOrdersModule = deepMerge(STRINGS.purchaseOrdersModule || {}, { common: { units: {"pcs":"pcs"} } });

STRINGS.inquiries = deepMerge(STRINGS.inquiries || {}, {"table":{"columns":{"createdBy":"Created By"},"copy":{"customer":"Customer"}}});
STRINGS.inquiriesModule = deepMerge(STRINGS.inquiriesModule || {}, {"inquiries":{"view":{"fields":{"createdBy":"By"},"copy":{"customer":"Customer"}}}});

STRINGS.portal = deepMerge(STRINGS.portal || {}, {"onboarding":{"forceChange":{"title":"Set your password","subtitle":"For security, please change your temporary password before continuing.","current":"Temporary password","new":"New password","confirm":"Confirm new password","min8":"min 8 chars","submit":"Change password & continue","saving":"Saving…","logout":"Log out","errors":{"required":"Enter your temporary and new password.","short":"New password must be at least 8 characters.","mismatch":"New passwords do not match.","failed":"Could not change the password."}},"profile":{"header":"Complete your profile","logout":"Logout","intro":"Tell us a bit about your business. Our team will review and approve your account.","rejectedBanner":"Your details need changes. Please review and resubmit.","submit":"Submit for approval","submitting":"Submitting…","fields":{"gst":"GST number (15 chars)","contact":"Contact person","phone2":"Alternate phone","deliveryLocation":"Delivery location","address1":"Address line 1","address2":"Address line 2","city":"City","state":"State","zip":"ZIP code","country":"Country"},"errors":{"gstLength":"GST number must be exactly 15 characters.","failed":"Could not submit your details."}},"pending":{"title":"Awaiting approval","message":"Thanks! Your profile has been submitted. Our team is reviewing it and you’ll get full access once approved.","refresh":"Check approval status","checking":"Checking…","logout":"Log out"}}});
STRINGS.customers = deepMerge(STRINGS.customers || {}, {"tabs":{"all":"All customers","approvals":"Approvals"},"invite":{"sectionTitle":"Portal onboarding","title":"Invite customer","note":"The customer gets a temporary password by email/WhatsApp, changes it, and completes their profile for approval.","fields":{"company":"Company name","owner":"Owner name","type":"Type","email":"Login email","mobile":"Login mobile"},"types":{"customer":"Customer","supplier":"Supplier"},"identifierHint":"At least one of email or mobile is required.","submit":"Send invite","sending":"Sending…","cancel":"Cancel","done":"Done","sentVia":"Invite sent via","throttledNote":"Skipped (sent very recently):","noChannel":"No email/WhatsApp channel is enabled, so the customer received nothing. Enable a channel and use Resend from the customer’s Portal access card.","errors":{"identifierRequired":"Enter a login email or mobile.","failed":"Could not send the invite."}},"approvals":{"columns":{"company":"Company","gst":"GST","location":"Location","submitted":"Submitted","actions":"Actions"},"view":"View","approve":"Approve","reject":"Reject","approvedMsg":"Customer approved.","rejectedMsg":"Customer rejected.","rejectTitle":"Reject profile","rejectMessage":"Reject this customer’s submitted profile? They can fix and resubmit.","reasonPlaceholder":"Reason (optional)","totalLabel":"pending customers","empty":{"title":"No pending approvals","message":"Submitted customer profiles will appear here for review."}},"portalAccess":{"resendInvite":"Resend invite","resent":"Invite re-sent","resendThrottled":"Invited very recently — try again shortly. The existing password still works.","resendNoChannel":"No channel is enabled — nothing was sent."}});

STRINGS.portal = deepMerge(STRINGS.portal, { onboarding: { forceChange: {"changedRefreshFailed":"Password changed. Please refresh the page to continue."} } });

STRINGS.customers = deepMerge(STRINGS.customers || {}, {"approvals":{"columns":{"status":"Status"},"badges":{"pendingReview":"Pending review","pendingCustomer":"Pending customer action"},"resend":"Resend","cancel":"Cancel invite","cancelTitle":"Cancel invite","cancelMessage":"Cancel this invite? The customer can be re-invited later.","resentMsg":"Invite re-sent","resendNoChannelMsg":"No channel is enabled — nothing was sent.","resendThrottledMsg":"Invited very recently — try again shortly. The existing password still works.","empty":{"title":"Nothing to review","message":"Invited and submitted customers awaiting action appear here."}}});

STRINGS.printSettings = {
  title: "Print Format",
  subtitle: "Paper size used when a challan is printed. Applies to everyone who prints, not just you.",
  saved: "{{document}} will now print on {{size}}",
  saveFailed: "Could not change the paper size for {{document}}",
  sizes: {
    a5: "A5",
    a4: "A4",
  },
  currentBadge: "Current",
  orientation: {
    portrait: "Portrait",
    landscape: "Landscape",
  },
  documents: {
    delivery_challan: {
      label: "Delivery Challan",
      description: "Sales challan handed over with a delivery.",
    },
    purchase_challan: {
      label: "Purchase Challan",
      description: "Challan raised against a purchase advice.",
    },
  },
};

STRINGS.coilSettings = {
  title: "Coil Tracking",
  subtitle: "Track stock as individual coil rolls for the product sizes that need it. Applies across the whole system.",
  tracking: {
    label: "Per-coil inventory",
    description: "When on, a product size can be marked coil tracked. Its orders are picked coil by coil, and a partly used coil keeps its batch identity. Leave off unless the yard actually records coils.",
  },
  status: {
    enabled: "Enabled",
    disabled: "Disabled",
  },
  notifications: {
    updated: "Coil tracking {{state}}",
    updateFailed: "Could not change the coil tracking setting",
  },
};

STRINGS.orderSettings = {
  title: "Order Confirmation",
  subtitle: "Controls whether a new order has to be confirmed by hand before it moves on. Applies to both sales orders and purchase orders.",
  skip: {
    label: "Skip the Confirm step",
    description: "When on, a new order or purchase order is confirmed the moment it is created, so nobody has to press Confirm. An order that cannot be confirmed yet — a coil-tracked item with no coils picked — is still saved and keeps its Confirm button.",
  },
  status: {
    enabled: "Enabled",
    disabled: "Disabled",
  },
  notifications: {
    enabled: "New orders will now confirm themselves",
    disabled: "New orders will wait for the Confirm button again",
    updateFailed: "Could not change the order confirmation setting",
  },
};

STRINGS.coils = {
  picker: {
    title: "Select Coils",
    context: {
      order: "Order picking",
      loading: "Loading — revise picks",
    },
    panes: {
      available: "Available Coils",
      picked: "Picked Coils",
    },
    filters: {
      colour: "Colour",
      batch: "Batch",
      allColours: "All colours",
      searchPlaceholder: "Batch or mill batch ref",
    },
    sort: {
      label: "Sort",
      oldestFirst: "Oldest received first",
      newestFirst: "Newest received first",
      lightestFirst: "Lightest first",
      heaviestFirst: "Heaviest first",
      batch: "Batch number",
    },
    columns: {
      batch: "Batch",
      supplierRef: "Mill Batch",
      colour: "Colour",
      received: "Received",
      remainingKg: "Remaining (kg)",
      remainingM: "Remaining (m)",
      availableKg: "Available (kg)",
      kgPerMetre: "kg/m",
      take: "Take (kg)",
      status: "Status",
    },
    take: "Take",
    takeAll: "Take all",
    add: "Add",
    added: "Added",
    remove: "Remove",
    clearAll: "Clear all",
    overrideRemainder: "Override remainder",
    remainderLabel: "Remainder {weight} kg · {length} m",
    adjusted: "adjusted {delta} kg",
    held: "{weight} kg held by other orders",
    truncated: "Only the first {count} of {total} coils are listed",
    age: {
      today: "Today",
      days: "{days} days",
      months: "{months} mo",
      years: "{years} yr",
    },
    summary: {
      picked: "{picked} kg picked of {required} kg",
      bar: "Picked {count} coils · {picked} kg of {required} kg",
      barNoTarget: "Picked {count} coils · {picked} kg",
      short: "{amount} kg short",
      over: "{amount} kg over",
      exact: "Matches the line exactly",
    },
    empty: {
      title: "No coils available",
      subtitle: "No coils match these filters in this warehouse. Add coils in the coil ledger, or receive them against a purchase.",
      noStock: "No coils in stock for this product at this warehouse",
    },
  },
  entry: {
    title: "Add Coils",
    subtitle: "One row per physical coil. Press Enter to commit a row and start the next.",
    fields: {
      warehouse: "Warehouse",
      productSize: "Product",
      batchNumber: "Batch Number",
      supplierBatchRef: "Supplier / Mill Batch Ref",
      colour: "Colour",
      receivedDate: "Received Date",
      weightKg: "Weight (kg)",
      lengthM: "Length (m)",
      remark: "Remark",
    },
    computedKgPerMetre: "{value} kg/m",
    addAnother: "Add another coil",
    runningTotal: "{count} coils · {weight} kg",
    coilNumber: "Coil {number}",
    removeRow: "Remove this coil",
    expected: "Expected {quantity} {unit}",
    difference: {
      short: "{amount} kg short",
      over: "{amount} kg over",
      exact: "Matches the line exactly",
    },
    awaitingKgPerMetre: "Enter weight and length",
    save: "Save Coils",
  },
  ledger: {
    title: "Coil Stock",
    subtitle: "Individual coils held for this product in this warehouse",
    addCoil: "Add Coil",
    editCoil: "Edit Coil",
    adjustWeight: "Adjust Weight",
    adjustTitle: "Adjust Remaining Weight",
    newRemaining: "New Remaining (kg)",
    adjustNote: "Note",
    adjustNotePlaceholder: "Weighbridge re-check, stock take…",
    writeOff: "Write Off",
    writeOffTitle: "Write Off Coil",
    writeOffReason: "Reason",
    writeOffReasonPlaceholder: "Damaged in handling, rejected at inspection…",
    writeOffConfirm: "Write off coil {batch}? Its remaining {weight} kg leaves stock and the coil can never be picked again.",
    filters: {
      includeInactive: "Show exhausted & written off",
    },
    columns: {
      heldKg: "Held (kg)",
    },
    totals: "{count} coils · {weight} kg",
    empty: {
      title: "No coils yet",
      subtitle: "Coil stock starts empty. Add each physical coil here, or receive them against a purchase.",
    },
    reconcile: {
      matched: "Coil weights match inventory",
      mismatch: "Off by {difference} kg",
      coilTotal: "Coil Total",
      inventoryTotal: "Inventory Total",
      coilCount: "{count} coils",
      unavailable: "Reconciliation unavailable",
    },
    status: {
      whole: "Whole",
      partlyConsumed: "Partly Used",
      exhausted: "Exhausted",
      writtenOff: "Written Off",
    },
    messages: {
      created: "{count} coils added",
      updated: "Coil updated",
      adjusted: "Coil weight adjusted",
      writtenOff: "Coil written off",
      loadFailed: "Could not load coils",
      stockManagedByCoils: "Stock for a coil-tracked product is the sum of its coils, so it is set by receiving or adjusting a coil — not typed here. Minimum stock is still editable.",
      notStockedHere: "Coil-tracked — stocked through the coil ledger",
      notStockedHereHint: "Coil-tracked products are greyed out: their stock comes from receiving or adjusting individual coils, not from this screen.",
    },
  },
  line: {
    piecesAreCoils: "Pieces on a coil line is the number of coils picked",
    cutPieces: "Cut Pieces",
    cutPiecesHint: "Number of pieces to cut the coil(s) into",
    cutPiecesRequired: "Enter how many pieces to cut the coil into",
    cutPiecesMinCoils: "Cut pieces cannot be fewer than the {count} coil(s) on this line",
    cutPiecesInteger: "Cut pieces must be a positive whole number",
    summary: "{count} coils · {weight} kg",
    summaryWithLength: "{count} coils · {weight} kg · {length} m",
    selectCoils: "Select coils",
    editCoils: "Edit coils",
    noneSelected: "No coils selected",
    disabledNote: "Choose a warehouse and a product before picking coils.",
  },
  receipt: {
    pill: "Coil",
    enterCoils: "Enter coils",
    noneEntered: "No coils entered",
    derivedFromCoils: "Computed from the coils entered on this line",
  },
  errors: {
    takeExceedsAvailable: "Cannot take more than the {available} kg available on this coil",
    takeMustBePositive: "Enter a weight greater than zero",
    batchRequired: "Batch number is required",
    weightRequired: "Weight is required",
    lengthRequired: "Length is required",
    wholeKgRequired: "Weight must be a whole number of kg",
    receivedDateRequired: "Received date is required",
    duplicateBatch: "Batch {batch} is already on this sheet",
    noCoilsEntered: "Enter at least one complete coil",
    reasonRequired: "A reason is required to write off a coil",
    coilsRequired: "Enter the coils received on this line",
    coilsExceedNet: "Coils weigh {coils} kg but the truck's net weight is only {net} kg — check the empty (tare) weight and the coil weights.",
    coilRowInvalid: "Coil {number}: {message}",
  },
  challan: {
    coilsLabel: "Coils",
    moreCoils: "+{count} more",
  },
  productSize: {
    coilTracked: {
      label: "Track as individual coils",
      help: "Stock for this size is held as individual coil rolls. Orders are picked coil by coil, and a partly used coil keeps its batch. Only available while coil tracking is on.",
      badge: "Coil Tracked",
      stockLocked: "Computed from the coils below — stock is their total weight, never typed.",
    },
    // Wording for a warehouse this size is ALREADY stocked in: coils entered here are
    // credited ON TOP of the existing balance, they do not replace it.
    existingWarehouse: {
      current: "Already stocked here: {current}. Add coils below to increase it.",
      adding: "Already stocked here: {current}. Adding {added} from the coils below.",
    },
    openingCoils: {
      label: "Opening Coils",
      none: "No coils yet — this warehouse opens at zero stock.",
      row: "{batch} · {weight} kg · {length} m",
      sheetTitle: "{product} · {warehouse}",
    },
  },
  // A coil that has been used by an order (claimed on placement) — the roll is open.
  looseCoil: {
    badge: "Loose Coil",
  },
};

STRINGS.pieces = {
  productSize: {
    trackByPieces: {
      errors: { coilConflict: "A product can be tracked by coils or by pieces, not both. Coil stock is the weight of its coils; pieces tracking counts units." },
      label: "Track stock by pieces",
      help: "Keep this size's warehouse stock as a piece count instead of kilograms. Reservations use the ordered pieces; receipts and deliveries move the actual pieces.",
      badge: "By Pieces",
      flipWarning: "Changing the stock unit does not convert the existing balance. After saving, re-enter this size's stock in the new unit on the warehouse inventory screen.",
    },
  },
  inventory: {
    unitPieces: "pcs",
    unitKg: "kg",
  },
  loading: {
    pcsUnitNote: "Sold by PIECES — the amount is priced per piece. Enter the delivered piece count in “Actual Pieces”; the per-piece weight is only for the weighbridge and loading cost.",
    loadByPiecesNote: "Loaded by pieces — enter the delivered PIECE COUNT, not the weight.",
    pcsReceiveNote: "Received by PIECES — enter the received piece count. The amount is priced per piece.",
    pcsCountRequired: "Piece count is required — enter the pieces received for this line.",
  },
};

STRINGS.challanPrintVariance = {
  title: "Challan Print Variance",
  subtitle: "Challans generated on a day but not printed that same day, including ones never printed. Filtered by the date the challan was generated.",
  loading: "Loading...",
  apply: "Apply",
  empty: "Every challan generated in this range was printed the same day",
  totalLabel: "challans with a print variance",
  searchPlaceholder: "Challan no, order no, party or vehicle",
  tiles: {
    generated: "Generated",
    variance: "Variance",
    notPrinted: "Not Printed",
    printedLate: "Printed Later",
  },
  filters: {
    generatedFrom: "Generated From",
    generatedTo: "Generated To",
    type: "Type",
    allTypes: "Sales & Purchase",
    status: "Print Status",
    allStatuses: "All",
    printedOtherDay: "Printed on another day",
  },
  columns: {
    generatedDate: "Generated On",
    printedDate: "Printed On",
    printStatus: "Print Status",
    type: "Type",
    party: "Party",
    order: "Order",
  },
  status: {
    notPrinted: "Not printed",
    daysLate: "{days}d late",
    differentDay: "Different day",
  },
  source: {
    sales: "Sales",
    purchase: "Purchase",
  },
};

// The either/or offered when deleting something that has already moved warehouse stock.
STRINGS.stockRevert = {
  single: {
    heading: "This order has already moved stock. What should happen to it?",
    revertLabel: "Put the stock back",
    revertDescription:
      "The warehouse gets the quantity back, as if this order had never been made. Choose this if the order was a mistake.",
    keepLabel: "Leave the stock as it is",
    keepDescription:
      "Stock stays as it is — choose this if the goods really did move. Any quantity that was only reserved is freed either way.",
  },
  bulk: {
    heading: "Some of these orders have already moved stock. What should happen to it?",
    revertLabel: "Put the stock back",
    revertDescription:
      "The warehouse gets every quantity back, as if these orders had never been made. Choose this if they were a mistake.",
    keepLabel: "Leave the stock as it is",
    keepDescription:
      "Stock stays as it is — choose this if the goods really did move. Any quantity that was only reserved is freed either way.",
  },
};

STRINGS.purchaseOrdersModule.purchaseOrders.table.tooltips.deleteReceivedSuperAdminOnly =
  "Only a Super Admin can delete a purchase order once its goods have been received";


// Keys the audit-fix batch used with inline English defaults — defined here so the
// Hindi UI does not fall back to English for them.
STRINGS.ordersModule = STRINGS.ordersModule || {};
STRINGS.ordersModule.common = STRINGS.ordersModule.common || {};
STRINGS.ordersModule.common.loadFailed = "Could not load this order. It may have been deleted, or the server is unavailable.";
STRINGS.purchaseOrdersModule = STRINGS.purchaseOrdersModule || {};
STRINGS.purchaseOrdersModule.common = STRINGS.purchaseOrdersModule.common || {};
STRINGS.purchaseOrdersModule.common.loadFailed = "Could not load this purchase order. It may have been deleted, or the server is unavailable.";
STRINGS.common = STRINGS.common || {};
STRINGS.common.errors = STRINGS.common.errors || {};
STRINGS.common.errors.generic = "The action could not be completed. Please try again.";
STRINGS.deliveryOrdersModule = STRINGS.deliveryOrdersModule || {};
STRINGS.deliveryOrdersModule.forms = STRINGS.deliveryOrdersModule.forms || {};
STRINGS.deliveryOrdersModule.forms.challan = STRINGS.deliveryOrdersModule.forms.challan || {};
STRINGS.deliveryOrdersModule.forms.challan.sendFailed = "Could not send. Please try again.";
STRINGS.deliveryOrdersModule = STRINGS.deliveryOrdersModule || {};
STRINGS.deliveryOrdersModule.forms = STRINGS.deliveryOrdersModule.forms || {};
STRINGS.deliveryOrdersModule.forms.challan = STRINGS.deliveryOrdersModule.forms.challan || {};
STRINGS.deliveryOrdersModule.forms.challan.sendSuccess = "Sent successfully.";
STRINGS.purchaseOrders = STRINGS.purchaseOrders || {};
STRINGS.purchaseOrders.forms = STRINGS.purchaseOrders.forms || {};
STRINGS.purchaseOrders.forms.unloading = STRINGS.purchaseOrders.forms.unloading || {};
STRINGS.purchaseOrders.forms.unloading.draftSaveFailed = "Could not save draft. Please try again.";
STRINGS.purchaseOrders = STRINGS.purchaseOrders || {};
STRINGS.purchaseOrders.forms = STRINGS.purchaseOrders.forms || {};
STRINGS.purchaseOrders.forms.unloading = STRINGS.purchaseOrders.forms.unloading || {};
STRINGS.purchaseOrders.forms.unloading.draftSaved = "Draft saved.";
STRINGS.hsnCodes = STRINGS.hsnCodes || {};
STRINGS.hsnCodes.notifications = STRINGS.hsnCodes.notifications || {};
STRINGS.hsnCodes.notifications.bulkPartial = "{{deleted}} deleted, {{skipped}} skipped (referenced by other records).";
STRINGS.taxes = STRINGS.taxes || {};
STRINGS.taxes.notifications = STRINGS.taxes.notifications || {};
STRINGS.taxes.notifications.bulkPartial = "{{deleted}} deleted, {{skipped}} skipped (still in use).";
STRINGS.purchaseOrdersModule = STRINGS.purchaseOrdersModule || {};
STRINGS.purchaseOrdersModule.purchaseOrders = STRINGS.purchaseOrdersModule.purchaseOrders || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms = STRINGS.purchaseOrdersModule.purchaseOrders.forms || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm = STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm.errors = STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm.errors || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm.errors.noItems = "No items available to advise.";

// Daybook / proforma print header for the parent sales order column.
STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"orderNo": "Order No."});

export default STRINGS;
